/**
 * OSMDPlayableScene
 *
 * The shared performance-notation engine for MuseFlow's play surfaces: OSMD
 * rendering on the custom Phaser backend, transport, cursor, per-note accuracy
 * tracking via OSMDEventStream (green/yellow/red timing feedback), and the
 * play-mode mechanics (loop/bracket, count-in, metronome, ...). The machinery is
 * surface-agnostic - a shared toolkit reused across product surfaces - so the base
 * carries a neutral name and each surface binds it through a thin leaf scene that
 * pins its own scene key and layers surface-specific behavior. Today the only leaf
 * is OSMDRepertoireScene (Repertoire); OSMDExerciseScene (Exercise Section, MUS-317)
 * and the OSMDSightReadingScene reparent (MUS-453) are the upcoming leaves.
 *
 * This base implements the load-all-at-once phrase model: all phrases load up front
 * via addAllPhrases() and stack vertically with camera scrolling (linear phrase
 * progression, no ChevronManager). That contrasts with the legacy
 * OSMDSightReadingScene's fixed 4-slot circular buffer; reconciling the two models
 * is the MUS-453 follow-up.
 *
 * Renamed from OSMDRepertoireScene (2026-08-26). The class name changed but the
 * Repertoire scene KEY string ('OSMDRepertoireScene') is preserved on the
 * OSMDRepertoireScene leaf, so every scene.start() consumer and the scene registry
 * are unchanged; this is a behavior-preserving rename, not a re-architecture.
 */

import Phaser from 'phaser';
import {
  OpenSheetMusicDisplay,
  BackendType,
  EngravingRules,
  GraphicalMusicPage,
  TransposeCalculator,
  VexFlowGraphicalNote,
} from '@museflow-ai/opensheetmusicdisplay';
import { PhaserVexFlowBackend } from '../../vexflow-phaser-backend/PhaserVexFlowBackend';
import { NoteContainer, NoteState } from '../../vexflow-phaser-backend/NoteContainer';
import RepertoireCursor from 'Phaser/GameObjects/RepertoireCursor';
import {
  OSMDIteratorExtensible,
  IOSMDNotePositionProvider,
  OSMDNoteData,
  extractTieFields,
} from 'Phaser/GameObjects/OSMDIterator';
import { OSMDEventStream, INoteContainerProvider } from 'Phaser/GameObjects/OSMDEventStream';
import {
  ClearQueuePhaser,
  ReactToPhaserListeners,
  NotifyReactEvents,
  RemoveListener,
  type MidiOffPayload,
  type MidiOnPayload,
  type OSMDPhraseDataEventPayload,
} from 'Phaser/EventBus2';
import type { ToPhaserEvents } from 'Phaser/EventBus2/ToPhaser';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import Phrase from 'Models/Phrase';
import ITimeKeeper from 'Models/ITimeKeeper';
import TimeKeeper from 'Models/TimeKeeper';
// createTimeKeeper removed — auxiliary TK uses plain TimeKeeper directly
// to avoid NativeTimeKeeper's native metronome scheduling on iOS.
import * as Tone from 'tone';
import Logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';
import { reportError } from 'Utils/reportError';
import { isCapacitorNative } from 'Utils/Constants';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import TimeSignature from 'Models/TimeSignature';
import {
  beatLengthsInSecs,
  beatLengthsInWholeNotes,
  isPlayableTimeSignature,
  measureDurationSecs,
  secondsPerWholeNote,
  tempoBeatUnitOf,
} from 'Models/MeterTiming';
import IPlayable from 'Phaser/GameObjects/Playable';
import { INoteGraphicsDatabase } from 'Phaser/Scenes/StaffExercise';
import BracketOverlay, {
  type PhraseLayoutInfo,
  type BracketNoteData,
} from 'Phaser/GameObjects/BracketOverlay';
import { setLastLoopPosition } from 'Utils/LoopPositionStorage';
import { calculateDefaultBracketPlacement } from 'Utils/BracketPlacement';
import { computeFirstLineEndTimestamp } from 'Utils/BracketSnapHelpers';
import { getScoreThemePalette, normalizeScoreTheme } from 'Utils/ScoreTheme';
import type { ScoreTheme } from 'Utils/ScoreTheme';
import { isTouchDevice } from 'Utils/UserAgent';
import { notifyAppAudioOutput } from 'AudioRecognition/appAudioSuppression';
import Scheduler from 'Utils/Scheduler';
import { createActor, type ActorRefFrom } from 'xstate';
import { playbackMachine } from 'Phaser/PlaybackMachine/playbackMachine';
import { buildSceneActions } from 'Phaser/PlaybackMachine/buildSceneActions';
import type { IPlaybackSceneFacade } from 'Phaser/PlaybackMachine/IPlaybackSceneFacade';
import type { PlaybackEvent, PlaybackPhase } from 'Phaser/PlaybackMachine/playbackMachine.types';
import PerfectPhraseController from 'Phaser/PerfectPhrase/PerfectPhraseController';
import PerfectPhraseBadge from 'Phaser/GameObjects/PerfectPhraseBadge';
import { shouldFlattenReturningContourBeam } from 'Utils/BeamEngraving';
import {
  getInitialRepertoirePhraseCount,
  REPERTOIRE_PHRASE_BATCH_SIZE,
  shouldPrefetchRepertoireForPlayback,
  shouldPrefetchRepertoireForViewport,
} from './RepertoireLazyLoadPolicy';
import { isScoreTap } from './scoreTap';
import { metronomeSoundConfig } from 'Volume/volumeOutput';
import CountInCursorHold from 'Phaser/Gameplay/CountInCursorHold';
import CountInCountdownView, {
  entryWholeNotes,
  highestNote,
  type WorldBox,
  type WorldPoint,
} from 'Phaser/Gameplay/CountInCountdownView';
import type { CountInPlan } from 'Models/CountIn';
import {
  countInRequestFor,
  isPlayAlongCountInUngraded,
  playAlongLoop,
} from 'Utils/playAlongCountIn';

/** World-space padding between the left edge of the viewport and the first phrase on mobile. */
const MOBILE_LEFT_PADDING = 40;

/** Every React→Phaser event the repertoire scene's `init()` subscribes to.
 *  Used to remove any stale registrations before re-subscribing (so the
 *  singleton EventBus never accumulates duplicate handlers across scene
 *  restarts / re-mounts) and to tear them all down in `destroy()`. Keep this
 *  list in sync with the registrations in `init()`. */
const OSMD_REPERTOIRE_REACT_LISTENER_EVENTS: (keyof ToPhaserEvents)[] = [
  'ResizeWindow',
  'PauseExercise',
  'AddPhraseOSMD',
  'AddAllOSMDPhrases',
  'MidiOn',
  'MidiOff',
  'StartExercise',
  'SoftRefreshRepertoire',
  'ChangeTempo',
  'SetCursorVisible',
  'SetZoomLevel',
  'UnloadExercise',
  'SetLoopingActive',
  'RestartLoopFromBeginning',
  'SetPerfectPhraseConfig',
  'UserPlay',
  'UserPause',
  'UserReset',
  'UserToggleLoop',
  'AppBackgrounded',
  'AppForegrounded',
  'InjectScheduler2',
];

/** Screen-space padding between the cursor and the left edge of the viewport on mobile.
 *  Matches MOBILE_CURSOR_SCROLL_PADDING_LEFT from CursorMobile. */
const MOBILE_CURSOR_CAMERA_PADDING = 200;
const CURSOR_FOLLOW_EXTRA_PADDING_LEFT = 20;

/** DEBUG: when true, draw per-phrase bounds overlays (fill + border + edge
 *  markers) so phrase extents and any clipping are visible. Set false to
 *  remove. */
const DEBUG_PHRASE_BOUNDS = false;
/** Distinct colors cycled per phrase for the debug overlay. */
const DEBUG_PHRASE_COLORS = [0xff0000, 0x00aa00, 0x0066ff, 0xff8800, 0xaa00ff, 0x00aaaa];

/** DEBUG: when true, render the on-screen mobile camera-tracking overlay text.
 *  Set false to hide it. */
const DEBUG_CAMERA_TEXT = false;

/**
 * Type for accessing OSMD's SourceMeasure properties through the graphical measure.
 * OSMD's GraphicalMeasure has a public `parentSourceMeasure: SourceMeasure`.
 * SourceMeasure has `AbsoluteTimestamp` (Fraction with RealValue) and `Duration` (Fraction).
 * We access these via type assertion because our TypeScript definitions don't fully
 * export OSMD internal types.
 */
interface OSMDSourceMeasureAccessor {
  parentSourceMeasure?: {
    AbsoluteTimestamp?: { RealValue: number };
    Duration?: { RealValue: number };
  };
}

interface VexFlowBeamNoteAccessor {
  getYs: () => number[];
}

interface VexFlowBeamAccessor {
  getNotes: () => VexFlowBeamNoteAccessor[];
  stem_direction: number;
  render_options: {
    flat_beams?: boolean;
  };
  postFormatted: boolean;
}

interface VexFlowMeasureBeamAccessor {
  vfbeams?: Record<string, VexFlowBeamAccessor[]>;
  autoVfBeams?: VexFlowBeamAccessor[];
  autoTupletVfBeams?: VexFlowBeamAccessor[];
}

/**
 * Data structure for an OSMD-rendered repertoire phrase.
 * Same structure as OSMDPhraseData in OSMDSightReadingScene but local to this file.
 */
interface RepertoirePhraseData {
  /** The raw MusicXML string */
  musicXml: string;
  /** Unique identifier for this phrase */
  uuid: string;
  /** Starting timestamp in measure units */
  startTimestamp: number;
  /** Time signature for this phrase */
  timeSignature: TimeSignature;
  /** Whether to show the clef */
  showClef: boolean;
  /** Whether to show the brace */
  showBrace: boolean;
  /** Whether to show the time signature */
  showTimeSig: boolean;
  /** Semitone transposition applied to this phrase */
  transposeSemitones: number;
}

type StartExercisePayload = { tempo?: number; autoStart?: boolean };

// ============================================================================
// OSMDPlayablePhrase
// ============================================================================

/**
 * Represents a playable phrase with OSMD rendering for the repertoire scene.
 * Adapted from OSMDSightReadingScene's OSMDPlayablePhrase with the following changes:
 * - Removed `bottom` property (all phrases stacked vertically)
 * - Removed transition/transitionOut methods (not needed for vertical scrolling)
 * - Simplified bestFit to accept only target width
 * - Added phraseModel reference for soft-reset iterator recreation
 */
class OSMDPlayablePhrase implements IOSMDNotePositionProvider {
  cursor?: RepertoireCursor;
  osmd: OpenSheetMusicDisplay | null = null;
  phaserBackend: PhaserVexFlowBackend | null = null;
  container: Phaser.GameObjects.Container;
  noteContainers: Map<string, NoteContainer> = new Map();
  notePositions: OSMDNoteData[] = [];
  data: RepertoirePhraseData;
  idx: number = 0;
  isActive: boolean = false;
  phraseStartingMeasure: number = 0;
  /** Whether the cursor should be rendered — mirrors the scene-level setting.
   *  When false, activate() skips cursor.enable() so the cursor stays hidden. */
  cursorVisible: boolean = true;

  /** The Phrase model, stored for iterator recreation during softReset */
  phraseModel: Phrase;

  private scene: Phaser.Scene;
  private dummyContainer: HTMLDivElement | null = null;
  private destroyed: boolean = false;

  /** Original unscaled width of the rendered content (set after initialize) */
  private originalWidth: number = 0;
  /** Original unscaled height of the rendered content (set after initialize) */
  private originalHeight: number = 0;

  /** Scaled dimensions set by bestFit */
  private _scaledHeight: number = 0;
  private _scaledWidth: number = 0;

  /** The CSS-pixel container width used for the final OSMD render */
  private resolvedContainerWidth: number = 0;

  /** Custom clef images */
  private customClefImages: Phaser.GameObjects.Image[] = [];
  /** Custom brace image */
  private customBraceImage: Phaser.GameObjects.Image | null = null;
  /** Clef scaling ratio - adjusted for OSMD coordinate system */
  private readonly clefGapRatio = 1 / 45;
  private readonly scoreTheme: ScoreTheme;

  constructor(
    scene: Phaser.Scene,
    data: RepertoirePhraseData,
    phraseModel: Phrase,
    initialX: number,
    initialY: number,
    scoreTheme: ScoreTheme,
  ) {
    this.scene = scene;
    this.data = data;
    this.phraseModel = phraseModel;
    this.scoreTheme = scoreTheme;
    this.container = scene.add.container(initialX, initialY);
    // OSMD adds notation children while the phrase is still at its temporary
    // construction origin. Keep the container hidden until layoutPhrases()
    // commits its final scale and position so a lazy batch cannot flash at
    // the top-left of the canvas between animation frames.
    this.container.setVisible(false);
  }

  /**
   * Initialize the OSMD rendering for this phrase.
   *
   * @param viewportWidth - The scene's viewport width in device pixels.
   *   OSMD uses the dummy container width to decide how many measures fit
   *   on one system (line). A wider container produces a wider, shorter
   *   rendering.
   *
   *   To balance simple and complex pieces we use a two-pass approach:
   *   1. Render at the baseline container width (900 CSS px).
   *   2. Check the resulting content aspect ratio (width / height).
   *   3. If the content is "too tall" (aspect ratio < threshold), clear
   *      the first render and re-render with a wider container so OSMD
   *      spreads the notation horizontally instead of stacking vertically.
   *   Simple pieces keep their natural spacing; only tall/complex pieces
   *   get the wider container.
   */
  async initialize(viewportWidth: number): Promise<void> {
    try {
      await this.initializeInternal(viewportWidth);
    } catch (error) {
      reportError(error, 'osmd.render');
      // initialize() owns every resource created below until it resolves.
      // Do not leave a half-rendered Phaser container or hidden OSMD DOM node
      // behind when a MusicXML document cannot be loaded.
      this.destroy();
      throw error;
    }
  }

  private async initializeInternal(viewportWidth: number): Promise<void> {
    const dpr = window.devicePixelRatio || 1;
    const baselineContainerWidth = 900;

    // Create a hidden DOM container for OSMD layout calculations
    this.dummyContainer = document.createElement('div');
    this.dummyContainer.id = `osmd-rep-dummy-${this.data.uuid}`;
    this.dummyContainer.style.position = 'fixed';
    this.dummyContainer.style.left = '-99999px';
    this.dummyContainer.style.top = '-99999px';
    this.dummyContainer.style.width = `${baselineContainerWidth}px`;
    this.dummyContainer.style.height = '400px';
    this.dummyContainer.style.overflow = 'hidden';
    this.dummyContainer.style.visibility = 'hidden';
    document.body.appendChild(this.dummyContainer);

    // Create OSMD instance with Phaser backend
    this.osmd = new OpenSheetMusicDisplay(this.dummyContainer, {
      backend: 'phaser',
      autoResize: false,
      pageFormat: 'Endless',
      drawTitle: false,
      drawSubtitle: false,
      drawComposer: false,
      drawLyricist: false,
      drawCredits: false,
      drawPartNames: false,
      drawMeasureNumbers: false,
      drawFingerings: true,
      drawTimeSignatures: this.data.showTimeSig,
      drawMetronomeMarks: false,
      stretchLastSystemLine: true,
      disableCursor: false,
      newPageFromXML: true,
      defaultFontFamily: 'Times New Roman, Canela, Arial',
    });

    // Configure EngravingRules for larger, more prominent notation
    const scorePalette = getScoreThemePalette(this.scoreTheme);
    this.osmd.EngravingRules.DefaultVexFlowNoteFont = 'gonville';
    // NOTE: intentionally do NOT set PageBackgroundColor. The Phaser backend
    // renders it as an opaque per-phrase fillRect spanning [0, backendWidth]
    // (starting at the page's left margin, left of the first-measure content).
    // In the tight horizontal mobile chain, each phrase's background then paints
    // over the previous phrase's closing barline / last-measure right edge,
    // making the last measure look clipped. The score sits on the camera
    // background (set to the score/paper color below) instead.
    // this.osmd.EngravingRules.PageBackgroundColor = scorePalette.scoreBackground;
    this.osmd.EngravingRules.StaffLineColor = scorePalette.staffLineColor;
    this.osmd.EngravingRules.DefaultColorMusic = scorePalette.notationColor;
    this.osmd.EngravingRules.DefaultColorNotehead = scorePalette.notationColor;
    this.osmd.EngravingRules.DefaultColorRest = scorePalette.notationColor;
    this.osmd.EngravingRules.DefaultColorStem = scorePalette.notationColor;
    this.osmd.EngravingRules.DefaultColorLabel = scorePalette.musicMarkingColor;
    this.osmd.EngravingRules.DefaultColorLyrics = scorePalette.musicMarkingColor;
    this.osmd.EngravingRules.DefaultColorChordSymbol = scorePalette.musicMarkingColor;
    this.osmd.EngravingRules.DefaultColorTitle = scorePalette.musicMarkingColor;
    this.osmd.EngravingRules.DefaultColorCursor = scorePalette.playbackCursor;
    this.osmd.EngravingRules.StaffHeight = 6.0;
    this.osmd.EngravingRules.BetweenStaffDistance = 3.0;
    this.osmd.EngravingRules.StaffDistance = 4.0;
    this.osmd.EngravingRules.BetweenStaffLinesDistance = 1.0;
    this.osmd.EngravingRules.StaffLineWidth = 0.1;
    this.osmd.EngravingRules.StemWidth = 0.12;
    this.osmd.EngravingRules.LedgerLineWidth = 1.0;
    this.osmd.EngravingRules.WedgeLineWidth = 0.1;
    this.osmd.EngravingRules.SystemLeftMargin = 0;
    this.osmd.EngravingRules.SystemRightMargin = 0;
    this.osmd.EngravingRules.PageLeftMargin = 1;
    this.osmd.EngravingRules.PageRightMargin = 1;
    this.osmd.EngravingRules.PageTopMargin = 1;
    this.osmd.EngravingRules.PageBottomMargin = 1;
    this.osmd.EngravingRules.MeasureLeftMargin = 0.2;
    this.osmd.EngravingRules.MeasureRightMargin = 0.2;
    this.osmd.EngravingRules.VoiceSpacingMultiplierVexflow = 0.45;
    this.osmd.EngravingRules.VoiceSpacingAddendVexflow = 1.5;
    this.osmd.EngravingRules.SheetMaximumWidth = 32767;
    this.osmd.EngravingRules.NewSystemAtXMLNewSystemAttribute = true;
    this.osmd.DrawingParameters.drawSelectionEndSymbol = true;

    // Set up the Phaser backend factory
    const scene = this.scene;
    const parentContainer = this.container;

    this.osmd.phaserBackendFactory = (
      osmdRules: EngravingRules,
      page: GraphicalMusicPage,
    ): PhaserVexFlowBackend => {
      const phaserBackend = new PhaserVexFlowBackend(osmdRules, scene);
      // Set graphicalMusicPage (inherited from VexFlowBackend parent class)
      (phaserBackend as unknown as { graphicalMusicPage: GraphicalMusicPage }).graphicalMusicPage =
        page;
      phaserBackend.getPhaserContext().setSkipClefRendering(true);
      phaserBackend.getPhaserContext().setContainerRouting(true);
      parentContainer.add(phaserBackend.getPhaserContext().getContainer());
      return phaserBackend;
    };

    // ---- First-pass render at baseline container width ----
    await this.osmd.load(this.data.musicXml);
    // Configure transposition before initial draw and force a graphic rebuild.
    // In OSMD, setting Sheet.Transpose after load may require updateGraphic()
    // so noteheads (not only key context) are recalculated.
    this.osmd.TransposeCalculator = new TransposeCalculator();
    this.osmd.Sheet.Transpose = this.data.transposeSemitones;
    this.osmd.updateGraphic();
    this.renderWithEngravingAdjustments();

    // ---- Adaptive second-pass: widen container for tall content ----
    // Check the first-pass aspect ratio. For normal pieces (aspect ratio
    // >= 3.0) the baseline 900px container produces well-proportioned
    // output. For tall/complex pieces (aspect ratio < 3.0) — grand staff
    // with dynamics, pedal marks, expression text — the notation is
    // crammed vertically. We re-render with a wider container so OSMD
    // spreads the notation horizontally.
    //
    // The wider width is interpolated: pieces slightly below the threshold
    // get a modest increase, while very tall pieces get the full widened
    // container. This prevents simple pieces from being over-stretched.
    //
    // Check the first-pass aspect ratio to decide whether to re-render
    // at a wider container width. The max container width is the viewport
    // CSS width — wide enough to give notes breathing room without making
    // the original render so large that the unified scale shrinks the
    // notation down to an unreadable size.
    const firstPassWidth = this.calculateOriginalWidth();
    const firstPassHeight = this.calculateOriginalHeight();
    const firstPassAspectRatio = firstPassWidth / firstPassHeight;

    const wideningUpperThreshold = 3.0; // Above: keep baseline (no widening)
    const wideningLowerThreshold = 2.5; // At or below: full widened container
    const viewportCssWidth = Math.round(viewportWidth / dpr);
    const maxContainerWidthCss = Math.max(baselineContainerWidth, viewportCssWidth);

    if (firstPassAspectRatio < wideningUpperThreshold) {
      // Compute interpolated container width between baseline and max
      const wideningFactor = Math.min(
        1.0,
        (wideningUpperThreshold - firstPassAspectRatio) /
          (wideningUpperThreshold - wideningLowerThreshold),
      );
      const adaptiveContainerWidth = Math.round(
        baselineContainerWidth + wideningFactor * (maxContainerWidthCss - baselineContainerWidth),
      );

      // Clear first-pass Phaser game objects from the container
      this.container.removeAll(true);

      // Update the DOM container width and re-render
      this.dummyContainer.style.width = `${adaptiveContainerWidth}px`;
      this.renderWithEngravingAdjustments();
      this.resolvedContainerWidth = adaptiveContainerWidth;
    } else {
      this.resolvedContainerWidth = baselineContainerWidth;
    }

    // Extract note positions and store NoteContainers from the final render
    this.extractNotePositions();
    this.collectNoteContainers();

    // Render custom clefs and brace to replace OSMD's default glyphs
    if (this.data.showClef) {
      this.renderCustomClefs();
    }
    if (this.data.showBrace) {
      this.renderCustomBrace();
    }

    // Upgrade text resolution for crisp rendering on HiDPI displays
    this.upgradeTextResolution();

    // Post-process finger numbers: reduce size, center, and align at fixed height
    this.postProcessFingerNumbers();

    // Store the original unscaled dimensions after rendering
    this.originalWidth = this.calculateOriginalWidth();
    this.originalHeight = this.calculateOriginalHeight();
  }

  /**
   * Re-render this phrase at a different container width.
   * Used to normalize all phrases in a piece to the same container width
   * so they all have the same original width and appear the same screen
   * width after unified scaling.
   */
  async reRenderAtContainerWidth(targetContainerWidth: number): Promise<void> {
    if (!this.osmd || !this.dummyContainer) return;
    if (targetContainerWidth === this.resolvedContainerWidth) return;

    // Clear existing Phaser game objects
    this.container.removeAll(true);
    this.noteContainers.clear();
    this.notePositions = [];
    this.customClefImages = [];
    this.customBraceImage = null;

    // Update the DOM container width and re-render
    this.dummyContainer.style.width = `${targetContainerWidth}px`;
    this.renderWithEngravingAdjustments();
    this.resolvedContainerWidth = targetContainerWidth;

    // Re-extract everything from the new render
    this.extractNotePositions();
    this.collectNoteContainers();

    if (this.data.showClef) {
      this.renderCustomClefs();
    }
    if (this.data.showBrace) {
      this.renderCustomBrace();
    }

    // Upgrade text resolution for crisp rendering on HiDPI displays
    this.upgradeTextResolution();

    // Post-process finger numbers: reduce size, center, and align at fixed height
    this.postProcessFingerNumbers();

    this.originalWidth = this.calculateOriginalWidth();
    this.originalHeight = this.calculateOriginalHeight();
  }

  /** Get the CSS-pixel container width used for the final OSMD render */
  getResolvedContainerWidth(): number {
    return this.resolvedContainerWidth;
  }

  /**
   * OSMD/VexFlow normally derives beam slope from the first and last notes.
   * For a long accompaniment figure that rises and then returns, that can
   * create a misleading diagonal even though the full contour is balanced.
   * Apply a flat beam only to that narrow case and redraw the already-laid-out
   * sheet so ordinary melody beams keep their renderer-selected slope.
   */
  private renderWithEngravingAdjustments(): void {
    if (!this.osmd) return;

    this.osmd.render();

    const measureLists = this.osmd.GraphicSheet?.MeasureList;
    if (!measureLists) return;

    let changed = false;

    for (const measureList of measureLists) {
      for (const graphicalMeasure of measureList) {
        if (!graphicalMeasure) continue;

        const measure = graphicalMeasure as unknown as VexFlowMeasureBeamAccessor;
        const beams = new Set<VexFlowBeamAccessor>([
          ...Object.values(measure.vfbeams ?? {}).flat(),
          ...(measure.autoVfBeams ?? []),
          ...(measure.autoTupletVfBeams ?? []),
        ]);

        for (const beam of beams) {
          if (beam.render_options.flat_beams) continue;

          const notes = beam.getNotes();
          const positions = notes.map((note) => {
            const noteYs = note.getYs();
            if (noteYs.length === 0) return Number.NaN;
            return beam.stem_direction > 0 ? Math.min(...noteYs) : Math.max(...noteYs);
          });

          if (shouldFlattenReturningContourBeam(positions)) {
            beam.render_options.flat_beams = true;
            beam.postFormatted = false;
            changed = true;
          }
        }
      }
    }

    if (changed) {
      this.osmd.Drawer.clear();
      this.osmd.Drawer.drawSheet(this.osmd.GraphicSheet);
    }
  }

  // ---------------------------------------------------------------------------
  // Dimension calculation
  // ---------------------------------------------------------------------------

  private calculateOriginalWidth(): number {
    if (this.osmd?.GraphicSheet) {
      const unitInPixels = 10;
      const pages = this.osmd.GraphicSheet.MusicPages;
      if (pages.length > 0) {
        let maxX = 0;
        for (const page of pages) {
          for (const system of page.MusicSystems) {
            for (const staffLine of system.StaffLines) {
              const measures = staffLine.Measures;
              if (measures.length > 0) {
                const lastMeasure = measures[measures.length - 1];
                const pos = lastMeasure.PositionAndShape;
                const measureEnd = (pos.AbsolutePosition.x + pos.Size.width) * unitInPixels;
                maxX = Math.max(maxX, measureEnd);
              }
            }
          }
        }
        return maxX + 40;
      }
    }
    const bounds = this.container.getBounds();
    return bounds.width > 0 ? bounds.width : 800;
  }

  private calculateOriginalHeight(): number {
    if (this.osmd?.GraphicSheet) {
      const unitInPixels = 10;
      const pages = this.osmd.GraphicSheet.MusicPages;
      if (pages.length > 0) {
        let maxY = 0;
        for (const page of pages) {
          for (const system of page.MusicSystems) {
            for (const staffLine of system.StaffLines) {
              const pos = staffLine.PositionAndShape;
              const staffBottom = (pos.AbsolutePosition.y + pos.Size.height) * unitInPixels;
              maxY = Math.max(maxY, staffBottom);
            }
          }
        }
        return maxY + 40;
      }
    }
    const bounds = this.container.getBounds();
    return bounds.height > 0 ? bounds.height : 200;
  }

  // ---------------------------------------------------------------------------
  // Note position extraction
  // ---------------------------------------------------------------------------

  private extractNotePositions(): void {
    if (!this.osmd?.GraphicSheet) return;

    const graphicSheet = this.osmd.GraphicSheet;
    const unitInPixels = 10;
    const phraseStartTimestamp = this.data.startTimestamp;

    const timeSig = this.data.timeSignature;
    const fullMeasureDuration = (1 / timeSig.denominator) * timeSig.numerator;

    // Pre-compute actual measure start timestamps from OSMD's SourceMeasure data.
    // This correctly handles pickup (anacrusis) measures where the first measure
    // has fewer beats than the time signature indicates.
    const measureStartTimestamps = this.buildMeasureStartTimestamps(
      phraseStartTimestamp,
      fullMeasureDuration,
    );

    // Notehead highlighting: assign a stable id per OSMD NoteTie group, keyed on
    // the Tie object's identity (every head of one tie shares the same Tie
    // instance). This binds continuation heads to their start by identity rather
    // than pitch downstream. Rebuilt each walk; ids are only meaningful within
    // this notePositions build.
    const tieGroupIds = new Map<object, string>();

    for (const page of graphicSheet.MusicPages) {
      for (const system of page.MusicSystems) {
        for (const staffLine of system.StaffLines) {
          const staffIndex = staffLine.ParentStaff?.idInMusicSheet ?? 0;

          for (const measure of staffLine.Measures) {
            const measureNumber = measure.MeasureNumber;
            const measureStartTimestamp =
              measureStartTimestamps.get(measureNumber) ??
              phraseStartTimestamp + Math.max(measureNumber - 1, 0) * fullMeasureDuration;

            for (const staffEntry of measure.staffEntries) {
              for (const gve of staffEntry.graphicalVoiceEntries) {
                const voiceNumber = gve.parentVoiceEntry?.ParentVoice?.VoiceId ?? 0;

                for (const graphicalNote of gve.notes) {
                  const sourceNote = graphicalNote.sourceNote;
                  const positionAndShape = graphicalNote.PositionAndShape;

                  if (!positionAndShape) continue;

                  const absPos = positionAndShape.AbsolutePosition;
                  const bbox = positionAndShape.BoundingRectangle;

                  let noteName = 'Rest';
                  let midiPitch = 0;

                  const renderedPitch = sourceNote.TransposedPitch ?? sourceNote.Pitch;
                  if (renderedPitch) {
                    const baseNoteName = this.getNoteEnumString(renderedPitch.FundamentalNote);
                    const standardOctave = renderedPitch.Octave + 3;
                    const accidentalStr = this.getAccidentalString(renderedPitch.Accidental);

                    noteName = `${baseNoteName}${accidentalStr}${standardOctave}`;
                    midiPitch = renderedPitch.getHalfTone() + 12;
                  }

                  const vfGraphicalNote = graphicalNote as VexFlowGraphicalNote;
                  const vfNote = vfGraphicalNote.vfnote?.[0];
                  interface VexFlowNoteWithAttrs {
                    attrs?: { id?: string };
                  }
                  const vexflowId =
                    (vfNote as VexFlowNoteWithAttrs)?.attrs?.id ||
                    `note-${measureNumber}-${voiceNumber}-${this.notePositions.length}`;

                  const relInMeasure = staffEntry.relInMeasureTimestamp?.RealValue ?? 0;
                  const noteTimestamp = measureStartTimestamp + relInMeasure;

                  // Capture tie grouping so grading can fold a tie into ONE held
                  // note. Extracted to extractTieFields for unit coverage.
                  const { isTieStart: tieIsStart, tieDuration: tieCombinedDuration } =
                    extractTieFields(sourceNote);

                  // Stable per-group id from the Tie object identity (for
                  // notehead highlighting). Undefined for untied notes.
                  const tieObject = (sourceNote as { NoteTie?: object | null }).NoteTie ?? null;
                  let tieGroupId: string | undefined;
                  if (tieObject) {
                    tieGroupId = tieGroupIds.get(tieObject);
                    if (tieGroupId === undefined) {
                      tieGroupId = `tie-${tieGroupIds.size}`;
                      tieGroupIds.set(tieObject, tieGroupId);
                    }
                  }

                  const noteData: OSMDNoteData = {
                    x: absPos.x * unitInPixels,
                    y: absPos.y * unitInPixels,
                    width: (bbox?.width ?? 1) * unitInPixels,
                    height: (bbox?.height ?? 1) * unitInPixels,
                    midiPitch,
                    noteName,
                    duration: sourceNote.Length?.RealValue ?? 0,
                    measureNumber,
                    voiceNumber,
                    staffIndex,
                    isRest: sourceNote.isRest(),
                    timestamp: noteTimestamp,
                    vexflowId,
                    isTieStart: tieIsStart,
                    tieDuration: tieCombinedDuration,
                    tieGroupId,
                  };

                  this.notePositions.push(noteData);
                }
              }
            }
          }
        }
      }
    }
  }

  private getNoteEnumString(noteEnum: number): string {
    const noteNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    return noteNames[noteEnum % 7] || 'C';
  }

  private getAccidentalString(accidental: number): string {
    switch (accidental) {
      case 1: // SHARP
      case 5: // QUARTERTONESHARP
        return '#';
      case 2: // FLAT
      case 6: // QUARTERTONEFLAT
        return 'b';
      case 3: // DOUBLESHARP
        return '##';
      case 4: // DOUBLEFLAT
        return 'bb';
      default:
        return '';
    }
  }

  // ---------------------------------------------------------------------------
  // Measure timing (pickup / anacrusis handling)
  // ---------------------------------------------------------------------------

  /**
   * Build a map of measure number → actual start timestamp by reading
   * OSMD's SourceMeasure.AbsoluteTimestamp, which correctly handles
   * pickup (anacrusis) measures whose duration is shorter than the
   * time signature indicates.
   *
   * Falls back to uniform-duration calculation when OSMD internals
   * are not accessible.
   */
  private buildMeasureStartTimestamps(
    phraseStartTimestamp: number,
    fullMeasureDuration: number,
  ): Map<number, number> {
    const measureStarts = new Map<number, number>();
    if (!this.osmd?.GraphicSheet) return measureStarts;

    const graphicSheet = this.osmd.GraphicSheet;
    const seenMeasureNumbers = new Set<number>();

    for (const page of graphicSheet.MusicPages) {
      for (const system of page.MusicSystems) {
        if (system.StaffLines.length === 0) continue;

        // Only iterate the first staff line per system to avoid
        // counting the same measure twice in a grand staff.
        for (const measure of system.StaffLines[0].Measures) {
          const measureNum = measure.MeasureNumber;
          if (seenMeasureNumbers.has(measureNum)) continue;
          seenMeasureNumbers.add(measureNum);

          // Access OSMD's SourceMeasure.AbsoluteTimestamp through type assertion.
          // AbsoluteTimestamp is relative to the beginning of this OSMD instance's
          // music, not the global piece — each phrase has its own OSMD instance.
          const sourceMeasure = (measure as unknown as OSMDSourceMeasureAccessor)
            .parentSourceMeasure;
          const absoluteTimestamp = sourceMeasure?.AbsoluteTimestamp?.RealValue;

          if (absoluteTimestamp !== undefined) {
            measureStarts.set(measureNum, phraseStartTimestamp + absoluteTimestamp);
          } else {
            // Fallback: assume all measures are full duration
            measureStarts.set(
              measureNum,
              phraseStartTimestamp + (measureNum - 1) * fullMeasureDuration,
            );
          }
        }
      }
    }

    // Log when a pickup measure is detected
    const firstMeasureStart = measureStarts.get(1);
    const secondMeasureStart = measureStarts.get(2);
    if (firstMeasureStart !== undefined && secondMeasureStart !== undefined) {
      const firstMeasureDuration = secondMeasureStart - firstMeasureStart;
      if (Math.abs(firstMeasureDuration - fullMeasureDuration) > 0.001) {
        Logger.debug('[OSMDRepertoire] Pickup measure detected', {
          uuid: this.data.uuid,
          fullMeasureDuration,
          firstMeasureDuration,
          pickupOffset: fullMeasureDuration - firstMeasureDuration,
        });
      }
    }

    return measureStarts;
  }

  // ---------------------------------------------------------------------------
  // NoteContainer management
  // ---------------------------------------------------------------------------

  private collectNoteContainers(): void {
    if (!this.osmd?.Drawer?.Backends) return;

    for (const backend of this.osmd.Drawer.Backends) {
      if (backend.getOSMDBackendType() === BackendType.Phaser) {
        const phaserBackend = backend as PhaserVexFlowBackend;
        const containers = phaserBackend.getPhaserContext().getAllElementContainers();

        for (const noteContainer of containers) {
          this.noteContainers.set(noteContainer.id, noteContainer);

          const noteData = this.notePositions.find((nd) => nd.vexflowId === noteContainer.id);
          if (noteData) {
            noteContainer.setBounds(noteData.x, noteData.y, noteData.width, noteData.height);
          }
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Custom clefs and brace rendering
  // ---------------------------------------------------------------------------

  private renderCustomClefs(): void {
    if (!this.osmd?.GraphicSheet) return;

    const graphicSheet = this.osmd.GraphicSheet;
    const unitInPixels = 10;

    for (const musicPage of graphicSheet.MusicPages) {
      for (const musicSystem of musicPage.MusicSystems) {
        const staffLinesInSystem = musicSystem.StaffLines;

        for (let staffLineIndex = 0; staffLineIndex < staffLinesInSystem.length; staffLineIndex++) {
          const staffLine = staffLinesInSystem[staffLineIndex];
          const staffBBox = staffLine.PositionAndShape;
          const staffAbsPos = staffBBox.AbsolutePosition;

          const staffX = staffAbsPos.x * unitInPixels;
          const staffY = staffAbsPos.y * unitInPixels;

          const pitchLineGap = (this.osmd.EngravingRules.StaffHeight / 4) * unitInPixels;

          const isGrandStaff = staffLinesInSystem.length >= 2;
          const isBassClef = isGrandStaff && staffLineIndex % 2 === 1;
          const assetKey = isBassClef ? 'bass_clef' : 'treble_clef';

          if (!this.scene.textures.exists(assetKey)) continue;

          const clefScale = this.clefGapRatio * pitchLineGap;
          const clefImage = this.scene.add.image(0, 0, assetKey);
          clefImage.setScale(clefScale);
          clefImage.setTintFill(getScoreThemePalette(this.scoreTheme).notationColorNumber);

          if (!isBassClef) {
            clefImage.setRotation(-0.05);
          }

          clefImage.setOrigin(0, 0.5);

          const clefSymbolMargin = 5;
          const scaledClefHeight = clefImage.height * clefScale;

          let clefY: number;
          if (isBassClef) {
            clefY = staffY + pitchLineGap * 1 - scaledClefHeight * 0.15;
          } else {
            clefY = staffY + pitchLineGap * 3 - scaledClefHeight * 0.15;
          }

          clefImage.setPosition(staffX + clefSymbolMargin, clefY);
          this.container.add(clefImage);
          this.customClefImages.push(clefImage);
        }
      }
    }
  }

  private renderCustomBrace(): void {
    if (!this.osmd?.GraphicSheet) return;

    const graphicSheet = this.osmd.GraphicSheet;
    const unitInPixels = 10;

    for (const musicPage of graphicSheet.MusicPages) {
      for (const musicSystem of musicPage.MusicSystems) {
        const staffLines = musicSystem.StaffLines;
        if (staffLines.length < 2) continue;

        const assetKey = 'brace';
        if (!this.scene.textures.exists(assetKey)) continue;

        const firstStaff = staffLines[0];
        const lastStaff = staffLines[staffLines.length - 1];

        const pitchLineGap = (this.osmd.EngravingRules.StaffHeight / 4) * unitInPixels;
        const singleStaffHeight = pitchLineGap * 4;

        const trebleStaffTopY = firstStaff.PositionAndShape.AbsolutePosition.y * unitInPixels;
        const trebleStaffBottomY = trebleStaffTopY + singleStaffHeight;
        const bassStaffTopY = lastStaff.PositionAndShape.AbsolutePosition.y * unitInPixels;
        const bassStaffBottomY = bassStaffTopY + singleStaffHeight;

        const grandStaffHeight = bassStaffBottomY - trebleStaffTopY;
        const gapCenterY = (trebleStaffBottomY + bassStaffTopY) / 2;
        const staffX = firstStaff.PositionAndShape.AbsolutePosition.x * unitInPixels;

        const braceImage = this.scene.add.image(0, 0, assetKey);
        const braceScale = (grandStaffHeight / braceImage.height) * 0.88;
        braceImage.setScale(braceScale);
        braceImage.setTintFill(getScoreThemePalette(this.scoreTheme).notationColorNumber);
        braceImage.setOrigin(1, 0.5);

        const braceX = staffX - 3;
        const scaledBraceHeight = braceImage.height * braceScale;
        const braceY = gapCenterY - scaledBraceHeight * 0.07;
        braceImage.setPosition(braceX, braceY);

        this.container.add(braceImage);
        this.customBraceImage = braceImage;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Text quality and fingering position post-processing
  // ---------------------------------------------------------------------------

  /** Regex matching standard piano fingering numbers (0–5) as standalone text */
  private static readonly FINGERING_PATTERN = /^[0-5]$/;

  /** Scale factor applied to finger number font size (30% reduction from OSMD default) */
  private static readonly FINGERING_SCALE_FACTOR = 0.7;

  /**
   * Gap in OSMD-coordinate pixels between the extreme note and the
   * fixed fingering alignment line. Above-staff fingerings sit this
   * many pixels above the highest treble note; below-staff fingerings
   * sit this many pixels below the lowest bass note.
   */
  private static readonly FINGERING_GAP_ABOVE = 8;
  private static readonly FINGERING_GAP_BELOW = 8;

  /**
   * Walk the phrase container tree and set the resolution of every
   * Phaser.GameObjects.Text to the device pixel ratio for crisp
   * rendering on HiDPI (Retina) displays.
   *
   * Called after OSMD renders (both initial and re-render paths).
   */
  private upgradeTextResolution(): void {
    const dpr = window.devicePixelRatio || 1;
    if (dpr > 1) {
      this.upgradeTextResolutionRecursive(this.container, dpr);
    }
  }

  /**
   * Recursively visit every Text child in a Phaser container and
   * upgrade its resolution for HiDPI displays.
   */
  private upgradeTextResolutionRecursive(
    parentContainer: Phaser.GameObjects.Container,
    resolution: number,
  ): void {
    for (const child of parentContainer.list) {
      if (child instanceof Phaser.GameObjects.Text) {
        child.setResolution(resolution);
      } else if (child instanceof Phaser.GameObjects.Container) {
        this.upgradeTextResolutionRecursive(child, resolution);
      }
    }
  }

  /**
   * Post-process finger number text objects to match professional
   * engraving standards:
   *
   * 1. **Size**: Reduce font size by 30% from OSMD's default.
   * 2. **Centering**: Center each finger number horizontally on its note.
   * 3. **Fixed height**: Align all above-staff fingerings at a single Y
   *    and all below-staff fingerings at a single Y. Uses a two-pass
   *    approach: first collect all fingering texts and their absolute
   *    positions, then snap each group to the most extreme position
   *    within that group (highest for above-staff, lowest for below-staff).
   *
   * This avoids cross-referencing notePositions (OSMD GraphicSheet coords)
   * with text object positions (VexFlow render coords), which can differ.
   *
   * Called after `upgradeTextResolution()`.
   */
  private postProcessFingerNumbers(): void {
    const contentCenterY = this.calculateOriginalHeight() / 2;

    // --- Pass 1: Collect all fingering text objects with positions ---
    interface FingeringInfo {
      textObj: Phaser.GameObjects.Text;
      /** Cumulative Y offset of all ancestor containers above this text */
      parentYOffset: number;
      /** Absolute Y of the text bottom within the phrase container */
      absoluteBottomY: number;
    }
    const aboveStaffFingerings: FingeringInfo[] = [];
    const belowStaffFingerings: FingeringInfo[] = [];

    this.collectFingeringTexts(
      this.container,
      0,
      contentCenterY,
      aboveStaffFingerings,
      belowStaffFingerings,
    );

    // --- Pass 2: Compute fixed alignment targets ---
    // For above-staff: the topmost fingering (minimum absoluteBottomY) is
    // already at the correct height for the highest note. All others align
    // to it so every finger number sits on the same horizontal line.
    // For below-staff: the bottommost fingering is the reference.

    if (aboveStaffFingerings.length > 0) {
      const targetAbsY = Math.min(...aboveStaffFingerings.map((info) => info.absoluteBottomY));
      for (const info of aboveStaffFingerings) {
        this.styleAndPositionFingering(info.textObj, info.parentYOffset, targetAbsY, true);
      }
    }

    if (belowStaffFingerings.length > 0) {
      const targetAbsY = Math.max(...belowStaffFingerings.map((info) => info.absoluteBottomY));
      for (const info of belowStaffFingerings) {
        this.styleAndPositionFingering(info.textObj, info.parentYOffset, targetAbsY, false);
      }
    }
  }

  /**
   * Recursively walk the container tree and collect all fingering text
   * objects, partitioned into above-staff and below-staff groups.
   *
   * Each text object's origin is (0, 1) as set by PhaserVexFlowContext,
   * so textObj.y represents the bottom of the text in local coords.
   */
  private collectFingeringTexts(
    parentContainer: Phaser.GameObjects.Container,
    parentYOffset: number,
    contentCenterY: number,
    aboveStaffResults: Array<{
      textObj: Phaser.GameObjects.Text;
      parentYOffset: number;
      absoluteBottomY: number;
    }>,
    belowStaffResults: Array<{
      textObj: Phaser.GameObjects.Text;
      parentYOffset: number;
      absoluteBottomY: number;
    }>,
  ): void {
    for (const child of parentContainer.list) {
      if (child instanceof Phaser.GameObjects.Text) {
        if (OSMDPlayablePhrase.FINGERING_PATTERN.test(child.text)) {
          const absoluteBottomY = child.y + parentYOffset;
          const entry = { textObj: child, parentYOffset, absoluteBottomY };
          if (absoluteBottomY < contentCenterY) {
            aboveStaffResults.push(entry);
          } else {
            belowStaffResults.push(entry);
          }
        }
      } else if (child instanceof Phaser.GameObjects.Container) {
        this.collectFingeringTexts(
          child,
          parentYOffset + child.y,
          contentCenterY,
          aboveStaffResults,
          belowStaffResults,
        );
      }
    }
  }

  /**
   * Apply font size reduction, centering, and fixed-Y positioning to a
   * single fingering text object.
   *
   * PhaserVexFlowContext.fillText creates text with origin (0, 1) —
   * left-aligned, baseline-anchored. VexFlow positions above-staff and
   * below-staff fingerings with different x-offset conventions:
   *
   * - Above staff: VexFlow places x at the note's left edge, so after
   *   shrinking we shift x right by half the width difference to keep
   *   the visual center over the note head.
   * - Below staff: VexFlow places x closer to the note's center, so no
   *   additional offset is needed after shrinking.
   *
   * @param textObj        The fingering Phaser.GameObjects.Text.
   * @param parentYOffset  Cumulative Y offset of ancestor containers.
   * @param targetAbsY     Absolute Y to align this fingering to.
   * @param isAboveStaff   Whether this is an above-staff fingering.
   */
  private styleAndPositionFingering(
    textObj: Phaser.GameObjects.Text,
    parentYOffset: number,
    targetAbsY: number,
    isAboveStaff: boolean,
  ): void {
    // 1. Measure text width at original font size (before any changes)
    const originalWidth = textObj.width;

    // 2. Reduce font size by 30%
    const currentFontSize = parseFloat(textObj.style.fontSize as string);
    if (!isNaN(currentFontSize)) {
      textObj.setFontSize(currentFontSize * OSMDPlayablePhrase.FINGERING_SCALE_FACTOR);
    }

    // 3. Set font style to normal (OSMD renders bold by default)
    textObj.setFontStyle('normal');

    // 4. Center horizontally.
    //    VexFlow positions above-staff and below-staff text differently:
    //    - Above staff: x is at the note's left edge → shift right by
    //      half the width reduction to preserve the visual center.
    //    - Below staff: x is at the note's center → shift left by half
    //      the new width so the text's center lands on the note center.
    const newWidth = textObj.width;
    if (isAboveStaff) {
      textObj.x += (originalWidth - newWidth) / 2;
    } else {
      textObj.x -= newWidth / 2;
    }

    // 5. Snap to the fixed alignment Y
    //    targetAbsY is the absolute bottom-Y we want for all fingerings in this group.
    //    localY = targetAbsY - parentYOffset
    textObj.y = targetAbsY - parentYOffset;
  }

  // ---------------------------------------------------------------------------
  // Note position accessors (IOSMDNotePositionProvider)
  // ---------------------------------------------------------------------------

  getNoteContainer(id: string): NoteContainer | undefined {
    return this.noteContainers.get(id);
  }

  getStaveNoteContainers(): NoteContainer[] {
    const containers: NoteContainer[] = [];
    for (const noteContainer of this.noteContainers.values()) {
      if (noteContainer.elementType === 'stavenote') {
        containers.push(noteContainer);
      }
    }
    return containers;
  }

  getNotePositionCoords(id: string): { x: number; y: number } | undefined {
    const noteData = this.notePositions.find((noteDataItem) => noteDataItem.vexflowId === id);
    const scale = this.container.scaleX;
    if (noteData) {
      return {
        x: noteData.x * scale + this.container.x,
        y: noteData.y * scale + this.container.y,
      };
    }
    return undefined;
  }

  getNotePosition(id: string, timestamp?: number, rest?: boolean): number | null {
    const pos = this.getNoteXPosition(id, timestamp, rest);
    return pos !== undefined ? pos : null;
  }

  getNoteById(id: string, _phraseTimestamp?: number): IPlayable | null {
    const noteContainer = this.noteContainers.get(id);
    if (noteContainer) {
      return new NoteContainerPlayable(noteContainer);
    }
    return null;
  }

  getNoteXPosition(id: string, timestamp?: number, isRest?: boolean): number | undefined {
    const scale = this.container.scaleX;

    const noteData = this.notePositions.find((noteDataItem) => noteDataItem.vexflowId === id);
    if (noteData) {
      return noteData.x * scale + this.container.x;
    }

    if (timestamp !== undefined) {
      const notesByTimestamp = this.notePositions.filter(
        (noteDataItem) =>
          Math.abs(noteDataItem.timestamp - timestamp) < 0.001 &&
          noteDataItem.isRest === (isRest ?? false),
      );
      if (notesByTimestamp.length > 0) {
        return notesByTimestamp[0].x * scale + this.container.x;
      }
    }

    return undefined;
  }

  getFirstNotePosition(): number {
    const notes = this.notePositions.filter((noteDataItem) => !noteDataItem.isRest);
    const scale = this.container.scaleX;
    if (notes.length > 0) {
      const firstNote = notes.reduce(
        (minNote, noteDataItem) => (noteDataItem.x < minNote.x ? noteDataItem : minNote),
        notes[0],
      );
      return firstNote.x * scale + this.container.x;
    }
    return this.container.x;
  }

  getFirstNotePositionInMeasure(measureIndex: number): number | undefined {
    const measureNumber = measureIndex + 1;
    const notes = this.notePositions.filter(
      (noteDataItem) => noteDataItem.measureNumber === measureNumber && !noteDataItem.isRest,
    );
    const scale = this.container.scaleX;
    if (notes.length > 0) {
      return notes[0].x * scale + this.container.x;
    }
    return undefined;
  }

  /**
   * Get the actual start timestamp for a measure, correctly handling
   * pickup (anacrusis) measures. Returns the minimum timestamp across
   * all notes/rests in the measure, which accounts for the reduced
   * duration of pickup measures.
   *
   * The returned timestamp includes phraseStartTimestamp. Callers
   * should subtract data.startTimestamp to get a phrase-local value.
   *
   * @param localMeasureIndex - 0-based local measure index within this phrase
   * @returns The absolute timestamp of the first beat, or undefined if no notes found
   */
  getMeasureStartTimestamp(localMeasureIndex: number): number | undefined {
    const localMeasureNumber = localMeasureIndex + 1;
    const notesInMeasure = this.notePositions.filter(
      (noteDataItem) => noteDataItem.measureNumber === localMeasureNumber,
    );
    if (notesInMeasure.length === 0) return undefined;
    return Math.min(...notesInMeasure.map((noteDataItem) => noteDataItem.timestamp));
  }

  /**
   * Get the X position of the first beat in a measure, including rests.
   * Unlike getFirstNotePositionInMeasure (which skips rests), this returns
   * the position of the earliest element in the measure — whether it's a
   * sounding note or a rest. Used for cursor repositioning on pause so the
   * cursor goes to beat 1 even when the measure starts with a rest.
   *
   * @param measureIndex - 0-based local measure index within this phrase
   */
  getFirstBeatPositionInMeasure(measureIndex: number): number | undefined {
    const measureNumber = measureIndex + 1;
    const beatsInMeasure = this.notePositions.filter(
      (noteDataItem) => noteDataItem.measureNumber === measureNumber,
    );
    if (beatsInMeasure.length === 0) return undefined;

    // Find the element with the smallest X position (leftmost = first beat)
    const firstBeat = beatsInMeasure.reduce(
      (earliest, noteDataItem) => (noteDataItem.x < earliest.x ? noteDataItem : earliest),
      beatsInMeasure[0],
    );
    const scale = this.container.scaleX;
    return firstBeat.x * scale + this.container.x;
  }

  getPhraseEndPosition(): number {
    const scaledWidth = this.getScaledWidth();
    if (scaledWidth > 0) {
      return this.container.x + scaledWidth;
    }
    const scale = this.container.scaleX;
    const resolvedWidth = this.originalWidth || this.calculateOriginalWidth();
    return this.container.x + resolvedWidth * scale;
  }

  /**
   * Return the world-space X coordinate of the right edge of the staff lines.
   * Unlike getScaledWidth() (which includes extra padding for content bounds),
   * this reads the actual StaffLine PositionAndShape from OSMD's GraphicSheet
   * so the result is flush with where the horizontal staff lines end.
   */
  getStaffLineRightEdgeX(): number {
    if (this.osmd?.GraphicSheet) {
      const unitInPixels = 10;
      const pages = this.osmd.GraphicSheet.MusicPages;
      let maxStaffRight = 0;
      for (const page of pages) {
        for (const system of page.MusicSystems) {
          for (const staffLine of system.StaffLines) {
            const bbox = staffLine.PositionAndShape;
            const staffRight = (bbox.AbsolutePosition.x + bbox.Size.width) * unitInPixels;
            maxStaffRight = Math.max(maxStaffRight, staffRight);
          }
        }
      }
      if (maxStaffRight > 0) {
        const scale = this.container.scaleX;
        return this.container.x + maxStaffRight * scale;
      }
    }
    // Fallback to the padded content width if OSMD data is unavailable
    return this.getPhraseEndPosition();
  }

  /**
   * Get the world-space X positions of each measure's right barline.
   * Reads OSMD's Measure.PositionAndShape to get exact barline coordinates.
   */
  getMeasureBarlineXPositions(): number[] {
    const barlines: number[] = [];
    if (!this.osmd?.GraphicSheet) return barlines;

    const unitInPixels = 10;
    const scale = this.container.scaleX;

    for (const page of this.osmd.GraphicSheet.MusicPages) {
      for (const system of page.MusicSystems) {
        if (system.StaffLines.length === 0) continue;
        // Only iterate the first staff line to avoid duplicates in grand staff
        for (const measure of system.StaffLines[0].Measures) {
          const pos = measure.PositionAndShape;
          const measureRightLocal = (pos.AbsolutePosition.x + pos.Size.width) * unitInPixels;
          const measureRightWorld = this.container.x + measureRightLocal * scale;
          barlines.push(measureRightWorld);
        }
      }
    }

    return barlines.sort((a, b) => a - b);
  }

  containsMeasure(measureIndex: number): boolean {
    const localMeasureNumber = measureIndex - this.phraseStartingMeasure + 1;
    if (localMeasureNumber < 1) return false;
    return this.notePositions.some(
      (noteDataItem) => noteDataItem.measureNumber === localMeasureNumber,
    );
  }

  // ---------------------------------------------------------------------------
  // Dimensions and scaling
  // ---------------------------------------------------------------------------

  getHeight(): number {
    if (this._scaledHeight > 0) {
      return this._scaledHeight;
    }
    return this.originalHeight || this.calculateOriginalHeight();
  }

  getWidth(): number {
    if (this._scaledWidth > 0) {
      return this._scaledWidth;
    }
    return this.originalWidth || this.calculateOriginalWidth();
  }

  getScaledHeight(): number {
    return this._scaledHeight || this.getHeight();
  }

  getScaledWidth(): number {
    return this._scaledWidth || this.getWidth();
  }

  getOriginalWidth(): number {
    return this.originalWidth;
  }

  getOriginalHeight(): number {
    return this.originalHeight;
  }

  /**
   * Get the vertical center of the staff content (for cursor positioning).
   */
  getStaffCenterY(): number {
    return this.container.y + this.getHeight() / 2;
  }

  /**
   * Get the vertical span of the staff content.
   */
  getStaffSpan(): number {
    return this.getHeight();
  }

  /**
   * Scale the phrase to fit target dimensions while maintaining aspect ratio.
   * Uses the minimum of X and Y scale factors so content fits within bounds.
   */
  bestFit(targetWidth: number, targetHeight: number): void {
    const unscaledWidth = this.originalWidth || this.calculateOriginalWidth();
    const unscaledHeight = this.originalHeight || this.calculateOriginalHeight();
    const scaleX = targetWidth / unscaledWidth;
    const scaleY = targetHeight / unscaledHeight;
    const scale = Math.min(scaleX, scaleY);

    this.bestFitWithScale(scale, targetWidth);
  }

  /**
   * Apply a specific scale factor and center horizontally within the target width.
   * Used when a unified scale is calculated across multiple phrases.
   */
  bestFitWithScale(scale: number, targetWidth: number): void {
    const unscaledWidth = this.originalWidth || this.calculateOriginalWidth();
    const unscaledHeight = this.originalHeight || this.calculateOriginalHeight();

    this.container.setScale(scale);
    this._scaledHeight = unscaledHeight * scale;
    this._scaledWidth = unscaledWidth * scale;

    // Center the content horizontally within the available width
    const centerX = (targetWidth - this._scaledWidth) / 2;
    this.container.setX(centerX);
  }

  // ---------------------------------------------------------------------------
  // Content edge helpers (local / unscaled coordinates)
  // ---------------------------------------------------------------------------

  /**
   * Local-space X of the left edge of the phrase content:
   * brace left edge if visible, otherwise the first measure's left barline.
   */
  getContentLeftEdgeLocal(): number {
    if (this.customBraceImage) {
      const braceLeft = this.customBraceImage.x - this.customBraceImage.displayWidth;
      return braceLeft;
    }
    return this.getFirstMeasureLeftLocal();
  }

  /**
   * Local-space X of the right edge of the phrase content:
   * the right barline of the last measure.
   */
  getContentRightEdgeLocal(): number {
    if (!this.osmd?.GraphicSheet) return this.originalWidth;
    const unitInPixels = 10;
    let maxRight = 0;
    for (const page of this.osmd.GraphicSheet.MusicPages) {
      for (const system of page.MusicSystems) {
        if (system.StaffLines.length === 0) continue;
        for (const measure of system.StaffLines[0].Measures) {
          const pos = measure.PositionAndShape;
          const right = (pos.AbsolutePosition.x + pos.Size.width) * unitInPixels;
          maxRight = Math.max(maxRight, right);
        }
      }
    }
    return maxRight > 0 ? maxRight : this.originalWidth;
  }

  /**
   * Local-space X of the first measure's left barline.
   */
  private getFirstMeasureLeftLocal(): number {
    if (!this.osmd?.GraphicSheet) return 0;
    const unitInPixels = 10;
    let minLeft = Infinity;
    for (const page of this.osmd.GraphicSheet.MusicPages) {
      for (const system of page.MusicSystems) {
        if (system.StaffLines.length === 0) continue;
        for (const measure of system.StaffLines[0].Measures) {
          const pos = measure.PositionAndShape;
          const left = pos.AbsolutePosition.x * unitInPixels;
          minLeft = Math.min(minLeft, left);
        }
      }
    }
    return minLeft === Infinity ? 0 : minLeft;
  }

  /**
   * Local-space Y of the top staff line. Used by the mobile horizontal
   * layout to align phrases by their staff baseline rather than by
   * container center — phrases with different amounts of content above
   * the staff (ledger lines, articulations) would otherwise have their
   * staves at different vertical positions.
   */
  getFirstStaffLineYLocal(): number {
    if (!this.osmd?.GraphicSheet) return 0;
    const unitInPixels = 10;
    let minTop = Infinity;
    for (const page of this.osmd.GraphicSheet.MusicPages) {
      for (const system of page.MusicSystems) {
        for (const staffLine of system.StaffLines) {
          const top = staffLine.PositionAndShape.AbsolutePosition.y * unitInPixels;
          minTop = Math.min(minTop, top);
        }
      }
    }
    return minTop === Infinity ? 0 : minTop;
  }

  /**
   * The precise width of the phrase content in local (unscaled) coordinates:
   * right barline minus left content edge (brace or first barline).
   */
  getContentWidth(): number {
    return this.getContentRightEdgeLocal() - this.getContentLeftEdgeLocal();
  }

  // ---------------------------------------------------------------------------
  // Position helpers
  // ---------------------------------------------------------------------------

  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  setY(yPos: number): void {
    this.container.setY(yPos);
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  showAfterLayout(): void {
    this.container.setVisible(true);
  }

  getPhraseStartX(): number {
    return this.container.x;
  }

  getNextPosition(): number {
    return this.getPhraseEndPosition();
  }

  // ---------------------------------------------------------------------------
  // Activation / deactivation
  // ---------------------------------------------------------------------------

  activate(unpause?: boolean): void {
    this.isActive = true;
    this.cursor?.enable();
    if (unpause) this.cursor?.unpause();
  }

  deactivate(): void {
    this.isActive = false;
    this.cursor?.disable();
  }

  pause(): void {
    this.cursor?.pause();
  }

  update(): void {
    if (this.isActive) {
      this.cursor?.update();
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;

    if (this.osmd?.Drawer?.Backends) {
      for (const backend of this.osmd.Drawer.Backends) {
        if (backend.getOSMDBackendType() === BackendType.Phaser) {
          backend.free();
        }
      }
    }

    if (this.dummyContainer) {
      this.dummyContainer.remove();
      this.dummyContainer = null;
    }

    this.cursor?.destroy();
    this.container.destroy();

    this.osmd = null;
    this.phaserBackend = null;
    this.noteContainers.clear();
    this.notePositions = [];
  }
}

// ============================================================================
// NoteContainerPlayable — adapter for IPlayable interface
// ============================================================================

/**
 * Wrapper class to make NoteContainer implement IPlayable interface.
 * Identical to OSMDSightReadingScene's NoteContainerPlayable.
 */
class NoteContainerPlayable implements IPlayable {
  private noteContainer: NoteContainer;
  private currentState?: ProgressState;
  isFocused: boolean = false;

  constructor(noteContainer: NoteContainer) {
    this.noteContainer = noteContainer;
  }

  getPosition(): { x: number; y: number } {
    const bounds = this.noteContainer.getBounds();
    return {
      x: bounds?.x ?? 0,
      y: bounds?.y ?? 0,
    };
  }

  getState(): ProgressState | undefined {
    return this.currentState;
  }

  setTimingState(state: ProgressState): void {
    this.currentState = state;
    let noteState: NoteState = 'default';
    switch (state) {
      case ProgressState.Ended:
        noteState = 'correct';
        break;
      // MUS-394: a late release (over-hold) emits MistimedRelease and must show yellow
      // on the container path too, matching the NoteGraphic path. Without this case it
      // fell through to 'default' and reset an over-held note to uncolored.
      case ProgressState.LateStart:
      case ProgressState.MistimedRelease:
        noteState = 'late';
        break;
      case ProgressState.TimelyStart:
        noteState = 'correct';
        break;
      case ProgressState.Missed:
      case ProgressState.Missing:
        noteState = 'missed';
        break;
      case ProgressState.Waiting:
        noteState = this.isFocused ? 'focused' : 'default';
        break;
      default:
        noteState = 'default';
    }
    this.noteContainer.setState(noteState);
  }

  update(_cursorPosition: number): void {
    // NoteContainer handles its own updates via state changes
  }

  setFocus(focus: boolean): void {
    this.isFocused = focus;
    if (focus) {
      this.noteContainer.setState('focused');
    } else if (this.currentState === ProgressState.Waiting || this.currentState === undefined) {
      this.noteContainer.setState('default');
    }
  }

  setColor(color: string | number): void {
    const colorNum = typeof color === 'string' ? parseInt(color.replace('#', ''), 16) : color;
    this.noteContainer.setColor(colorNum);
  }

  pulse(): void {
    this.noteContainer.pulse();
    this.noteContainer.scalePop();
  }

  getHaloOffset(): number {
    return 0;
  }
}

// ============================================================================
// OSMDPlayableScene — main scene class
// ============================================================================

/**
 * OSMD-based repertoire scene.
 * Renders MusicXML using OSMD's Phaser backend with all phrases loaded at once
 * and stacked vertically with camera Y-scrolling.
 */
export default abstract class OSMDPlayableScene
  extends Phaser.Scene
  implements INoteGraphicsDatabase, INoteContainerProvider
{
  /** All playable phrases — dynamic array, not fixed-size slots */
  phrases: OSMDPlayablePhrase[] = [];

  /** The event stream for MIDI event processing */
  eventStream!: OSMDEventStream;

  /** Scene width (logical pixels) */
  width: number = 0;
  /** Scene height (logical pixels) */
  height: number = 0;

  /** Index of the currently active phrase */
  protected activePhrase: number = 0;
  /** Cumulative measure count across all phrases */
  protected measureCount: number = 0;
  /** The widest container width (CSS px) used by any phrase so far.
   *  All phrases are normalized to this width so they display the same
   *  on-screen width after unified scaling. */
  private maxPhraseContainerWidth: number = 0;

  /** Auxiliary timekeeper for count-in cursor blinking */
  protected timeKeeperAuxiliary?: ITimeKeeper;
  /** Current tempo in BPM (adjustable by user) */
  protected tempo: number = 72;
  /** Goal/intended tempo for the song (shown in BPM indicator, never changes) */
  private performanceTempo: number = 72;
  /** Repertoire title string (passed from React) */
  private titleString: string = '';
  /** Composer name string (passed from React) */
  private composerString: string = '';
  /** Title text object rendered above the score in world space */
  private titleText?: Phaser.GameObjects.Text;
  /** Composer text object rendered below the title in world space */
  private composerText?: Phaser.GameObjects.Text;
  /** BPM indicator: quarter note symbol */
  private bpmNoteText?: Phaser.GameObjects.Text;
  /** BPM indicator: "= {tempo}" text */
  private bpmValueText?: Phaser.GameObjects.Text;
  /** "For educational purposes only" text positioned below the last phrase (scrolls with content) */
  private educationalText?: Phaser.GameObjects.Text;
  /** Metronome click audio for count-in phase (loaded from Preloader scene) */
  protected clickAudio?: Phaser.Sound.BaseSound;

  /** Last metronome tick timestamp for beat spacing */
  protected lastTickTime?: number;
  /** The beat clock's place in the bar: beats ticked since lastTickTime was seeded. */
  private beatClockBeatIndex = 0;
  /** lastTickTime as the beat clock last left it (a different value = re-seeded). */
  private beatClockLastTickTime?: number;

  /** Whether playback has started (after count-in) */
  protected playbackStarted: boolean = false;
  /** True while native Scheduler2 is unpausing after the first note/count-in. */
  private playbackStartPending: boolean = false;
  /** MIDI events that arrive during native Scheduler2 startup. */
  private pendingPlaybackStartEvents: Array<
    { type: 'on'; payload: MidiOnPayload } | { type: 'off'; payload: MidiOffPayload }
  > = [];
  /** Whether the count-in phase is active (cursor blinking, waiting for first note).
   *  Used to make startExercise idempotent — if the count-in was already started
   *  by softReset's auto-start, a subsequent StartExercise from the container
   *  won't re-initialize the auxiliary TimeKeeper or replay the first click. */
  private countInActive: boolean = false;
  /** Timer used by listen mode to finish the count-in without waiting for MIDI input. */
  private autoPlaybackCountInTimer?: Phaser.Time.TimerEvent;
  /** Whether the update loop should run */
  protected shouldUpdate: boolean = false;
  /** Whether a pause was requested (processed on next update) */
  protected pauseQueued: boolean = false;
  /** Whether all phrases have been played — prevents cursor reposition on pause */
  private pieceCompleted: boolean = false;
  /** Whether the RepertoireCompleted event has already been emitted to React.
   *  Guards against double-emission: both _pauseExercise (scheduler timeout
   *  path) and onFinishPlayingPhrase (cursor finish path) can trigger
   *  onFinishAllPhrases, but only the first should emit the event. */
  private repertoireCompletedEmitted: boolean = false;
  /** Whether cursors should be visually rendered (toggled by the user via settings) */
  protected cursorVisible: boolean = true;
  private scoreTheme: ScoreTheme = 'light';

  /** Queue of MIDI events received before playback fully started */
  protected eventsQueue: MidiOnPayload[] = [];
  /** Start request received before phrase rendering completed. */
  private pendingStartExercisePayload: StartExercisePayload | null = null;
  /** On iOS the main TimeKeeper unpause is asynchronous: the native scheduler
   *  must await `MuseFlowAudio.startPlayback()` (to read the native start time)
   *  before it can unpause the TimeKeeper. `unpauseScheduler2At` captures that
   *  promise here so the count-in's first note is forwarded into the event
   *  stream only once the TimeKeeper is actually running. Forwarding it
   *  synchronously drops the first note — `handleNoteOnEvent` bails while the
   *  TimeKeeper is still paused. Null on web, where the unpause is synchronous
   *  and the note can forward immediately. */
  private pendingScheduler2Unpause: Promise<void> | null = null;
  /** True while addAllPhrases is asynchronously rendering/loading the score. */
  private isAddingPhrases: boolean = false;

  /** Full payload count for the current score, including phrases not rendered yet. */
  private totalPhraseCount: number = 0;
  /** Phrase payloads waiting for viewport/playback-driven rendering. */
  private pendingPhraseDatas: OSMDPhraseDataEventPayload[] = [];
  /** Serializes lazy batches so scroll, playback, and resize cannot render concurrently. */
  private lazyPhraseLoadPromise: Promise<void> | null = null;
  /** Invalidates async batches when the piece changes or the scene shuts down. */
  private phraseLoadGeneration: number = 0;
  /** The active cursor finished while its next lazy batch was still rendering. */
  private waitingForLazyPhraseAdvance: boolean = false;
  /** A reset overlapped an in-flight lazy batch. Re-run it after the batch
   *  commits so late cursor/note replacements cannot undo the reset state. */
  private softResetPendingAfterLazyLoad: boolean = false;

  /** Whether a programmatic scroll tween is in flight (e.g. scrolling to next
   *  phrase after onPlayingLastSlice). While true, ensureActivePhraseVisible()
   *  is skipped so that a noteOn on the old activePhrase doesn't fight the
   *  tween and snap the camera back. */
  private isAutoScrolling: boolean = false;

  /** The phrase index we are transitioning TO after onPlayingLastSlice fires.
   *  Covers the gap between the scroll tween completing (isAutoScrolling → false)
   *  and onFinishPlayingPhrase updating this.activePhrase. Without this,
   *  ensureActivePhraseVisible reads the stale activePhrase and scrolls back
   *  to the old phrase, causing a down-up-down jitter. Cleared when
   *  onFinishPlayingPhrase completes the transition, or on pause/softReset. */
  private pendingPhraseTransition: number | null = null;

  // -- Cursor placement (looping) -------------------------------------------

  /** The timestamp the user clicked/tapped to place the cursor at.
   *  When set, playback will start from this position instead of the
   *  beginning. Cleared on soft-reset or piece completion. */
  cursorStartTimestamp: number | null = null;

  // -- Bracket overlay (looping) -------------------------------------------

  /** Visual bracket overlay that highlights the looped section. */
  private bracketOverlay: BracketOverlay | null = null;

  /** Perfect-phrase counter: tracks 100%-accuracy loop streaks. */
  private perfectPhrase: PerfectPhraseController | null = null;

  /** Streak badge rendered at the top-right of the loop bracket. */
  private perfectPhraseBadge: PerfectPhraseBadge | null = null;

  /** Whether the Perfect Phrase Counter is enabled (sourced from React/Redux
   *  via SetPerfectPhraseConfig). When off, the badge is hidden and no
   *  "perfected" toast fires. */
  private perfectPhraseEnabled = false;

  /** Qualifying streak size for "perfected" (number of checkboxes). */
  private perfectPhraseTarget = 0;

  /** Last streak seen by the streak-changed handler, used to detect the
   *  upward crossing into the target (fire the perfected toast once). */
  private prevPerfectStreak = 0;

  /** Perfect-phrase: latched true the moment the current loop iteration first
   *  reaches a clean 100% (every expected note green, no errors). Once latched,
   *  the iteration counts as perfect for the rest of its life — a trailing
   *  "anticipatory" note (the user starting the NEXT iteration's first note a
   *  hair early, within the early-onset cushion, before the cursor reaches the
   *  loop end and restartLoop() runs) lands as an extra note in the still-open
   *  loop range. Without the latch that extra note flips the range to
   *  hasError=true, which both drops the streak via the per-tick poll AND fails
   *  the strict end-of-loop check, denying the point the user just earned by
   *  playing the whole loop perfectly. Reset at the start of every iteration. */
  private perfectLoopLatched = false;

  /** Debug only ([PPC] logs): last per-tick "accuracy ok" value passed to the
   *  controller, so the poll logs a line only when the verdict flips (avoids
   *  flooding at ~60 Hz). Reset to null at the start of each iteration. */
  private prevPerfectTickOk: boolean | null = null;

  /** Max pause duration (ms) before a perfect-phrase streak is dropped. */
  private static readonly PERFECT_PHRASE_PAUSE_GRACE_MS = 60_000;

  /** `performance.now()` captured when playback entered the paused phase, used
   *  to decide whether a resume exceeded the perfect-phrase pause grace. */
  private perfectPhrasePausedAt: number | null = null;

  /** Whether loop playback is currently active.
   *  When true, the cursor will loop back to the bracket start when it
   *  reaches the bracket end timestamp. Set by setLoopingActive(). */
  private isLoopingActive: boolean = false;

  /** Whether the scene is in a loop-restart count-in phase.
   *  When true, the metronome is clicking and the cursor is blinking at the
   *  bracket start position, waiting for the user's first note to resume
   *  playback within the loop. Set by restartLoop(), cleared by noteOn(). */
  private isLoopCountInActive: boolean = false;

  /** Whether the scene has ever entered playback (first note played).
   *  Used to distinguish first play from resume after pause. */
  private hasEverPlayed: boolean = false;

  /** The effective loop end timestamp — bracket end + last note's duration.
   *  Used by checkLoopBoundary to wait for the last note to finish. */
  private effectiveLoopEndTimestamp: number = 0;

  /** Whether the scene is waiting for the first correct note after a loop
   *  restart to trigger the ripple fade animation. Set to true in
   *  restartLoop(), cleared when the first correct onset is detected. */
  private awaitingRippleFadeTrigger: boolean = false;

  /** Active Phaser tweens for the ripple fade animation, tracked so they
   *  can be cancelled if playback stops mid-animation. */
  private rippleFadeTweens: Phaser.Tweens.Tween[] = [];

  /** Counts correct notes after loop restart to trigger ripple fade on the second note. */
  private rippleFadeNoteCount: number = 0;

  /** Buffer of recent MIDI notes, used to replay notes (including chords)
   *  that arrive just before restartLoop fires. */
  private recentNoteBuffer: Array<{ payload: MidiOnPayload; time: number }> = [];

  // -- Mobile view ---------------------------------------------------------

  /** User-controlled zoom factor (0.5 – 1.0). Combined with maxZoomLevel
   *  to set the camera zoom: camera.zoom = zoomLevel * maxZoomLevel. */
  private zoomLevel: number = 0.9;

  /** The camera zoom value at which the tallest phrase fills the viewport
   *  vertically (minus padding). Computed after layout so zoom=1 means
   *  "phrase fills the screen." */
  private maxZoomLevel: number = 1;

  /** Active zoom tween (stopped before starting a new one). */
  private zoomTween?: Phaser.Tweens.Tween;

  /** World-space distance between the cursor center X and the left content
   *  edge of the first phrase (brace or first barline). Captured once after
   *  initial layout so the camera scroll can keep this gap visible at any zoom. */
  private mobileZoomBaselineGap?: number;

  /** Whether the scene is rendering in mobile layout mode */
  mobileView: boolean = false;
  /** On-screen debug overlay for mobile camera tracking */
  protected debugText?: Phaser.GameObjects.Text;

  // -- Scroll input state --------------------------------------------------

  /** Bound wheel event handler (stored for cleanup in destroy) */
  private boundWheelHandler?: (event: WheelEvent) => void;
  /** Whether a pointer drag scroll is in progress */
  private isDraggingScroll: boolean = false;
  /** The pointer Y at the start of the drag (screen coordinates) */
  private dragStartPointerY: number = 0;
  /** The camera scrollY at the start of the drag */
  private dragStartScrollY: number = 0;
  /** The pointer X at the start of the drag (for tap detection) */
  private dragStartPointerX: number = 0;
  /** The camera scrollX at the start of the drag (mobile horizontal scroll) */
  private dragStartScrollX: number = 0;
  /** Whether the user has manually scrolled the mobile camera on the X-axis.
   *  When true, suppresses cursor-driven camera updates so the view stays
   *  where the user released the drag. Cleared when playback (re)starts. */
  private mobileUserScrolledX: boolean = false;
  lastResizeProps?: { width: number; height: number; playButtonOverlap?: number };

  /** DEBUG: per-phrase bounds overlay objects, cleared/recreated each layout. */
  private phraseDebugObjects: Phaser.GameObjects.GameObject[] = [];

  constructor(key: string) {
    super(key);
  }

  /**
   * Log memory diagnostics — counts of Phaser game objects, textures,
   * DOM elements, and JS heap usage to help identify what's leaking.
   */
  private logMemoryDiagnostics(label: string): void {
    Logger.debug(`[MUS-145-MEM] === ${label} ===`);
    Logger.debug(`[MUS-145-MEM] Phrases: ${this.phrases?.length ?? 0}`);

    // Phrase-level stats (safe even during teardown)
    let totalNoteContainers = 0;
    let totalGraphics = 0;
    (this.phrases ?? []).forEach((phrase) => {
      totalNoteContainers += phrase.noteContainers?.size ?? 0;
      try {
        if (phrase.container) {
          phrase.container.each((child: Phaser.GameObjects.GameObject) => {
            if (child.type === 'Graphics') totalGraphics++;
          });
        }
      } catch {
        // container may be destroyed
      }
    });
    Logger.debug(
      `[MUS-145-MEM] NoteContainers: ${totalNoteContainers}, Graphics: ${totalGraphics}`,
    );

    // DOM-level stats (always safe)
    const gameContainer = document.getElementById('game-container');
    const domElements = gameContainer?.querySelectorAll('*').length ?? 0;
    const osmdDivs = document.querySelectorAll('div[style*="position: fixed"]').length;
    Logger.debug(
      `[MUS-145-MEM] DOM elements in #game-container: ${domElements}, OSMD hidden divs: ${osmdDivs}`,
    );

    // JS heap (Chrome/Safari only)
    const perfMemory = (
      performance as unknown as {
        memory?: {
          usedJSHeapSize: number;
          totalJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      }
    ).memory;
    const heapInfo = perfMemory
      ? `${(perfMemory.usedJSHeapSize / 1024 / 1024).toFixed(1)}MB / ${(perfMemory.totalJSHeapSize / 1024 / 1024).toFixed(1)}MB (limit: ${(perfMemory.jsHeapSizeLimit / 1024 / 1024).toFixed(0)}MB)`
      : 'N/A';
    Logger.debug(`[MUS-145-MEM] JS Heap: ${heapInfo}`);

    // Game-level stats (may not be available during teardown)
    try {
      const game = this.game;
      if (game) {
        const sceneChildren = this.children?.length ?? 0;
        //const textureCount = game.textures?.list ? Object.keys(game.textures.list).length : 0;
        //let soundCount = 0;
        /*if (game.sound instanceof Phaser.Sound.WebAudioSoundManager) {
          soundCount = game.sound?.sounds?.length ?? 0;
        } else {
          console.error('[MUS-145-MEM] No audio sound manager');
        }*/

        let totalGameObjects = 0;
        game.scene?.scenes?.forEach((scene: Phaser.Scene) => {
          totalGameObjects += scene.children?.length ?? 0;
        });

        Logger.debug(
          `[MUS-145-MEM] Scene children: ${sceneChildren}, Total game objects: ${totalGameObjects}`,
        );
        //console.error(`[MUS-145-MEM] Textures: ${textureCount}, Sounds: ${soundCount}`);

        const renderer = game.renderer;
        if (renderer && 'gl' in renderer && renderer.gl) {
          const gl = renderer.gl;
          const dbg = gl.getExtension('WEBGL_debug_renderer_info');
          const glInfo = dbg
            ? (gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) as string)
            : 'WebGL active';
          Logger.debug(`[MUS-145-MEM] WebGL: ${glInfo}`);
        }
      }
    } catch {
      Logger.debug('[MUS-145-MEM] (game-level stats unavailable — scene tearing down)');
    }
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  preload(): void {
    const basePath = '/phaser/graphics/';

    // Notation glyph textures persist in the global TextureManager across scene
    // re-entry. Re-loading an already-registered key makes Phaser log
    // "Texture key already in use", so only queue a load when the key is absent.
    const loadSvgOnce = (key: string, path: string): void => {
      if (!this.textures.exists(key)) {
        this.load.svg(key, path);
      }
    };

    loadSvgOnce('treble_clef', `${basePath}treble_clef.svg`);
    loadSvgOnce('bass_clef', `${basePath}bass_clef.svg`);
    loadSvgOnce('brace', `${basePath}brace.svg`);

    for (let digitIndex = 1; digitIndex <= 9; digitIndex++) {
      loadSvgOnce(`time_sig_${digitIndex}`, `${basePath}time_sig_${digitIndex}.svg`);
    }
  }

  init({
    eventStream,
    mobile,
    width,
    height,
    performanceTempo,
    title,
    composerName,
    cursorVisible: cursorVisiblePayload,
    practiceDarkMode = false,
    scoreTheme,
    zoomLevel,
  }: {
    eventStream: OSMDEventStream;
    mobile?: boolean;
    width: number;
    height: number;
    performanceTempo?: number;
    title?: string;
    composerName?: string;
    cursorVisible?: boolean;
    practiceDarkMode?: boolean;
    scoreTheme?: ScoreTheme;
    zoomLevel?: number;
  }): void {
    console.log(`[REP] Scene init() — cursorVisiblePayload=${cursorVisiblePayload}`);
    this.mobileView = mobile ?? false;
    this.width = width;
    this.height = height;
    this.eventStream = eventStream;
    // Perfect-phrase: let the event stream drop the streak the instant a note in
    // the loop range grades yellow/red, so a semi-error breaks perfection even
    // after the iteration momentarily latched at a clean 100%.
    this.eventStream.setLoopPerfectionBrokenCallback(() => this.handleLoopPerfectionBroken());
    this.scoreTheme = normalizeScoreTheme(scoreTheme, practiceDarkMode);
    if (performanceTempo) {
      this.performanceTempo = performanceTempo;
    }
    this.titleString = title ?? '';
    this.composerString = composerName ?? '';
    // Set before any ListenAddOSMDPhrase collectWaiting runs createCursorForPhrase
    if (cursorVisiblePayload !== undefined) {
      this.cursorVisible = cursorVisiblePayload;
      Logger.info('[CURSOR-VIS] Scene init: cursorVisible from payload', {
        cursorVisible: this.cursorVisible,
      });
    }
    // Clear stale references from previous scene run (Phaser destroys game
    // objects on shutdown but our instance fields still point at them).
    this.titleText = undefined;
    this.composerText = undefined;
    this.bpmNoteText = undefined;
    this.bpmValueText = undefined;
    this.educationalText = undefined;
    this.phrases = [];
    this.phraseLoadGeneration++;
    this.totalPhraseCount = 0;
    this.pendingPhraseDatas = [];
    this.lazyPhraseLoadPromise = null;
    this.waitingForLazyPhraseAdvance = false;
    this.softResetPendingAfterLazyLoad = false;
    this.cursorStartTimestamp = null;
    // Use a plain JS TimeKeeper for the auxiliary — NOT a NativeTimeKeeper.
    // The auxiliary TK is only used for count-in timestamp tracking and
    // cursor blink timing. A NativeTimeKeeper would trigger native-side
    // metronome scheduling on unpause, causing a duplicate metronome on iOS.
    this.timeKeeperAuxiliary = new TimeKeeper();
    const scorePalette = getScoreThemePalette(this.scoreTheme);
    // Use the score/paper color for the whole camera background. Previously the
    // per-phrase PageBackgroundColor fillRect provided the paper area, but that
    // opaque rect overlapped (and clipped) adjacent phrases in the horizontal
    // mobile chain, so it has been removed (see initialize()). The camera
    // background now supplies the paper backdrop uniformly.
    this.cameras.main.setBackgroundColor(scorePalette.scoreBackgroundPhaser);

    if (zoomLevel !== undefined) {
      Logger.debug('[zoom::init] loading repertoire scene with zoomLevel', zoomLevel);
      this.zoomLevel = Math.max(0.5, Math.min(zoomLevel, 1));
    }

    Logger.debug('[OSMD-MIDI] Scene init()', {
      eventStreamId: eventStream.id,
      width,
      height,
    });

    // Set this scene as the note container provider for the event stream
    eventStream.setNoteContainerProvider(this);

    // Set up event listeners for React → Phaser events
    console.log(`[LOOP-DBG] Scene init() — registering event listeners`);
    const initSize = {
      x: this.scale.width / window.devicePixelRatio,
      y: this.scale.height / window.devicePixelRatio,
      w: this.game.canvas.width,
      h: this.game.canvas.height,
    };
    this.lastResizeProps = { width: initSize.x, height: initSize.y, playButtonOverlap: undefined };
    console.debug('[OSMDPlayableScene::init] initial scene sizes', initSize);

    // Idempotent registration: the EventBus is a module-level singleton that
    // outlives any single scene instance. If init() runs more than once over
    // its lifetime (scene restart, React re-mount, or dev HMR) without a
    // matching destroy() in between, these `EventBus.on` registrations
    // accumulate — every MidiOn then fans out to N copies of noteOn(), so a
    // single keypress is graded HIT once and ADDITIONAL_NOTE (yellow) on every
    // extra copy. Remove any prior registrations for these events first so we
    // always end with exactly one handler bound to the current scene instance.
    for (const eventName of OSMD_REPERTOIRE_REACT_LISTENER_EVENTS) {
      RemoveListener(eventName);
    }

    ReactToPhaserListeners.ResizeWindow(this.resize, this);
    ReactToPhaserListeners.PauseExercise(() => this.pauseExercise(), this);
    ReactToPhaserListeners.AddPhraseOSMD((data) => this.addPhrase(data), this, false, true);
    ReactToPhaserListeners.AddAllOSMDPhrases(
      (data) => {
        this.addAllPhrases(data).catch((error) => {
          Logger.error('[OSMDPlayableScene] addAllPhrases failed:', error);
          reportError(error, 'osmd.addAllPhrases');
        });
      },
      this,
      false,
      true,
    );
    console.log(`[LOOP-DBG] Scene init() — registering MidiOn listener`);
    ReactToPhaserListeners.MidiOn((data) => this.noteOn(data), this);
    ReactToPhaserListeners.MidiOff((data) => this.noteOff(data), this);
    console.log(`[EXERCISE-DBG] [OSMDScene.init] registering StartExercise listener`);
    ReactToPhaserListeners.StartExercise((data) => this.startExercise(data), this, false, true);
    // Reset and cursor toggles are direct user actions. A singleton scene
    // restart briefly removes all listeners, so collect queued actions when
    // the replacement scene initializes instead of silently losing the tap.
    ReactToPhaserListeners.SoftRefreshRepertoire(() => this.softReset(), this, false, true);
    ReactToPhaserListeners.ChangeTempo((data) => this.changeTempo(data), this);
    ReactToPhaserListeners.SetCursorVisible(
      (data) => this.setCursorVisible(data),
      this,
      false,
      true,
    );
    ReactToPhaserListeners.SetZoomLevel((data) => this.setZoomLevel(data), this);
    ReactToPhaserListeners.UnloadExercise(() => this.unloadPiece(), this);

    ReactToPhaserListeners.SetLoopingActive((data) => this.setLoopingActive(data.active), this);
    ReactToPhaserListeners.RestartLoopFromBeginning(() => this.restartLoopFromBeginning(), this);
    // collectWaiting=true: the container emits SetPerfectPhraseConfig from a
    // Redux-driven useEffect that can fire before this listener registers; replay
    // the buffered emit so the scene picks up the persisted config on load.
    ReactToPhaserListeners.SetPerfectPhraseConfig(
      (data) => this.setPerfectPhraseConfig(data),
      this,
      false,
      true,
    );

    // MUS-243: User-intent events from React buttons. Each dispatches the
    // corresponding XState event to the playback machine. The machine drives
    // all subsystem mutations via its action set.
    ReactToPhaserListeners.UserPlay(() => this.dispatchPlaybackEvent({ type: 'USER_PLAY' }), this);
    ReactToPhaserListeners.UserPause(() => {
      console.log(
        `[LOOP-DBG-243] Scene received UserPause event → dispatching USER_PAUSE. phase=${this.getPlaybackPhase()}`,
      );
      this.dispatchPlaybackEvent({ type: 'USER_PAUSE' });
    }, this);
    ReactToPhaserListeners.UserReset(
      () => this.dispatchPlaybackEvent({ type: 'USER_RESET' }),
      this,
    );
    ReactToPhaserListeners.UserToggleLoop(
      (data) => this.dispatchPlaybackEvent({ type: 'USER_TOGGLE_LOOP', active: data.active }),
      this,
    );
    ReactToPhaserListeners.AppBackgrounded(() => {
      console.log(
        `[LOOP-DBG-243] Scene received AppBackgrounded event → dispatching APP_BACKGROUNDED. phase=${this.getPlaybackPhase()}`,
      );
      this.dispatchPlaybackEvent({ type: 'APP_BACKGROUNDED' });
    }, this);
    ReactToPhaserListeners.AppForegrounded(
      () => this.dispatchPlaybackEvent({ type: 'APP_FOREGROUNDED' }),
      this,
    );
    // collectWaiting=true: the Container emits InjectScheduler2 from a
    // [scheduler2] useEffect that fires BEFORE the Phaser scene's init()
    // registers this listener. Without replay of the buffered emit, the
    // event is lost, the machine actor is never created, and every
    // dispatchPlaybackEvent() is dropped ("called before machine started").
    ReactToPhaserListeners.InjectScheduler2(
      (data) => this.setScheduler2(data.scheduler2 as Scheduler),
      this,
      false,
      true,
    );

    Logger.debug(
      '[OSMD-MIDI] Scene init() — event listeners registered (MidiOn, MidiOff, StartExercise, ChangeTempo, etc.)',
    );

    // Reuse or create metronome click audio.  this.sound is the game-level
    // SoundManager (singleton), so sounds survive scene shutdown — only
    // create a new instance on the very first init().
    //
    // Guard against the 'click' key being missing from the audio cache: if
    // the scene boots before Preloader has populated the cache (or the
    // asset failed to load), `this.sound.add('click')` throws and crashes
    // the entire app. metronomeClick() already no-ops when clickAudio is
    // undefined, so falling through here just yields a silent count-in.
    if (!isCapacitorNative() && !this.clickAudio) {
      if (this.cache.audio.exists('click')) {
        this.clickAudio = this.sound.add('click');
      } else {
        const message = '[OSMDPlayableScene] "click" audio not in cache; count-in will be silent';
        Logger.warn(message);
        awsRum?.recordError(new Error(message));
      }
    }
    this.logMemoryDiagnostics('Scene init() complete');
  }

  create(): void {
    NotifyReactEvents.CurrentSceneReady(this);
    this.events.once('shutdown', this.destroy, this);
    this.setupInputScrolling();

    if (this.mobileView && DEBUG_CAMERA_TEXT) {
      this.debugText = this.add
        .text(10, 10, '', { fontSize: '24px', color: '#ff0000', backgroundColor: '#00000088' })
        .setScrollFactor(0)
        .setDepth(9999);
    }

    this.bracketOverlay = new BracketOverlay(this, this.mobileView);

    // Perfect-phrase counter + streak badge. The controller owns the PPC state
    // machine; the badge renders the streak as a row of checkboxes at the
    // top-right of the loop bracket (one box per qualifying-streak unit). The
    // enabled flag + target are sourced from React/Redux via
    // SetPerfectPhraseConfig (applied in setPerfectPhraseConfig).
    this.perfectPhraseBadge = new PerfectPhraseBadge(this);
    this.perfectPhraseBadge.setTarget(this.perfectPhraseTarget);
    this.perfectPhrase = new PerfectPhraseController((streak) =>
      this.handlePerfectPhraseStreak(streak),
    );
  }

  // ---------------------------------------------------------------------------
  // User scroll input (mouse wheel + pointer drag)
  // ---------------------------------------------------------------------------

  /**
   * Enable user-driven vertical scrolling of the camera via:
   * 1. Mouse wheel events on the game canvas
   * 2. Pointer (touch/mouse) drag on the game canvas
   *
   * The camera.scrollY is clamped to the camera bounds set in layoutPhrases().
   */
  private setupInputScrolling(): void {
    const camera = this.cameras.main;

    // --- Mouse wheel scrolling via Phaser input system ---
    // Phaser's input.on('wheel') is more reliable than raw DOM 'wheel' events
    // because Phaser manages its own input pipeline and knows which scene is active.
    this.input.on(
      'wheel',
      (
        _pointer: Phaser.Input.Pointer,
        _gameObjects: Phaser.GameObjects.GameObject[],
        _deltaX: number,
        deltaY: number,
      ) => {
        const scrollSpeed = 1.5;
        const delta = deltaY * scrollSpeed;
        const cameraBounds = camera.getBounds();
        const maxScrollY = cameraBounds.height - camera.height;

        camera.scrollY = Phaser.Math.Clamp(camera.scrollY + delta, 0, Math.max(0, maxScrollY));
        this.prefetchForCurrentViewport();
      },
    );

    // Also attach a DOM wheel listener as a fallback, and to preventDefault()
    // so the parent container doesn't scroll the page.
    const canvas = this.game.canvas;
    this.boundWheelHandler = (event: WheelEvent) => {
      event.preventDefault();
    };
    canvas.addEventListener('wheel', this.boundWheelHandler, { passive: false });

    // --- Pointer drag scrolling (touch / mouse drag) ---
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDraggingScroll = true;
      this.dragStartPointerX = pointer.x;
      this.dragStartPointerY = pointer.y;
      this.dragStartScrollY = camera.scrollY;
      this.dragStartScrollX = camera.scrollX;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isDraggingScroll) return;

      // Do not scroll the camera when the bracket overlay is being dragged
      if (this.bracketOverlay?.isDragging()) return;

      // Mobile: horizontal drag scroll, allowed whenever playback is not
      // actively running. Scroll is suppressed only while the cursor is
      // advancing (playing) or the score is in a count-in / loop-restart
      // "flashing" stage; when paused or stopped — including after the piece
      // has finished — the user can scroll the score freely. We read the
      // machine phase rather than `playbackStarted`, which is a stale legacy
      // flag: the non-loop pause and piece-completed paths never reset it,
      // so it stayed true and blocked all scrolling once playback started.
      // The scroll position persists after release (mobileUserScrolledX
      // suppresses cursor-driven camera updates) until the next playback
      // start clears the flag and the camera returns to following the cursor.
      if (this.mobileView) {
        if (this.isPlaybackActivelyRunning()) return;

        const dragDeltaX = this.dragStartPointerX - pointer.x;
        const { min, max } = this.getMobileScrollXBounds();

        camera.scrollX = Phaser.Math.Clamp(this.dragStartScrollX + dragDeltaX, min, max);
        this.mobileUserScrolledX = true;
        this.prefetchForCurrentViewport();
        return;
      }

      const dragDelta = this.dragStartPointerY - pointer.y;
      const cameraBounds = camera.getBounds();
      const maxScrollY = cameraBounds.height - camera.height;

      camera.scrollY = Phaser.Math.Clamp(
        this.dragStartScrollY + dragDelta,
        0,
        Math.max(0, maxScrollY),
      );
      this.prefetchForCurrentViewport();
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const wasDragging = this.isDraggingScroll;
      this.isDraggingScroll = false;
      this.prefetchForCurrentViewport();

      // Do not treat bracket overlay drags as score taps
      if (this.bracketOverlay?.isDragging()) return;

      // Detect taps vs drags: if the pointer barely moved, treat as a tap and
      // forward it to handleScoreTap. Tap-to-place-cursor itself remains
      // disabled (see CURSOR_PLACEMENT_ENABLED) — this still forwards taps so
      // touch single-tap-to-pause and double-tap-to-play keep working. Drags
      // (scrolling) exceed the threshold and are ignored.
      if (wasDragging) {
        const dragDistanceX = pointer.x - this.dragStartPointerX;
        const dragDistanceY = pointer.y - this.dragStartPointerY;
        // Measured in CSS pixels: the canvas is drawn at the device pixel ratio.
        const isTap = isScoreTap(dragDistanceX, dragDistanceY, window.devicePixelRatio || 1);
        if (isTap) {
          this.handleScoreTap(pointer);
        }
      }
    });

    this.input.on('pointerupoutside', () => {
      this.isDraggingScroll = false;
    });
  }

  /**
   * Remove scroll input listeners. Called from destroy().
   */
  private teardownInputScrolling(): void {
    if (this.boundWheelHandler) {
      this.game.canvas.removeEventListener('wheel', this.boundWheelHandler);
      this.boundWheelHandler = undefined;
    }
    this.isDraggingScroll = false;
    this.cancelPendingTap();
  }

  // ---------------------------------------------------------------------------
  // Cursor placement via click/tap (looping feature)
  // ---------------------------------------------------------------------------

  /** Pending first-tap timer while paused, used to disambiguate a single
   *  cursor-placement tap from the first tap of a double-tap-to-play. */
  private pendingTapTimer?: Phaser.Time.TimerEvent;
  /** Max gap between two taps to count as a double-tap. */
  private static readonly DOUBLE_TAP_MS = 300;

  /** Tap-to-place-cursor is temporarily disabled (see #739). Touch
   *  single-tap-to-pause and double-tap-to-play stay active regardless; only
   *  the cursor-placement outcome of a tap is gated on this flag. */
  private static readonly CURSOR_PLACEMENT_ENABLED = false;

  /**
   * Handle a tap/click on the score.
   *
   * Desktop (non-touch): the play/pause button is the sole playback control, so
   * a score click never toggles playback — it only places the cursor (when not
   * actively playing). No grace window is needed since there is no
   * double-tap-to-play gesture to disambiguate against, so the click places the
   * cursor immediately.
   *
   * Touch: while playing, a single tap pauses immediately. While paused/stopped,
   * the outcome is decided one grace window after the first tap:
   *  - a second tap within the window → start playback (double-tap-to-play);
   *  - no second tap → treat the first tap as a cursor-placement tap.
   * We gate the touch path on touch capability rather than the `mobileView`
   * small-screen flag: an iPad in landscape is wide enough that `mobileView` is
   * false, yet it's still a touch device.
   *
   * Drags never reach here: the pointerup handler only forwards stationary taps
   * (pointer moved < tapThreshold), so scrolling triggers neither playback nor
   * cursor placement.
   */
  private handleScoreTap(pointer: Phaser.Input.Pointer): void {
    const phase = this.getPlaybackPhase();

    // Desktop: click places the cursor (when not actively playing); never
    // toggles playback. Cursor placement is currently disabled (#739), so a
    // desktop click is a no-op.
    if (!isTouchDevice()) {
      if (
        OSMDPlayableScene.CURSOR_PLACEMENT_ENABLED &&
        (phase === 'stopped' || phase === 'paused')
      ) {
        this.placeCursorAtPointer(pointer);
      }
      return;
    }

    // Touch — single tap pauses immediately.
    if (phase === 'playing' || phase === 'normalCountIn' || phase === 'loopCountInWait') {
      this.cancelPendingTap(); // drop any pending first-tap
      this.dispatchPlaybackEvent({ type: 'USER_PAUSE' });
      return;
    }

    // Only paused/stopped get tap-to-place-cursor / double-tap-to-play.
    if (phase !== 'stopped' && phase !== 'paused') return;

    // Second tap of a pair arrived within the grace window.
    if (this.pendingTapTimer) {
      this.cancelPendingTap();
      // If the piece has finished, a double-tap restarts from the top rather
      // than resuming. Dispatching USER_PLAY here would take the machine's
      // `paused → playing` resume branch (hasEverPlayedInThisLoop) and unpause
      // at lastPausedAtTimestamp — the "weird resume at the last note". Route
      // the restart through React's soft refresh instead so `repertoireEnded`
      // is cleared and the scene fully resets before auto-restarting playback.
      if (this.pieceCompleted) {
        NotifyReactEvents.RepertoireRestartRequested(undefined);
      } else {
        this.dispatchPlaybackEvent({ type: 'USER_PLAY' });
      }
      return;
    }

    // First tap: capture the world position now (the pointer object is reused
    // by later input events) and wait one grace window to see if a second tap
    // arrives (double-tap-to-play). If none does, place the cursor at this
    // position — unless cursor placement is disabled (#739), in which case the
    // timer only clears the pending state so the next tap starts a fresh pair.
    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const worldX = world.x;
    const worldY = world.y;
    this.pendingTapTimer = this.time.delayedCall(OSMDPlayableScene.DOUBLE_TAP_MS, () => {
      this.pendingTapTimer = undefined;
      if (OSMDPlayableScene.CURSOR_PLACEMENT_ENABLED) {
        this.placeCursorAtWorldPosition(worldX, worldY);
      }
    });
  }

  /** Cancel any pending single-tap (cursor-placement) timer. */
  private cancelPendingTap(): void {
    if (this.pendingTapTimer) {
      this.pendingTapTimer.remove(false);
      this.pendingTapTimer = undefined;
    }
  }

  /** Place the loop cursor at the note nearest to a pointer's screen position. */
  private placeCursorAtPointer(pointer: Phaser.Input.Pointer): void {
    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    this.placeCursorAtWorldPosition(world.x, world.y);
  }

  /** Place the loop cursor at the note nearest to a world-space position. */
  private placeCursorAtWorldPosition(worldX: number, worldY: number): void {
    const nearest = this.findNearestNoteToWorldPosition(worldX, worldY);
    if (nearest) {
      this.snapCursorToNote(nearest.phraseIndex, nearest.noteData);
    }
  }

  /**
   * Find the nearest non-rest note to a world-space position across all phrases.
   * If no non-rest notes exist, returns null.
   * Handles edge cases:
   *  - Click on area with only rests: snaps to nearest actual note
   *  - Click before first note: snaps to first note
   *  - Click after last note: snaps to last note
   */
  private findNearestNoteToWorldPosition(
    worldX: number,
    worldY: number,
  ): { phraseIndex: number; noteData: OSMDNoteData } | null {
    let closestDistance = Infinity;
    let closestResult: { phraseIndex: number; noteData: OSMDNoteData } | null = null;

    this.phrases.forEach((phrase, phraseIndex) => {
      const container = phrase.getContainer();
      const scale = container.scaleX;
      const phraseX = container.x;
      const phraseY = container.y;

      // Only consider non-rest notes for cursor placement
      const nonRestNotes = phrase.notePositions.filter((noteDataItem) => !noteDataItem.isRest);

      nonRestNotes.forEach((noteData) => {
        // Convert the note's local position to world coordinates
        const noteWorldX = phraseX + noteData.x * scale;
        const noteWorldY = phraseY + noteData.y * scale;

        const distanceX = worldX - noteWorldX;
        const distanceY = worldY - noteWorldY;
        const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestResult = { phraseIndex, noteData };
        }
      });
    });

    return closestResult;
  }

  /**
   * Snap the cursor to a specific note in a specific phrase.
   * Updates the visual cursor position and stores the timestamp so
   * subsequent playback starts from this position.
   */
  private snapCursorToNote(phraseIndex: number, noteData: OSMDNoteData): void {
    if (phraseIndex < 0 || phraseIndex >= this.phrases.length) {
      return;
    }

    const targetPhrase = this.phrases[phraseIndex];
    if (!targetPhrase?.cursor?.Iterator) {
      return;
    }

    // Store the cursor start timestamp for playback
    this.cursorStartTimestamp = noteData.timestamp;

    // Switch active phrase if needed
    if (phraseIndex !== this.activePhrase) {
      // Reset the current phrase's cursor to its start position (only if iterator is ready)
      const currentPhrase = this.phrases[this.activePhrase];
      if (currentPhrase?.cursor?.Iterator) {
        const currentFirstNote = currentPhrase.getFirstNotePosition();
        currentPhrase.cursor.setStart(currentFirstNote, currentPhrase.data.startTimestamp, 0);
        currentPhrase.cursor.setX(currentFirstNote - (currentPhrase.cursor.width ?? 0) * 2);
      }
      this.activatePhrase(phraseIndex);
    }

    // Calculate the cursor X position in world coordinates
    const container = targetPhrase.getContainer();
    const scale = container.scaleX;
    const cursorWorldX = container.x + noteData.x * scale;

    // Position the cursor
    targetPhrase.cursor.setX(cursorWorldX - targetPhrase.cursor.width / 2);

    const localTimestamp = noteData.timestamp - targetPhrase.data.startTimestamp;
    const localMeasureIndex = Math.max(noteData.measureNumber - 1, 0);
    const iter = targetPhrase.cursor.Iterator as unknown as {
      starts: number[];
      cursorData: unknown[];
      indx: number;
      endReached: boolean;
      currentMeasureIndex: number;
    };
    console.log(
      `[CURSOR-DBG] snapCursorToNote — phraseIndex=${phraseIndex} noteTimestamp=${noteData.timestamp} localTimestamp=${localTimestamp} localMeasureIndex=${localMeasureIndex} measureNumber=${noteData.measureNumber}`,
    );
    console.log(
      `[CURSOR-DBG] snapCursorToNote — iterator state: starts=${JSON.stringify(iter.starts)} startsLength=${iter.starts?.length} cursorDataLength=${iter.cursorData?.length} indx=${iter.indx} endReached=${iter.endReached} currentMeasureIndex=${iter.currentMeasureIndex}`,
    );
    console.log(
      `[CURSOR-DBG] snapCursorToNote — starts[${localMeasureIndex}]=${iter.starts?.[localMeasureIndex]} cursorWorldX=${cursorWorldX}`,
    );

    // Ensure the iterator's starts array is populated up to the target measure.
    // On fresh load, starts only has [0]. If the bracket is moved to a later
    // measure, rollbackToMeasure would fail without this.
    const iterExt = targetPhrase.cursor.Iterator as unknown as {
      ensureStartsPopulated?: (m: number) => void;
    };
    if (iterExt.ensureStartsPopulated) {
      iterExt.ensureStartsPopulated(localMeasureIndex);
    }

    try {
      targetPhrase.cursor.setStart(cursorWorldX, localTimestamp, localMeasureIndex);
      console.log(
        `[CURSOR-DBG] snapCursorToNote — setStart SUCCESS cursorWorldX=${cursorWorldX} localTimestamp=${localTimestamp} localMeasureIndex=${localMeasureIndex}`,
      );
    } catch (err) {
      console.error(`[CURSOR-DBG] snapCursorToNote — setStart FAILED:`, err);
    }
    targetPhrase.cursor.enable();

    // Scroll to show the target phrase if it's not visible.
    // During looping, scrollToShowBracketRange handles positioning.
    if (!this.isLoopingActive) {
      this.scrollToPhraseSmooth(phraseIndex);
    }

    Logger.info('[OSMDRepertoire] Cursor placed via tap/click', {
      phraseIndex,
      timestamp: noteData.timestamp,
      localTimestamp,
      noteX: noteData.x,
      cursorWorldX,
      measureNumber: noteData.measureNumber,
    });
  }

  // ---------------------------------------------------------------------------
  // Bracket overlay (looping)
  // ---------------------------------------------------------------------------

  /**
   * Build phrase layout info from the current phrases array.
   * This maps each phrase's rendered position and note data into the
   * PhraseLayoutInfo format consumed by BracketOverlay.
   */
  getPhrasesLayoutInfo(): PhraseLayoutInfo[] {
    return this.phrases.map((phrase, phraseIndex) => {
      const container = phrase.getContainer();
      const scale = container.scaleX;

      const notePositions = phrase.notePositions
        .filter((noteData) => !noteData.isRest)
        .map((noteData) => ({
          timestamp: noteData.timestamp,
          x: container.x + noteData.x * scale,
          width: noteData.width * scale,
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      const containerLeftEdge = container.x;
      // Use the actual staff line right edge (where the horizontal lines end)
      // rather than getScaledWidth() which may include extra padding.
      const containerRightEdge = phrase.getStaffLineRightEdgeX();

      return {
        phraseIndex,
        y: container.y,
        height: phrase.getScaledHeight(),
        notePositions,
        measureBarlineXPositions: phrase.getMeasureBarlineXPositions(),
        containerLeftEdge,
        containerRightEdge,
        phraseStartTimestamp: phrase.data.startTimestamp,
      };
    });
  }

  /**
   * Build sorted arrays of all note onset and offset timestamps across
   * all phrases. Used by BracketOverlay for snapping and constraints.
   */
  private getAllNoteOnsetAndOffsetData(): BracketNoteData {
    const onsets: number[] = [];
    const offsets: number[] = [];
    const maxOffsetByOnset = new Map<number, number>();

    this.phrases.forEach((phrase) => {
      phrase.notePositions
        .filter((noteData) => !noteData.isRest)
        .forEach((noteData) => {
          const onset = noteData.timestamp;
          const offset = onset + noteData.duration;
          onsets.push(onset);
          offsets.push(offset);
          // Track the SHORTEST note at each onset. When a chord has mixed
          // durations (e.g. quarter melody + held bass), the user is visually
          // targeting the shorter melody note. Using the max would jump the
          // bracket end to the held bass note's offset, far past what the
          // user expects. The loop restart at the shorter offset means a held
          // bass note may be "cut off", but that's acceptable — the user
          // explicitly chose to end the bracket at this beat.
          const existing = maxOffsetByOnset.get(onset);
          if (existing === undefined || offset < existing) {
            maxOffsetByOnset.set(onset, offset);
          }
        });
    });

    onsets.sort((a, b) => a - b);
    offsets.sort((a, b) => a - b);

    // Deduplicate (chords produce multiple entries at the same timestamp)
    const uniqueOnsets = [...new Set(onsets)];
    const uniqueOffsets = [...new Set(offsets)];

    const phraseStartTimestamps = this.phrases
      .map((p) => p.data.startTimestamp)
      .sort((a, b) => a - b);

    return {
      noteOnsets: uniqueOnsets,
      noteOffsets: uniqueOffsets,
      maxOffsetByOnset,
      phraseStartTimestamps,
    };
  }

  /**
   * Handle bracket position changes from user interaction (drag/resize).
   * Updates the bracket overlay, persists the position, and re-renders
   * the dimming overlay. This is called by BracketOverlay after debouncing.
   */
  private handleBracketChanged(startTimestamp: number, endTimestamp: number): void {
    if (!this.bracketOverlay) return;

    // Perfect-phrase: changing the looped area invalidates any streak.
    this.resetPerfectPhrase('bracket-changed');

    // Reset drag-visual tracking so the next drag starts fresh.
    this.dragVisualPhraseIndex = -1;

    const layoutInfo = this.getPhrasesLayoutInfo();
    this.bracketOverlay.updatePosition(startTimestamp, endTimestamp, layoutInfo);

    const wasActivelyPlaying = this.playbackStarted;
    const wasInCountIn = this.countInActive && !this.playbackStarted;

    console.log(
      `[NOTE-REGISTER-DBG] handleBracketChanged ENTRY start=${startTimestamp} end=${endTimestamp} ` +
        `playbackStarted=${this.playbackStarted} countInActive=${this.countInActive} ` +
        `isLoopCountInActive=${this.isLoopCountInActive} shouldUpdate=${this.shouldUpdate} ` +
        `mainTKPaused=${this.eventStream?.timeKeeper?.isPaused()} ` +
        `errorRecLeft=${this.eventStream?.errorRecognitionActive?.left} ` +
        `errorRecRight=${this.eventStream?.errorRecognitionActive?.right} ` +
        `errorRecSuppressed=${this.eventStream?.errorRecognitionSuppressed} ` +
        `userErrorRec=${JSON.stringify(this.eventStream?.userErrorRecognitionActive)} ` +
        `wasActivelyPlaying=${wasActivelyPlaying} wasInCountIn=${wasInCountIn} ` +
        `activePhrase=${this.activePhrase}`,
    );

    if (wasActivelyPlaying) {
      // Scene-mechanic teardown (cursor visual + flags). MUS-243: main-TK
      // pause and error-recognition save/disable are owned by the machine —
      // the USER_BRACKET_CHANGE dispatched below transitions playing →
      // loopCountInWait, whose entry fires pauseMainTK + disableError-
      // Recognition. Re-doing them here double-fired against the machine.
      this.phrases[this.activePhrase]?.cursor?.pause();
      this.phrases[this.activePhrase]?.deactivate();

      this.playbackStarted = false;
      this.isLoopCountInActive = true;
      this.countInActive = true;
      this.lastTickTime = undefined;
    }

    // Reset so the next first-note triggers the count-in unpause path
    this.hasEverPlayed = false;

    // Position cursor at the new bracket start
    this.cursorStartTimestamp = startTimestamp;
    this.positionCursorAtBracketStart(startTimestamp);

    // Set loop bounds FIRST — before resetting note states/visuals. The
    // container's updateOsmdEventStream tick runs independently and could
    // re-mark notes as NEVER_PLAYED using OLD loop bounds if we reset
    // visuals before updating bounds.
    const effectiveLoopEnd = this.computeEffectiveLoopEnd(endTimestamp);
    this.effectiveLoopEndTimestamp = effectiveLoopEnd;
    console.log(
      `[LOOP-DBG] handleBracketChanged — updating loop bounds start=${startTimestamp} bracketEnd=${endTimestamp} effectiveLoopEnd=${effectiveLoopEnd}`,
    );
    this.eventStream.setLoopBounds(startTimestamp, effectiveLoopEnd);
    // MUS-243: dispatch USER_BRACKET_CHANGE so the machine updates context
    // and applies the new bounds to Scheduler2 via its setScheduler2LoopBounds
    // action. The legacy SetLoopBounds emission is no longer needed for
    // scheduler-side work — the container's listener for it is now a no-op.
    this.dispatchPlaybackEvent({
      type: 'USER_BRACKET_CHANGE',
      bracketStartTimestamp: startTimestamp,
      bracketEndTimestamp: endTimestamp,
    });
    NotifyReactEvents.SetLoopBounds({ startTimestamp, endTimestamp: effectiveLoopEnd });

    // MUS-243: event-stream note-state reset is owned by the machine. The
    // USER_BRACKET_CHANGE dispatched above runs resetLoopVisuals (after
    // disableErrorRecognition, synchronously) on the playing→loopCountInWait
    // entry, or directly in the stopped/paused USER_BRACKET_CHANGE handlers.
    // Re-running it here double-fired and risked the red-note race.

    // MUS-243: aux-TK arming and cursor enable/blink are owned by the
    // machine. The USER_BRACKET_CHANGE dispatched above transitions
    // playing/loopCountInWait → loopCountInWait (re-enter), whose entry
    // fires armAuxTK + enableCursorBlink. (When dragged while stopped the
    // machine stays stopped and no count-in starts — matching the legacy
    // wasActivelyPlaying||wasInCountIn gate.) Only the scene-visual
    // "hide non-active cursors" mechanic remains.
    if (wasActivelyPlaying || wasInCountIn) {
      this.lastTickTime = this.timeKeeperAuxiliary?.audioTimeToMeasureTimestamp() ?? 0;
    }

    for (let i = 0; i < this.phrases.length; i++) {
      if (i !== this.activePhrase) {
        this.phrases[i]?.cursor?.disable();
      }
    }

    // Persist to session storage
    const songId = this.eventStream?.id ?? '';
    if (songId.length > 0) {
      setLastLoopPosition(songId, startTimestamp, endTimestamp);
    }

    console.log(
      `[NOTE-REGISTER-DBG] handleBracketChanged EXIT playbackStarted=${this.playbackStarted} ` +
        `countInActive=${this.countInActive} isLoopCountInActive=${this.isLoopCountInActive} ` +
        `shouldUpdate=${this.shouldUpdate} mainTKPaused=${this.eventStream?.timeKeeper?.isPaused()} ` +
        `errorRecLeft=${this.eventStream?.errorRecognitionActive?.left} ` +
        `errorRecRight=${this.eventStream?.errorRecognitionActive?.right} ` +
        `errorRecSuppressed=${this.eventStream?.errorRecognitionSuppressed} ` +
        `userErrorRec=${JSON.stringify(this.eventStream?.userErrorRecognitionActive)} ` +
        `cursorStartTimestamp=${this.cursorStartTimestamp} ` +
        `effectiveLoopEnd=${this.effectiveLoopEndTimestamp} hasEverPlayed=${this.hasEverPlayed} ` +
        `activePhrase=${this.activePhrase}`,
    );

    // Always scroll to the bracket range when looping — after pause the
    // camera may be scrolled to a different line.
    if (this.hasEverPlayed || this.playbackStarted || this.isLoopCountInActive) {
      this.scrollToShowBracketRange();
    }

    Logger.info('[OSMDRepertoire] Bracket position changed', {
      startTimestamp,
      endTimestamp,
    });

    // Ungraded: a bracket moved while playing restarts the loop with a count-in.
    this.leaveCountInWaitWhenUngraded(false);
  }

  /**
   * Handle looping active state changes from React.
   * When activated, shows the bracket overlay with placeholder timestamps.
   * When deactivated, hides the bracket overlay.
   */
  private setLoopingActive(active: boolean): void {
    console.log(
      '[LOOPING DEBUG] setLoopingActive called, active:',
      active,
      'bracketOverlay:',
      !!this.bracketOverlay,
    );
    if (!this.bracketOverlay) return;

    // MUS-243: dispatch the looping toggle into the playback machine. On
    // enable, USER_BRACKET_CHANGE is dispatched below once bounds are
    // computed. On disable, USER_TOGGLE_LOOP off transitions the machine
    // back to `stopped` and the machine's `clearBracket` action wipes
    // bracket context.
    this.dispatchPlaybackEvent({ type: 'USER_TOGGLE_LOOP', active });

    // Perfect-phrase: toggling looping (on or off) clears any streak and the
    // pause timer — a fresh looped segment starts counting from scratch.
    this.resetPerfectPhrase(`looping-toggled active=${active}`);
    this.perfectPhrasePausedAt = null;

    if (active) {
      this.isLoopingActive = true;

      const layoutInfo = this.getPhrasesLayoutInfo();
      if (layoutInfo.length === 0) return;

      // Provide note data for snapping and constraints
      const noteData = this.getAllNoteOnsetAndOffsetData();
      this.bracketOverlay.setNoteData(noteData);

      // Register bracket changed callback
      this.bracketOverlay.setOnBracketChanged((start: number, end: number) =>
        this.handleBracketChanged(start, end),
      );

      // Real-time cursor repositioning during drag — lightweight visual-only
      // update. Avoids the full positionCursorAtBracketStart path which
      // activates phrases and resets iterator state on every frame.
      this.bracketOverlay.setOnBracketDragging((startTimestamp: number) =>
        this.moveCursorToTimestampVisualOnly(startTimestamp),
      );

      // DEBUG: always log bracket placement diagnostics
      const firstPhraseTimeSignature = this.phrases[0]?.data?.timeSignature;
      const timeSignatureNumerator = firstPhraseTimeSignature?.numerator ?? 4;
      const timeSignatureDenominator = firstPhraseTimeSignature?.denominator ?? 4;
      const FRACTIONAL_TICKS_PER_QUARTER_NOTE = 0.25;

      console.log(
        '[LOOPING DEBUG] time sig:',
        timeSignatureNumerator,
        '/',
        timeSignatureDenominator,
      );
      console.log('[LOOPING DEBUG] first 20 onsets:', noteData.noteOnsets.slice(0, 20));
      console.log('[LOOPING DEBUG] first 20 offsets:', noteData.noteOffsets.slice(0, 20));
      this.phrases.forEach((phrase, idx) => {
        console.log(
          `[LOOPING DEBUG] phrase[${idx}] startTimestamp:`,
          phrase.data.startTimestamp,
          'noteCount:',
          phrase.notePositions.filter((n) => !n.isRest).length,
        );
      });

      // Log phrase boundaries so we can see where each line starts/ends
      this.phrases.forEach((phrase, idx) => {
        const nonRestNotes = phrase.notePositions.filter((n) => !n.isRest);
        const firstOnset = nonRestNotes.length > 0 ? nonRestNotes[0].timestamp : null;
        const lastOnset =
          nonRestNotes.length > 0 ? nonRestNotes[nonRestNotes.length - 1].timestamp : null;
        const lastOffset =
          nonRestNotes.length > 0
            ? nonRestNotes[nonRestNotes.length - 1].timestamp +
              nonRestNotes[nonRestNotes.length - 1].duration
            : null;
        console.log(
          `[BRACKET-DEFAULT] phrase[${idx}] startTimestamp=${phrase.data.startTimestamp} firstOnset=${firstOnset} lastOnset=${lastOnset} lastOffset=${lastOffset} noteCount=${nonRestNotes.length}`,
        );
      });

      const existingStart = this.bracketOverlay.getStartTimestamp();
      const existingEnd = this.bracketOverlay.getEndTimestamp();
      const hasExistingRange = existingStart < existingEnd;
      console.log(
        '[BRACKET-DEFAULT] existingStart:',
        existingStart,
        'existingEnd:',
        existingEnd,
        'hasExistingRange:',
        hasExistingRange,
        'cursorStartTimestamp:',
        this.cursorStartTimestamp,
      );

      if (this.mobileView) {
        // Mobile bracket placement differs from desktop/iPad:
        //  - Desktop/iPad: restores previous bracket from session storage, or
        //    calculates a default 4-bar range starting at the cursor position.
        //  - Mobile: always brackets the notes currently visible on screen.
        //    No session storage restore — the visible area is narrow and the
        //    user expects the bracket to match what they see, not a stored
        //    position that may be off-screen.
        //
        // getVisibleNoteTimestampRange finds the camera center, locates the
        // nearest note, and expands to include all notes within the visible
        // world width. It guarantees at least 2 notes if the piece has 2+.
        const visibleRange = this.getVisibleNoteTimestampRange(noteData);
        if (visibleRange && visibleRange.startTimestamp < visibleRange.endTimestamp) {
          console.log(
            `[LOOPING DEBUG] TAKING PATH: mobile visible range start=${visibleRange.startTimestamp} end=${visibleRange.endTimestamp}`,
          );
          this.bracketOverlay.show(
            visibleRange.startTimestamp,
            visibleRange.endTimestamp,
            layoutInfo,
          );
          const actualEndX = this.bracketOverlay.getRenderedEndX();
          const cam = this.cameras.main;
          const rawViewR = cam.getWorldPoint(cam.width, 0).x;
          console.log(
            `[VISIBLE-RANGE] ACTUAL renderedEndX=${actualEndX?.toFixed(0) ?? 'null'} rawViewR=${rawViewR.toFixed(0)} overflow=${actualEndX !== null ? (actualEndX - rawViewR).toFixed(0) : 'n/a'}`,
          );
        } else {
          // Fallback: fewer than 2 notes in the piece.
          console.log('[LOOPING DEBUG] TAKING PATH: mobile fallback (not enough visible notes)');
          const firstOnset = noteData.noteOnsets[0];
          const fallbackEnd =
            noteData.noteOnsets.length >= 2
              ? (noteData.maxOffsetByOnset.get(noteData.noteOnsets[1]) ?? noteData.noteOnsets[1])
              : firstOnset;
          if (firstOnset >= fallbackEnd) return;
          this.bracketOverlay.show(firstOnset, fallbackEnd, layoutInfo);
        }
        // Lock the camera at its current scroll position. Without this, the
        // cursor-follow lerp in update() (updateMobileCameraScroll, factor
        // 0.15) gradually re-centers the camera on the cursor's new position
        // at the bracket start, which shifts the bracket off-screen over
        // several frames. startExercise() clears this flag when playback
        // begins so the camera resumes following the cursor.
        this.mobileUserScrolledX = true;
      } else if (hasExistingRange) {
        // Desktop/iPad: restore the previous bracket position from this session.
        console.log('[LOOPING DEBUG] TAKING PATH: existing range');
        this.bracketOverlay.show(existingStart, existingEnd, layoutInfo);
      } else {
        // Desktop/iPad: calculate default from session storage or first phrase.
        const fourBarsDuration =
          4 *
          timeSignatureNumerator *
          ((FRACTIONAL_TICKS_PER_QUARTER_NOTE * 4) / timeSignatureDenominator);
        const rawEnd = noteData.noteOnsets[0] + fourBarsDuration;
        console.log('[LOOPING DEBUG] TAKING PATH: calculating default');
        console.log('[LOOPING DEBUG] 4 bars duration:', fourBarsDuration, 'rawEnd:', rawEnd);

        const firstPhraseEndOffset = computeFirstLineEndTimestamp(
          this.phrases.map((p) => ({
            startTimestamp: p.data.startTimestamp,
            notes: p.notePositions,
          })),
        );

        const defaultPlacement = calculateDefaultBracketPlacement({
          songId: this.eventStream?.id ?? '',
          cursorStartTimestamp: this.cursorStartTimestamp,
          firstPhraseEndOffset,
          timeSignatureNumerator,
          timeSignatureDenominator,
          ticksPerQuarterNote: FRACTIONAL_TICKS_PER_QUARTER_NOTE,
          allNoteTimestamps: noteData.noteOnsets,
          allNoteOffsets: noteData.noteOffsets,
        });

        console.log(
          '[LOOPING DEBUG] result:',
          defaultPlacement.startTimestamp,
          '->',
          defaultPlacement.endTimestamp,
        );

        if (defaultPlacement.startTimestamp >= defaultPlacement.endTimestamp) return;

        this.bracketOverlay.show(
          defaultPlacement.startTimestamp,
          defaultPlacement.endTimestamp,
          layoutInfo,
        );
      }

      // Set cursorStartTimestamp so the next playback begins at the bracket
      // start, and visually snap the cursor to that position.
      const bracketStartTimestamp = this.bracketOverlay.getStartTimestamp();
      const bracketEndTimestamp = this.bracketOverlay.getEndTimestamp();
      this.cursorStartTimestamp = bracketStartTimestamp;

      // Compute effective end: bracket end + last note's length
      const effectiveLoopEnd = this.computeEffectiveLoopEnd(bracketEndTimestamp);
      this.effectiveLoopEndTimestamp = effectiveLoopEnd;

      const wasPlaying = this.playbackStarted || this.countInActive;

      // MUS-243: main-TK pause, aux-TK pause, error-recognition save/disable,
      // and the event-stream note-state reset are ALL owned by the playback
      // machine now. The USER_BRACKET_CHANGE dispatched below transitions the
      // machine (playing → loopCountInWait, or stays stopped/paused) and its
      // actions run disableErrorRecognition + resetLoopVisuals as consecutive
      // synchronous entry actions — which makes the legacy red-note race
      // (async updateOsmdEventStream tick between disable and reset)
      // structurally impossible. Only the scene-visual cursor teardown stays.
      if (wasPlaying) {
        this.phrases[this.activePhrase]?.cursor?.pause();
        this.phrases[this.activePhrase]?.deactivate();
      }

      // Reset playback state (scene flags that gate update()/noteOn).
      this.playbackStarted = false;
      this.shouldUpdate = false;
      this.countInActive = false;
      this.isLoopCountInActive = false;
      this.pieceCompleted = false;
      this.repertoireCompletedEmitted = false;
      this.lastTickTime = undefined;

      // Position cursor at bracket start (scene cursor mechanic — needed for
      // the enable-while-stopped case where the machine stays in `stopped`
      // and does not run positionCursorAtBracketStart).
      this.positionCursorAtBracketStart(bracketStartTimestamp);

      // Set loop bounds on the event stream so update() and handleNoteOnEvent()
      // only process buckets within the loop range.
      console.log(
        `[LOOP-DBG] setLoopingActive(true) — setting EventStream loop bounds start=${bracketStartTimestamp} bracketEnd=${bracketEndTimestamp} effectiveLoopEnd=${effectiveLoopEnd}`,
      );
      this.eventStream.setLoopBounds(bracketStartTimestamp, effectiveLoopEnd);

      // MUS-243: dispatch USER_BRACKET_CHANGE so the machine updates context
      // with the computed bounds and applies them to Scheduler2 via the
      // setScheduler2LoopBounds facade action.
      this.dispatchPlaybackEvent({
        type: 'USER_BRACKET_CHANGE',
        bracketStartTimestamp,
        bracketEndTimestamp,
      });

      // MUS-243: enabling looping while playing transitions the machine
      // playing → loopCountInWait (via the USER_BRACKET_CHANGE dispatched
      // above). The flag resets earlier in this method (shouldUpdate=false,
      // countInActive=false, isLoopCountInActive=false) were written for the
      // enable-while-STOPPED case, where the machine stays `stopped` and the
      // count-in only begins on a later play press (beginCountIn sets these).
      // For enable-while-PLAYING the machine is already in count-in wait, so
      // the scene must present the count-in immediately — otherwise update()
      // early-returns on !shouldUpdate and the cursor never blinks / the
      // metronome never clicks (cursor appears frozen/absent). Mirror exactly
      // what every other loopCountInWait entry path (restartLoop, beginCountIn)
      // sets so the scene state matches the machine phase.
      if (this.getPlaybackPhase() === 'loopCountInWait') {
        this.shouldUpdate = true;
        this.isLoopCountInActive = true;
        this.countInActive = true;
      }

      // Emit SetLoopBounds to React for the container's listener; the
      // listener no longer touches Scheduler2 directly (machine handles
      // that), but other consumers may still rely on the event.
      console.log(
        `[LOOP-DBG] setLoopingActive(true) — emitting SetLoopBounds to React start=${bracketStartTimestamp} effectiveEnd=${effectiveLoopEnd}`,
      );
      NotifyReactEvents.SetLoopBounds({
        startTimestamp: bracketStartTimestamp,
        endTimestamp: effectiveLoopEnd,
      });

      // Anchor the view to show the bracket range below the header,
      // but only if it doesn't already fit on screen.
      if (!this.isBracketRangeFullyVisible()) {
        this.scrollToShowBracketRange();
      }

      Logger.info('[OSMDRepertoire] Bracket overlay shown', {
        startTimestamp: bracketStartTimestamp,
        endTimestamp: bracketEndTimestamp,
      });

      // Ungraded: looping turned on while playing starts the loop with a count-in.
      this.leaveCountInWaitWhenUngraded(false);
    } else {
      console.log(
        `[LOOP-DBG] setLoopingActive(false) — STEP 0: entry. playbackStarted=${this.playbackStarted} shouldUpdate=${this.shouldUpdate} isLoopCountInActive=${this.isLoopCountInActive} countInActive=${this.countInActive} mainTKPaused=${this.eventStream?.timeKeeper?.isPaused()} activePhrase=${this.activePhrase} hasEverPlayed=${this.hasEverPlayed} errorRecActive=${JSON.stringify(this.eventStream?.errorRecognitionActive)} loopBounds=start:${(this.eventStream as unknown as { loopStartTimestamp: number | null }).loopStartTimestamp},end:${(this.eventStream as unknown as { loopEndTimestamp: number | null }).loopEndTimestamp}`,
      );

      // Fully pause the scene regardless of current state (playing, count-in, or idle).
      // This stops the cursor, event stream, and scheduler from processing.
      this.shouldUpdate = false;
      this.playbackStarted = false;
      this.countInActive = false;
      this.lastTickTime = undefined;

      // Pause the current cursor
      this.phrases[this.activePhrase]?.cursor?.pause();

      // Stop auxiliary TK
      if (this.timeKeeperAuxiliary) {
        this.timeKeeperAuxiliary.pause();
      }

      // Restore error recognition. It may have been suppressed during a loop
      // count-in; end that window so the user's actual hand-accuracy intent
      // applies for the next normal play session.
      // The Scheduler2's tick interval is killed by the container when
      // SetLoopBounds(null) is received, so there's no risk of notes
      // being marked between now and then.
      this.eventStream.unsuppressErrorRecognition();

      console.log(
        `[LOOP-DBG] setLoopingActive(false) — STEP 1: paused, error rec restored. shouldUpdate=${this.shouldUpdate} playbackStarted=${this.playbackStarted}`,
      );

      // Clear loop bounds BEFORE resetting visuals so the event stream
      // doesn't process stale bounds during cleanup.
      this.eventStream.clearLoopBounds();
      console.log(`[LOOP-DBG] setLoopingActive(false) — STEP 2: loop bounds cleared`);

      // Reset the event stream to the beginning so no notes are in a
      // partially-processed state. The last pass's notes are recorded first.
      NotifyReactEvents.LoopPassEnding();
      this.eventStream.seekToTimestamp(0);
      this.eventStream.resetNoteStatesInRange(0, Infinity);
      console.log(`[LOOP-DBG] setLoopingActive(false) — STEP 3: event stream reset`);

      this.isLoopingActive = false;
      this.isLoopCountInActive = false;
      this.hasEverPlayed = false;
      this.effectiveLoopEndTimestamp = 0;
      this.cursorStartTimestamp = null;
      this.bracketOverlay.setHandlesPlaybackAlpha(false);
      this.bracketOverlay.hide();

      console.log(`[LOOP-DBG] setLoopingActive(false) — STEP 4: flags reset, bracket hidden`);

      // Reset all note visuals (clear green/yellow/red coloring from looping)
      let resetCount = 0;
      for (const phrase of this.phrases) {
        for (const noteContainer of phrase.noteContainers.values()) {
          noteContainer.reset();
          resetCount++;
        }
      }
      console.log(
        `[LOOP-DBG] setLoopingActive(false) — STEP 5: reset ${resetCount} note containers`,
      );

      // Reset all phrase iterators so no cursor carries stale loop-session
      // state (advanced iterator index, lastPlayedMeasure, etc.) into the
      // next normal play session. Without this, pressing play after disabling
      // looping could start the cursor mid-phrase or cause rapid flashing
      // because the iterator thinks it's already partway through.
      for (const phrase of this.phrases) {
        if (phrase.cursor) {
          const iter = phrase.cursor.Iterator as unknown as {
            resetIterator?: () => void;
            lastPlayedMeasure?: number;
            shouldIncrementMeasureIndex?: boolean;
          };
          iter.resetIterator?.();
          iter.lastPlayedMeasure = 0;
          iter.shouldIncrementMeasureIndex = false;
        }
      }

      // Reset cursor to phrase 0 (beginning of song)
      if (this.phrases.length > 0) {
        this.phrases[this.activePhrase]?.deactivate();
        this.activePhrase = 0;
        this.phrases[0]?.activate();
        const firstNotePos = this.phrases[0]?.getFirstNotePosition() ?? 0;
        if (this.phrases[0]?.cursor) {
          this.phrases[0].cursor.setX(firstNotePos - (this.phrases[0].cursor.width ?? 0) / 2);
          try {
            this.phrases[0].cursor.setStart(firstNotePos, 0, 0);
          } catch {
            // Iterator might not be ready on first load
          }
          this.phrases[0].cursor.enable();
        }
        this.scrollToPhraseSmooth(0);
        this.updateMobileCameraScroll();
      }

      console.log(`[LOOP-DBG] setLoopingActive(false) — STEP 6: cursor reset to phrase 0`);

      // Notify React to clear Scheduler2 loop bounds and pause
      NotifyReactEvents.SetLoopBounds(null);
      NotifyReactEvents.ExercisePaused({
        lastPlayedMeasure: 0,
        pausedAtTimestamp: 0,
      });

      console.log(
        `[LOOP-DBG] setLoopingActive(false) — STEP 7: COMPLETE. shouldUpdate=${this.shouldUpdate} playbackStarted=${this.playbackStarted} isLoopingActive=${this.isLoopingActive}`,
      );
    }
  }

  /**
   * Position the cursor at the bracket start note when looping is activated.
   * Activates the correct phrase, sets up the cursor iterator, and scrolls
   * to make the bracket start visible.
   */
  private positionCursorAtBracketStart(bracketStartTimestamp: number): void {
    const targetPhraseIndex = this.findPhraseIndexForTimestamp(bracketStartTimestamp);
    if (targetPhraseIndex === -1) {
      return;
    }

    const targetPhrase = this.phrases[targetPhraseIndex];
    const targetNoteData = this.findNearestNoteInPhrase(targetPhrase, bracketStartTimestamp);
    if (!targetNoteData) {
      return;
    }

    // Store the timestamp so playback will start from here when it begins.
    this.cursorStartTimestamp = targetNoteData.timestamp;

    // Only reposition the cursor if it has been initialized (i.e., the
    // iterator is ready). Before the first playback the iterator is not
    // set up and calling setStart would crash.
    const activeCursor = this.phrases[this.activePhrase]?.cursor;
    if (!activeCursor?.Iterator) {
      return;
    }

    this.snapCursorToNote(targetPhraseIndex, targetNoteData);
  }

  /**
   * Lightweight visual-only cursor repositioning during bracket drag.
   * Only moves the cursor's X position to the nearest note — no phrase
   * activation, no iterator rollback, no state changes. The full
   * positionCursorAtBracketStart runs on finalize via handleBracketChanged.
   */
  /** Track which phrase's cursor is shown during drag, separate from activePhrase. */
  private dragVisualPhraseIndex: number = -1;

  private moveCursorToTimestampVisualOnly(startTimestamp: number): void {
    const targetPhraseIndex = this.findPhraseIndexForTimestamp(startTimestamp);
    if (targetPhraseIndex === -1) return;

    const targetPhrase = this.phrases[targetPhraseIndex];
    const targetNoteData = this.findNearestNoteInPhrase(targetPhrase, startTimestamp);
    if (!targetNoteData) {
      return;
    }

    // When the phrase changes during drag, hide the old cursor and show the
    // new one. Read alpha from the currently visible cursor so we preserve
    // the correct value (originalAlpha = 0.26), not 1.
    if (targetPhraseIndex !== this.dragVisualPhraseIndex) {
      const prevCursor =
        this.dragVisualPhraseIndex >= 0
          ? this.phrases[this.dragVisualPhraseIndex]?.cursor
          : this.phrases[this.activePhrase]?.cursor;
      const preservedAlpha = prevCursor?.alpha ?? 0.26;

      for (let i = 0; i < this.phrases.length; i++) {
        const cursor = this.phrases[i]?.cursor;
        if (cursor && i !== targetPhraseIndex) {
          cursor.setAlpha(0);
        }
      }
      targetPhrase.cursor?.setAlpha(preservedAlpha);
      this.dragVisualPhraseIndex = targetPhraseIndex;
    }

    // Always update activePhrase so positionCursorAtBracketStart (called on
    // finalize) can activate the correct phrase's cursor.
    this.activePhrase = targetPhraseIndex;

    const cursor = targetPhrase.cursor;
    if (!cursor) return;

    const container = targetPhrase.getContainer();
    const scale = container.scaleX;
    const cursorWorldX = container.x + targetNoteData.x * scale;
    cursor.setX(cursorWorldX - cursor.width / 2);
    this.updateMobileCameraScroll();
  }

  /**
   * Compute the effective loop end. The bracket end is already a note offset
   * (BracketOverlay.finalizeDrag snaps it to the next offset after the last
   * in-bracket onset), so the effective end equals the bracket end.
   *
   * The previous implementation extended the end by walking every in-bracket
   * note and taking the MAX (onset + length). That caused overshoot whenever
   * a held note (e.g. a left-hand whole note) started inside the bracket but
   * ended past the bracket end — the loop would wait for that note to finish
   * sounding before restarting, sometimes a full measure past the bracket.
   */
  private computeEffectiveLoopEnd(bracketEndTimestamp: number): number {
    return bracketEndTimestamp;
  }

  // ---------------------------------------------------------------------------
  // Loop playback engine
  // ---------------------------------------------------------------------------

  /**
   * Get the current playback timestamp from the TimeKeeper.
   * Returns the global measure timestamp (not phrase-local).
   * Returns undefined if playback has not started.
   */
  private getCurrentPlaybackTimestamp(): number | undefined {
    if (!this.playbackStarted) return undefined;
    return this.eventStream.timeKeeper.audioTimeToMeasureTimestamp();
  }

  /**
   * Check whether the cursor has reached or passed the bracket end boundary.
   * Called from the update loop when looping is active. If the cursor has
   * passed the end, triggers a loop restart.
   */
  private loopBoundaryLogCounter: number = 0;

  private checkLoopBoundary(): void {
    if (!this.isLoopingActive) {
      if (this.loopBoundaryLogCounter === 0)
        console.log(`[LOOP-DBG] checkLoopBoundary — skipped: isLoopingActive=false`);
      return;
    }
    if (!this.bracketOverlay) {
      if (this.loopBoundaryLogCounter === 0)
        console.log(`[LOOP-DBG] checkLoopBoundary — skipped: no bracketOverlay`);
      return;
    }
    if (!this.playbackStarted) {
      if (this.loopBoundaryLogCounter === 0)
        console.log(
          `[LOOP-DBG] checkLoopBoundary — skipped: playbackStarted=false isLoopCountInActive=${this.isLoopCountInActive} countInActive=${this.countInActive}`,
        );
      return;
    }

    const currentTimestamp = this.getCurrentPlaybackTimestamp();
    if (currentTimestamp === undefined) {
      if (this.loopBoundaryLogCounter === 0)
        console.log(`[LOOP-DBG] checkLoopBoundary — skipped: currentTimestamp=undefined`);
      return;
    }

    // Log every 60 frames (~1 second) to avoid flooding
    if (this.loopBoundaryLogCounter % 60 === 0) {
      console.log(
        `[LOOP-DBG] checkLoopBoundary — currentTimestamp=${currentTimestamp.toFixed(4)} effectiveLoopEnd=${this.effectiveLoopEndTimestamp} bracketStart=${this.bracketOverlay.getStartTimestamp()} bracketEnd=${this.bracketOverlay.getEndTimestamp()}`,
      );
    }
    this.loopBoundaryLogCounter++;

    if (currentTimestamp >= this.effectiveLoopEndTimestamp) {
      console.log(
        `[LOOP-DBG] checkLoopBoundary — boundary reached, calling restartLoop currentTimestamp=${currentTimestamp} effectiveLoopEnd=${this.effectiveLoopEndTimestamp}`,
      );
      this.loopBoundaryLogCounter = 0;
      this.restartLoop();
    }
  }

  /**
   * Perfect-phrase: poll the looped section's running accuracy. Called every
   * tick while a loop iteration is playing. Notifies the PPC when accuracy has
   * dropped below 100% (any non-green / extra note in the bracket so far).
   */
  /**
   * Drop the perfect-phrase streak AND clear the per-iteration perfect latch.
   * Clearing the latch matters on reset paths that don't pass back through
   * onLoopStarted (notably a resume after the >60s pause grace): a stale
   * "already perfect" flag must not survive to award a point at the next loop
   * boundary after the streak was deliberately reset.
   */
  private resetPerfectPhrase(reason = 'unspecified'): void {
    console.log(
      `[PPC] trigger=RESET reason=${reason} (streak→0, latch cleared, was latched=${this.perfectLoopLatched})`,
    );
    this.perfectLoopLatched = false;
    this.prevPerfectTickOk = null;
    this.perfectPhrase?.reset();
  }

  /**
   * Perfect-phrase: event-driven drop wired to EventStream. Fires the moment a
   * note inside the loop range grades yellow/red (a real semi-error/error, not a
   * forgiven anticipatory early start). Clears the 100% latch and pushes an
   * accuracy drop into the machine immediately, so a mistimed release of the
   * loop's final note fails the iteration even though its onsets were all green
   * (which would otherwise have latched the iteration perfect).
   */
  private handleLoopPerfectionBroken(): void {
    if (!this.isLoopingActive || !this.perfectPhrase) return;
    console.log(
      `[PPC] hook handleLoopPerfectionBroken — dropping (was latched=${this.perfectLoopLatched} prevTickOk=${this.prevPerfectTickOk})`,
    );
    this.perfectLoopLatched = false;
    this.prevPerfectTickOk = false;
    this.perfectPhrase.onLoopTick(false);
  }

  private pollPerfectPhraseAccuracy(): void {
    if (!this.perfectPhrase || !this.bracketOverlay) return;
    const start = this.bracketOverlay.getStartTimestamp();
    const end = this.bracketOverlay.getEndTimestamp();
    const { totalNotes, hitNotes, hasError } = this.eventStream.loopRangeAccuracy(start, end);
    // Latch the iteration as perfect the instant every expected note is green
    // with no error. After this point only an extra note can flip hasError, and
    // any extra note now is the anticipatory first note of the NEXT iteration
    // (played a hair early in the wind-back cushion) — not a mistake in the
    // loop the user just completed. Keep the latch so the trailing note neither
    // drops the streak here nor fails the boundary check in isLoopRangePerfect.
    const wasLatched = this.perfectLoopLatched;
    if (totalNotes > 0 && hitNotes === totalNotes && !hasError) {
      this.perfectLoopLatched = true;
    }
    if (!wasLatched && this.perfectLoopLatched) {
      console.log(
        `[PPC] latch SET — iteration reached clean 100% (totalNotes=${totalNotes} hitNotes=${hitNotes})`,
      );
    }
    const ok = this.perfectLoopLatched || !hasError;
    // Log only when the verdict flips, so the ~60 Hz poll doesn't flood.
    if (ok !== this.prevPerfectTickOk) {
      console.log(
        `[PPC] poll tick ok=${ok} (latched=${this.perfectLoopLatched} hasError=${hasError} totalNotes=${totalNotes} hitNotes=${hitNotes} range=[${start},${end}))`,
      );
      this.prevPerfectTickOk = ok;
    }
    this.perfectPhrase.onLoopTick(ok);
  }

  /**
   * Perfect-phrase: strict end-of-loop check that EVERY note in the bracket
   * was played fully green — a clean HIT on both onset and release, with no
   * extra/wrong notes (i.e. exactly 100% accuracy). A single yellow note
   * (off-time onset or release) fails this check. Used at the loop boundary to
   * confirm a perfect iteration before awarding a point.
   */
  private isLoopRangePerfect(): boolean {
    // If the iteration already reached a clean 100% on an earlier tick, honour
    // that — an anticipatory next-iteration note now sitting in the range must
    // not retroactively fail a loop the user played perfectly (see
    // perfectLoopLatched).
    if (this.perfectLoopLatched) {
      console.log('[PPC] evaluate boundary — latched=true → perfect (skipped range check)');
      return true;
    }
    if (!this.bracketOverlay) {
      console.log('[PPC] evaluate boundary — no bracketOverlay → not perfect');
      return false;
    }
    const start = this.bracketOverlay.getStartTimestamp();
    const end = this.bracketOverlay.getEndTimestamp();
    // Pass debug=true so loopRangeAccuracy logs the full [PPC] verdict breakdown
    // (per-note non-hits + extra-note decisions) once at the decision point.
    const { totalNotes, hitNotes, hasError } = this.eventStream.loopRangeAccuracy(start, end, true);
    const result = totalNotes > 0 && hitNotes === totalNotes && !hasError;
    console.log(
      `[PPC] evaluate boundary — result=${result} (totalNotes=${totalNotes} hitNotes=${hitNotes} hasError=${hasError} latched=false)`,
    );
    return result;
  }

  /**
   * Perfect-phrase: keep the checkbox badge anchored to the top-right of the
   * loop bracket. Hides it whenever the counter is disabled, looping is off, or
   * the bracket hasn't rendered yet.
   */
  private updatePerfectPhraseBadge(): void {
    if (!this.perfectPhraseBadge) return;

    if (!this.perfectPhraseEnabled || !this.isLoopingActive || !this.bracketOverlay) {
      this.perfectPhraseBadge.setVisible(false);
      return;
    }

    const topRight = this.bracketOverlay.getTopRightWorldPosition();
    if (!topRight) {
      this.perfectPhraseBadge.setVisible(false);
      return;
    }

    this.perfectPhraseBadge.setWorldPosition(topRight.x, topRight.y);
    this.perfectPhraseBadge.setVisible(true);
  }

  /**
   * Apply the Perfect Phrase Counter config sourced from React/Redux. Updates
   * the badge's checkbox count and, when the enabled flag or qualifying streak
   * changes, resets the streak so the boxes start fresh under the new setting.
   */
  private setPerfectPhraseConfig(data: { enabled: boolean; targetStreak: number }): void {
    const changed =
      data.enabled !== this.perfectPhraseEnabled || data.targetStreak !== this.perfectPhraseTarget;
    console.log(
      `[PPC] setConfig enabled=${data.enabled} target=${data.targetStreak} changed=${changed}`,
    );
    this.perfectPhraseEnabled = data.enabled;
    this.perfectPhraseTarget = Math.max(0, data.targetStreak);
    this.perfectPhraseBadge?.setTarget(this.perfectPhraseTarget);
    if (changed) {
      this.resetPerfectPhrase('config-changed');
      this.prevPerfectStreak = 0;
    }
  }

  /**
   * Streak-changed handler wired to the PPC controller. Keeps the badge in
   * sync and fires the "perfected" toast once when the streak first reaches the
   * qualifying target (only while the counter is enabled).
   */
  private handlePerfectPhraseStreak(streak: number): void {
    this.perfectPhraseBadge?.setStreak(streak);

    const firePerfectedToast =
      this.perfectPhraseEnabled &&
      this.perfectPhraseTarget > 0 &&
      streak >= this.perfectPhraseTarget &&
      this.prevPerfectStreak < this.perfectPhraseTarget;
    console.log(
      `[PPC] streak→${streak} (prev=${this.prevPerfectStreak} target=${this.perfectPhraseTarget} enabled=${this.perfectPhraseEnabled} firePerfectedToast=${firePerfectedToast})`,
    );

    if (firePerfectedToast && this.bracketOverlay) {
      const loopStartTimestamp = this.bracketOverlay.getStartTimestamp();
      const loopEndTimestamp = this.bracketOverlay.getEndTimestamp();
      NotifyReactEvents.SectionPerfected({
        loopStartTimestamp,
        loopEndTimestamp,
        loopStartMeasure: this.measureOfLastNoteBefore(loopStartTimestamp, true),
        loopEndMeasure: this.measureOfLastNoteBefore(loopEndTimestamp, false),
        streakTarget: this.perfectPhraseTarget,
        bpm: this.tempo,
      });
    }
    this.prevPerfectStreak = streak;
  }

  /**
   * The measure of the last note that starts before a score position (at or
   * before it when inclusive), numbered from 1 at the start of the piece: the
   * phrase's own measure number offset by the measures of the phrases before it,
   * as containsMeasure counts them. A pickup bar counts as measure 1.
   */
  private measureOfLastNoteBefore(timestamp: number, inclusive: boolean): number {
    const startsBefore = (noteTimestamp: number) =>
      inclusive ? noteTimestamp <= timestamp : noteTimestamp < timestamp;
    const phrasesStartedBefore = this.phrases.filter((candidate) =>
      candidate.notePositions.some((noteDataItem) => startsBefore(noteDataItem.timestamp)),
    );
    const phrase = phrasesStartedBefore[phrasesStartedBefore.length - 1];
    const lastNote = phrase?.notePositions
      .filter((noteDataItem) => startsBefore(noteDataItem.timestamp))
      .reduce((latest, noteDataItem) =>
        noteDataItem.timestamp >= latest.timestamp ? noteDataItem : latest,
      );
    if (!phrase || !lastNote) return 1;
    return Math.max(1, phrase.phraseStartingMeasure + lastNote.measureNumber);
  }

  /**
   * Full reset of the loop: wipes all note states (including correct/green),
   * stops playback, and returns to blink/wait mode at the bracket start.
   * Triggered by the replay button during looping.
   */
  private restartLoopFromBeginning(): void {
    if (!this.isLoopingActive || !this.bracketOverlay) return;

    // MUS-243: dispatch USER_RESET. The machine's resulting phase depends on
    // where it was:
    //   - playing / loopCountInWait → loopCountInWait (fresh flexible count-in)
    //   - paused → stays `paused` (MUS-242: reset-while-paused only resets
    //     visuals; the user must press play to resume).
    // The scene presentation MUST match the machine: only present a count-in
    // when the machine actually entered loopCountInWait. Previously this body
    // unconditionally set the count-in flags + emitted StartedPlaying, so a
    // reset-while-paused showed a flexible count-in the paused machine would
    // never service (playing a note was a no-op → cursor frozen, metronome
    // dead). Gate on the post-reset phase instead.
    this.dispatchPlaybackEvent({ type: 'USER_RESET' });
    const phaseAfterReset = this.getPlaybackPhase();

    const bracketStart = this.bracketOverlay.getStartTimestamp();

    this.phrases[this.activePhrase]?.cursor?.pause();
    this.phrases[this.activePhrase]?.deactivate();

    this.playbackStarted = false;
    this.hasEverPlayed = false;

    // Cursor sits visually at the loop start either way. (The machine's
    // paused/loopCountInWait actions also position it; this is idempotent.)
    this.cursorStartTimestamp = bracketStart;
    this.positionCursorAtBracketStart(bracketStart);

    if (phaseAfterReset === 'loopCountInWait' && this.leaveCountInWaitWhenUngraded(false)) {
      // Ungraded: the loop counts back in on its own.
    } else if (phaseAfterReset === 'loopCountInWait') {
      // Reset while playing / mid-count-in: present the flexible count-in so
      // the next note starts the loop (the machine is waiting for NOTE_PLAYED).
      this.isLoopCountInActive = true;
      this.countInActive = true;
      this.shouldUpdate = true;
      this.lastTickTime = this.timeKeeperAuxiliary?.audioTimeToMeasureTimestamp() ?? 0;
      NotifyReactEvents.StartedPlaying(undefined);
    } else {
      // Reset while paused (MUS-242): stay paused. Do NOT present a count-in
      // or emit StartedPlaying — the machine is in `paused` and won't accept
      // NOTE_PLAYED. The user presses play to resume (USER_PLAY → paused →
      // loopCountInWait), which then runs the real count-in.
      this.isLoopCountInActive = false;
      this.countInActive = false;
      this.shouldUpdate = false;
    }
  }

  /**
   * Restart playback from the bracket start position.
   * Stops the current cursor, activates the phrase containing the bracket
   * start, positions the cursor there, resets the event stream index,
   * and immediately resumes playback.
   */
  private restartLoop(): void {
    if (!this.bracketOverlay) return;
    console.log(`[LOOP-DBG] restartLoop() called`);

    // Perfect-phrase: the cursor reached the bracket end — this completes the
    // current loop iteration. Evaluate it BEFORE LOOP_BOUNDARY (below) resets
    // the note visuals/states, so the strict end-of-loop check still sees the
    // notes the user just played. The next iteration's onLoopStarted() fires
    // off the loopCountInWait → playing transition in the actor subscription.
    console.log('[PPC] trigger=LOOP_COMPLETED (cursor reached bracket end) — evaluating iteration');
    this.perfectPhrase?.onLoopCompleted(this.isLoopRangePerfect());

    // MUS-243: dispatch LOOP_BOUNDARY so the machine transitions through
    // loopBoundaryRestart → loopCountInWait. The machine's actions handle
    // subsystem-level work via the facade (resetLoopVisuals, pauseMainTK,
    // pauseScheduler2, armAuxTK, etc.). The remaining imperative work below
    // covers scene-specific concerns the machine doesn't model: per-phrase
    // iterator reset, cursor.setStart with computed X coordinates, note
    // visual reset across the bracket range, and recent-note-buffer replay.
    this.dispatchPlaybackEvent({ type: 'LOOP_BOUNDARY' });

    const bracketStartTimestamp = this.bracketOverlay.getStartTimestamp();

    // Find the phrase and note at the bracket start
    const targetPhraseIndex = this.findPhraseIndexForTimestamp(bracketStartTimestamp);
    if (targetPhraseIndex === -1) {
      Logger.warn('[OSMDRepertoire] restartLoop — no phrase found for bracket start', {
        bracketStartTimestamp,
      });
      return;
    }

    const targetPhrase = this.phrases[targetPhraseIndex];
    const targetNoteData = this.findNearestNoteInPhrase(targetPhrase, bracketStartTimestamp);
    if (!targetNoteData) {
      Logger.warn('[OSMDRepertoire] restartLoop — no note found at bracket start', {
        bracketStartTimestamp,
        phraseIndex: targetPhraseIndex,
      });
      return;
    }

    // Pause the current cursor
    console.log(
      `[LOOP-DBG] restartLoop — BEFORE cursor pause activePhrase=${this.activePhrase} targetPhraseIndex=${targetPhraseIndex} bracketStartTimestamp=${bracketStartTimestamp}`,
    );
    this.phrases[this.activePhrase]?.cursor?.pause();
    this.phrases[this.activePhrase]?.cursor?.setAlpha(0);
    this.phrases[this.activePhrase]?.deactivate();
    console.log(`[LOOP-DBG] restartLoop — AFTER cursor pause`);

    // Switch to the target phrase
    this.activePhrase = targetPhraseIndex;

    // Reset all phrase iterators that fall within the bracket range. When the
    // loop spans multiple phrases, subsequent iterations enter later phrases
    // with stale iterator state from previous iterations (lastPlayedMeasure,
    // currentMeasureIndex). Reset them so each loop iteration starts fresh.
    const bracketEndForReset = this.bracketOverlay.getEndTimestamp();
    this.phrases.forEach((phrase, idx) => {
      const phraseStart = phrase.data.startTimestamp;
      const phraseInBracket =
        phraseStart < bracketEndForReset &&
        phrase.notePositions.some((n) => !n.isRest && n.timestamp <= bracketEndForReset);
      if (phraseInBracket && idx !== targetPhraseIndex && phrase.cursor) {
        const iter = phrase.cursor.Iterator as unknown as {
          resetIterator?: () => void;
          lastPlayedMeasure?: number;
          shouldIncrementMeasureIndex?: boolean;
          starts?: number[];
        };
        iter.resetIterator?.();
        iter.lastPlayedMeasure = 0;
        iter.shouldIncrementMeasureIndex = false;
      }
    });

    // Position the cursor at the bracket start note
    const container = targetPhrase.getContainer();
    const scale = container.scaleX;
    const cursorWorldX = container.x + targetNoteData.x * scale;

    const localTimestamp = targetNoteData.timestamp - targetPhrase.data.startTimestamp;
    const localMeasureIndex = Math.max(targetNoteData.measureNumber - 1, 0);

    targetPhrase.activate(true);
    if (targetPhrase.cursor) {
      const restartIterExt = targetPhrase.cursor.Iterator as unknown as {
        ensureStartsPopulated?: (m: number) => void;
      };
      if (restartIterExt.ensureStartsPopulated) {
        restartIterExt.ensureStartsPopulated(localMeasureIndex);
      }
      const beforeRestart = targetPhrase.cursor as unknown as {
        lastPlayedMeasure?: number;
        iterator?: { lastPlayedMeasure?: number; currentMeasureIndex?: number; indx?: number };
      };
      console.log(
        `[PAUSE-DBG] restartLoop — BEFORE cursor.setStart cursorLastPlayedMeasure=${beforeRestart.lastPlayedMeasure} iterLastPlayedMeasure=${beforeRestart.iterator?.lastPlayedMeasure} iterMeasureIdx=${beforeRestart.iterator?.currentMeasureIndex} iterIndx=${beforeRestart.iterator?.indx}`,
      );
      try {
        targetPhrase.cursor.setStart(cursorWorldX, localTimestamp, localMeasureIndex);
      } catch {
        // Iterator not ready — visual position is sufficient
      }
      const afterRestart = targetPhrase.cursor as unknown as {
        lastPlayedMeasure?: number;
        iterator?: { lastPlayedMeasure?: number; currentMeasureIndex?: number; indx?: number };
      };
      console.log(
        `[PAUSE-DBG] restartLoop — AFTER cursor.setStart cursorLastPlayedMeasure=${afterRestart.lastPlayedMeasure} iterLastPlayedMeasure=${afterRestart.iterator?.lastPlayedMeasure} iterMeasureIdx=${afterRestart.iterator?.currentMeasureIndex} iterIndx=${afterRestart.iterator?.indx} localMeasureIndex=${localMeasureIndex}`,
      );
      targetPhrase.cursor.setX(cursorWorldX - targetPhrase.cursor.width / 2);
      targetPhrase.cursor.enable();
    }
    this.updateMobileCameraScroll();

    // MUS-243: error recognition, event-stream reset (seek + note-state +
    // accuracy queues), main-TK pause, aux-TK arming, and cursor blink are
    // ALL owned by the machine now (LOOP_BOUNDARY → loopBoundaryRestart →
    // loopCountInWait fires resetLoopVisuals, pauseMainTK, disableError-
    // Recognition, armAuxTK, enableCursorBlink via the facade). Re-running the
    // error-recognition disable here used to corrupt the state the loop
    // restored to; recognition is now suppressed/un-suppressed on the
    // eventStream (which preserves the user's intent), so this method must not
    // touch it at all. Only the note-container visual reset stays (the
    // machine's resetLoopVisuals handles event-stream state; this handles
    // the per-NoteContainer visuals).
    const bracketEndTimestamp = this.bracketOverlay.getEndTimestamp();
    this.resetNoteVisualsInRange(bracketStartTimestamp, bracketEndTimestamp);

    this.playbackStarted = false;
    this.isLoopCountInActive = true;
    this.countInActive = true;
    this.lastTickTime = this.timeKeeperAuxiliary?.audioTimeToMeasureTimestamp() ?? 0;

    // Always scroll back to the top of the loop on reset. For 1- or 2-line
    // loops that already fit fully in frame the tween's target equals the
    // current scrollY so it's a visual no-op; for 3+ line loops where the
    // user has scrolled forward past the loop start, this returns the
    // camera to the top of the loop bracket before the count-in.
    this.scrollToShowBracketRange();

    // Ungraded: no note will start the next pass; it counts back in on its own.
    if (this.leaveCountInWaitWhenUngraded(true)) {
      this.recentNoteBuffer = [];
      return;
    }

    // Emit StartedPlaying to ensure the scheduler's tick interval exists
    // and lastScheduledBeat is reset for the next iteration.
    NotifyReactEvents.StartedPlaying(undefined);

    // Check if notes were played just before the loop boundary (within 300ms).
    // Replay all of them (handles chords) as the first notes of the new
    // iteration so the user can play continuously through the boundary.
    const NOTE_BUFFER_WINDOW_MS = 300;
    const now = performance.now();
    const recentNotes = this.recentNoteBuffer.filter((n) => now - n.time < NOTE_BUFFER_WINDOW_MS);
    this.recentNoteBuffer = [];

    if (recentNotes.length > 0) {
      console.log(
        `[LOOP-DBG] restartLoop — replaying ${recentNotes.length} buffered notes: [${recentNotes.map((n) => `${n.payload.note}(${(now - n.time).toFixed(0)}ms)`).join(', ')}]`,
      );
      // The first note triggers LOOP_COUNT_IN_PATH (unpauses TK).
      // Subsequent notes go through PLAYBACK_STARTED path (TK already running).
      for (const buffered of recentNotes) {
        this.noteOn({ ...buffered.payload, observedAtMs: undefined });
      }
    }

    Logger.info('[OSMDRepertoire] Loop restarted with count-in', {
      bracketStartTimestamp,
      targetPhraseIndex,
      noteTimestamp: targetNoteData.timestamp,
      localTimestamp,
      timeSignature: `${targetPhrase.data.timeSignature.numerator}/${targetPhrase.data.timeSignature.denominator}`,
    });
  }

  // beginLoopCountIn() removed — loop restart is now self-contained.
  // The main TimeKeeper is paused directly in restartLoop() and unpaused
  // in noteOn() when the user plays the first note.

  /**
   * Find which phrase contains a given global timestamp.
   * Returns the phrase index, or -1 if no phrase contains the timestamp.
   */
  private findPhraseIndexForTimestamp(timestamp: number): number {
    return this.phrases.findIndex((phrase) => {
      const phraseTimestamps = phrase.notePositions.map((noteDataItem) => noteDataItem.timestamp);
      if (phraseTimestamps.length === 0) return false;

      const phraseMinTimestamp = Math.min(...phraseTimestamps);
      const phraseMaxTimestamp = Math.max(...phraseTimestamps);

      return timestamp >= phraseMinTimestamp && timestamp <= phraseMaxTimestamp;
    });
  }

  /**
   * Find the nearest non-rest note in a phrase to the given timestamp.
   * Returns the note data, or undefined if no suitable note exists.
   */
  private findNearestNoteInPhrase(
    phrase: OSMDPlayablePhrase,
    timestamp: number,
  ): OSMDNoteData | undefined {
    const nonRestNotes = phrase.notePositions.filter((noteDataItem) => !noteDataItem.isRest);
    if (nonRestNotes.length === 0) return undefined;

    return nonRestNotes.reduce((closest, noteDataItem) => {
      const closestDiff = Math.abs(closest.timestamp - timestamp);
      const currentDiff = Math.abs(noteDataItem.timestamp - timestamp);
      return currentDiff < closestDiff ? noteDataItem : closest;
    }, nonRestNotes[0]);
  }

  /**
   * Get the world-space X position of the bracket end. Used to clamp the
   * cursor so it doesn't visually advance past the bracket during looping.
   * Returns undefined if the bracket end position can't be resolved.
   */
  /**
   * Check if the bracket end timestamp falls within a specific phrase's
   * note range. Used to decide whether the cursor X clamp should apply —
   * the clamp should only restrict the cursor on the phrase where the
   * bracket actually ends, not on earlier phrases in a multi-phrase loop.
   */
  private isBracketEndOnPhrase(phraseIndex: number): boolean {
    if (!this.bracketOverlay) return false;
    const bracketEnd = this.bracketOverlay.getEndTimestamp();
    const phrase = this.phrases[phraseIndex];
    if (!phrase) return false;

    const nonRestNotes = phrase.notePositions.filter((n) => !n.isRest);
    if (nonRestNotes.length === 0) return false;

    const phraseMin = nonRestNotes[0].timestamp;
    const lastNote = nonRestNotes[nonRestNotes.length - 1];
    const phraseMaxOffset = lastNote.timestamp + lastNote.duration;

    // Bracket end falls on this phrase if it's within or at the phrase's
    // note range (onset of first note to offset of last note).
    return bracketEnd > phraseMin && bracketEnd <= phraseMaxOffset;
  }

  private getBracketEndWorldX(): number | undefined {
    if (!this.bracketOverlay) return undefined;
    const bracketEnd = this.bracketOverlay.getEndTimestamp();

    // The bracket end is the MAX offset of the last in-bracket chord. Find
    // the note whose onset is closest to (but before) bracketEnd and use its
    // phrase container to compute the world X at the bracketEnd timestamp.
    for (const phrase of this.phrases) {
      const container = phrase.getContainer();
      const scale = container.scaleX;

      // Find the last non-rest note in this phrase whose onset < bracketEnd
      const notesBeforeEnd = phrase.notePositions.filter(
        (n) => !n.isRest && n.timestamp < bracketEnd,
      );
      if (notesBeforeEnd.length === 0) continue;

      const lastNote = notesBeforeEnd[notesBeforeEnd.length - 1];
      // If this note's offset reaches bracketEnd, this phrase contains the
      // bracket boundary. Interpolate to get the X at bracketEnd.
      if (lastNote.timestamp + lastNote.duration >= bracketEnd - 0.001) {
        // Use the next note's X as a proxy for bracketEnd X, or the phrase end
        const nextNote = phrase.notePositions.find((n) => !n.isRest && n.timestamp >= bracketEnd);
        if (nextNote) {
          return container.x + nextNote.x * scale;
        }
        // No next note — use phrase end X
        return phrase.getStaffLineRightEdgeX();
      }
    }
    return undefined;
  }

  /**
   * Reset the visual state of NoteContainers whose timestamps fall within
   * the given range, preserving notes that were played correctly (green).
   *
   * When looping is active, correct notes keep their green coloring so the
   * user can see which notes they already nailed. Yellow and red notes
   * return to default so the user gets a fresh attempt on the next pass.
   */
  private resetNoteVisualsInRange(startTimestamp: number, endTimestamp: number): void {
    const shouldPreserveCorrectNotes = this.isLoopingActive;

    for (const phrase of this.phrases) {
      const phraseTimestamps = phrase.notePositions.map((noteDataItem) => noteDataItem.timestamp);
      if (phraseTimestamps.length === 0) continue;

      const phraseMin = Math.min(...phraseTimestamps);
      const phraseMax = Math.max(...phraseTimestamps);

      const phraseOverlapsBracket = phraseMax >= startTimestamp && phraseMin <= endTimestamp;

      if (phraseOverlapsBracket) {
        for (const noteContainer of phrase.noteContainers.values()) {
          const isCorrectNote = noteContainer.getState() === 'correct';

          if (shouldPreserveCorrectNotes && isCorrectNote) {
            continue;
          }

          noteContainer.reset();
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Ripple fade animation
  // ---------------------------------------------------------------------------

  /**
   * Called by the OSMDEventStream callback whenever a note is marked correct.
   * On the first correct note of a new loop iteration, immediately reset all
   * note visuals in the bracket range to default color (except the note that
   * was just played) so the user starts each pass with a clean slate.
   */
  private handleNoteMarkedCorrect(justPlayedNoteIds: string[]): void {
    if (!this.awaitingRippleFadeTrigger) return;
    if (!this.isLoopingActive) return;

    // Reset on the FIRST correct note of the new iteration
    this.awaitingRippleFadeTrigger = false;
    this.rippleFadeNoteCount = 0;

    const justPlayedSet = new Set(justPlayedNoteIds);

    // Immediately reset all note visuals in the bracket to default,
    // EXCEPT the note that was just marked correct — it should keep
    // its green color as immediate visual feedback.
    const bracketStart = this.bracketOverlay?.getStartTimestamp() ?? 0;
    const bracketEnd = this.bracketOverlay?.getEndTimestamp() ?? 0;
    for (const phrase of this.phrases) {
      for (const [id, noteContainer] of phrase.noteContainers.entries()) {
        if (justPlayedSet.has(id)) continue;
        const noteData = phrase.notePositions.find((n) => n.vexflowId === id);
        if (!noteData) continue;
        if (noteData.timestamp >= bracketStart && noteData.timestamp < bracketEnd) {
          noteContainer.reset();
        }
      }
    }
  }

  /**
   * Collect all NoteContainers within the bracket range that are currently
   * in the 'correct' (green) state, sorted by timestamp.
   */
  private collectGreenNotesInBracket(): Array<{
    noteContainer: NoteContainer;
    timestamp: number;
  }> {
    if (!this.bracketOverlay) return [];

    const bracketStart = this.bracketOverlay.getStartTimestamp();
    const bracketEnd = this.bracketOverlay.getEndTimestamp();

    // Get the current TK timestamp — notes at or before this point were
    // played in the CURRENT pass and should NOT be faded.
    const currentTimestamp = this.eventStream?.timeKeeper?.audioTimeToMeasureTimestamp() ?? 0;

    const greenNotes: Array<{ noteContainer: NoteContainer; timestamp: number }> = [];

    for (const phrase of this.phrases) {
      for (const noteData of phrase.notePositions) {
        if (noteData.isRest) continue;
        if (noteData.timestamp < bracketStart || noteData.timestamp > bracketEnd) continue;
        // Skip notes the user has already played in this pass
        if (noteData.timestamp <= currentTimestamp) continue;

        const noteContainer = phrase.getNoteContainer(noteData.vexflowId);
        if (noteContainer && noteContainer.getState() === 'correct') {
          greenNotes.push({ noteContainer, timestamp: noteData.timestamp });
        }
      }
    }

    return greenNotes.sort((a, b) => a.timestamp - b.timestamp);
  }

  /**
   * Calculate the total sweep duration in milliseconds for the ripple fade.
   * The sweep runs at 10x the current tempo across the bracket's timestamp range.
   *
   * The bracket timestamps are in "measure units" (whole-note fractions).
   * One whole note = `denominator` bottom notes, and the tempo counts the
   * meter's beat (a quarter in 4/4, a dotted quarter in 6/8: see MeterTiming),
   * so one measure-unit second = (60/tempo) * 4 seconds in x/4 and
   * (60/tempo) / 3 * 8 in 6/8.
   * At 10x speed the duration is divided by 10.
   */
  private calculateSweepDurationMs(): number {
    if (!this.bracketOverlay) return 0;

    const bracketStart = this.bracketOverlay.getStartTimestamp();
    const bracketEnd = this.bracketOverlay.getEndTimestamp();
    const bracketRangeInWholeNotes = bracketEnd - bracketStart;

    const secondsPerWholeNoteAtTempo = secondsPerWholeNote(
      this.tempo,
      this.getScoreTimeSignature(),
    );
    const sweepDurationSeconds = (bracketRangeInWholeNotes * secondsPerWholeNoteAtTempo) / 10;
    return sweepDurationSeconds * 1000;
  }

  /**
   * Start the ripple fade animation: sweep from bracket start to bracket end
   * at 3x tempo, progressively fading each green note back to its default state.
   */
  private startRippleFade(): void {
    if (!this.bracketOverlay) return;

    // Cancel any in-progress ripple fade from a previous trigger
    this.cancelRippleFade();

    const greenNotes = this.collectGreenNotesInBracket();
    console.log(
      `[LOOP-DBG] startRippleFade — greenNotes count=${greenNotes.length} sweepDurationMs=${this.calculateSweepDurationMs()}`,
    );
    if (greenNotes.length === 0) return;

    const bracketStart = this.bracketOverlay.getStartTimestamp();
    const bracketEnd = this.bracketOverlay.getEndTimestamp();
    const bracketRange = bracketEnd - bracketStart;
    const sweepDurationMs = this.calculateSweepDurationMs();
    const noteFadeDurationMs = 200;

    if (bracketRange <= 0 || sweepDurationMs <= 0) return;

    Logger.info('[OSMDRepertoire] Starting ripple fade animation', {
      greenNoteCount: greenNotes.length,
      sweepDurationMs,
      bracketStart,
      bracketEnd,
    });

    const tweens = greenNotes.map(({ noteContainer, timestamp }) => {
      const positionRatio = (timestamp - bracketStart) / bracketRange;
      const delayMs = positionRatio * sweepDurationMs;

      return this.tweens.add({
        targets: noteContainer.container,
        alpha: { from: 1, to: 0 },
        duration: noteFadeDurationMs,
        delay: delayMs,
        onComplete: () => {
          noteContainer.reset();
        },
      });
    });

    this.rippleFadeTweens = tweens;
  }

  /**
   * Cancel any active ripple fade tweens. Notes that have not yet been
   * swept remain in their current visual state (green stays green).
   * Restores full alpha on any note whose tween was mid-fade so it
   * does not get stuck partially transparent.
   */
  private cancelRippleFade(): void {
    if (!Array.isArray(this.rippleFadeTweens)) {
      this.rippleFadeTweens = [];
      this.awaitingRippleFadeTrigger = false;
      return;
    }
    for (const tween of this.rippleFadeTweens) {
      if (!tween) continue;
      try {
        const targets = tween.targets;
        if (Array.isArray(targets)) {
          for (const target of targets) {
            if (target && 'setAlpha' in target && typeof target.setAlpha === 'function') {
              target.setAlpha(1);
            }
          }
        }
        tween.stop();
      } catch {
        // Tween or its targets may already be destroyed
      }
    }
    this.rippleFadeTweens = [];
    this.awaitingRippleFadeTrigger = false;
  }

  /**
   * Handle bracket boundary changes during active playback.
   * If the cursor has passed the new bracket end, restart the loop from
   * the new start. Otherwise, the loop will naturally continue to the
   * new end boundary.
   */
  private handleBracketBoundsChanged(): void {
    if (!this.isLoopingActive) return;
    if (!this.bracketOverlay) return;
    if (!this.playbackStarted) return;

    const currentTimestamp = this.getCurrentPlaybackTimestamp();
    if (currentTimestamp === undefined) return;

    const newEndTimestamp = this.bracketOverlay.getEndTimestamp();

    if (currentTimestamp >= newEndTimestamp) {
      Logger.info('[OSMDRepertoire] Cursor past new bracket end after resize — restarting loop', {
        currentTimestamp,
        newEndTimestamp,
      });
      this.restartLoop();
    }
  }

  // ---------------------------------------------------------------------------
  // Phrase loading
  // ---------------------------------------------------------------------------

  private hasUnrenderedPhrases(): boolean {
    return this.pendingPhraseDatas.length > 0;
  }

  /**
   * Extend the rendered score by one bounded batch.
   *
   * Calls are coalesced so a wheel event and playback prefetch cannot mutate
   * the OSMD/Phaser graph at the same time. The generation check prevents a
   * slow batch from attaching itself to a different piece after navigation.
   */
  private requestNextPhraseBatch(reason: 'viewport' | 'playback' | 'boundary'): Promise<void> {
    if (this.lazyPhraseLoadPromise) return this.lazyPhraseLoadPromise;
    if (!this.hasUnrenderedPhrases()) return Promise.resolve();

    const generation = this.phraseLoadGeneration;
    const batch = this.pendingPhraseDatas.splice(0, REPERTOIRE_PHRASE_BATCH_SIZE);

    Logger.debug('[OSMDRepertoire] Loading lazy phrase batch', {
      reason,
      batchSize: batch.length,
      renderedPhraseCount: this.phrases.length,
      pendingPhraseCount: this.pendingPhraseDatas.length,
      totalPhraseCount: this.totalPhraseCount,
    });

    let batchFailed = false;
    const loadPromise = (async () => {
      let renderedCount = 0;
      try {
        for (const data of batch) {
          if (generation !== this.phraseLoadGeneration) return;
          const added = await this.addPhrase(data, true, true, generation);
          if (!added || generation !== this.phraseLoadGeneration) return;
          renderedCount++;
          // Give input, animation, and audio work a frame between expensive
          // OSMD phrase initializations instead of monopolizing the main loop.
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
        }
      } catch (error) {
        batchFailed = true;
        // Preserve the unrendered tail so a transient context recovery can
        // retry it instead of silently shortening the piece.
        if (generation === this.phraseLoadGeneration) {
          const notRendered = batch.slice(renderedCount);
          this.pendingPhraseDatas.unshift(...notRendered);
        }
        Logger.error('[OSMDRepertoire] Lazy phrase batch failed', {
          reason,
          renderedCount,
          batchSize: batch.length,
          error,
        });
        reportError(error, 'osmd.lazyPhraseBatch');
      } finally {
        // Keep successfully rendered phrases usable even if a later phrase in
        // the same batch failed. The unrendered tail remains queued for retry.
        if (generation === this.phraseLoadGeneration && renderedCount > 0) {
          this.layoutPhrases();
          this.updateCursorDimensions();
          this.logMemoryDiagnostics(`After lazy batch (${reason})`);
        }
      }
    })().finally(() => {
      if (generation !== this.phraseLoadGeneration) return;
      this.lazyPhraseLoadPromise = null;

      // addPhrase() can replace existing cursors and note containers while
      // normalizing a newly rendered phrase. If reset was pressed during that
      // work, repeat the reset now against the final objects rather than
      // allowing the late batch to leave stale colors/cursor state behind.
      if (this.softResetPendingAfterLazyLoad) {
        this.softResetPendingAfterLazyLoad = false;
        this.softReset();
        return;
      }

      // The cursor can finish during an unusually slow OSMD render. Complete
      // the transition as soon as the next phrase exists; do not mark the
      // entire repertoire complete at the temporary lazy boundary.
      if (this.waitingForLazyPhraseAdvance && this.activePhrase + 1 < this.phrases.length) {
        this.waitingForLazyPhraseAdvance = false;
        this.onFinishPlayingPhrase();
      }

      if (!batchFailed) {
        if (
          shouldPrefetchRepertoireForPlayback(
            this.activePhrase,
            this.phrases.length,
            this.totalPhraseCount,
          )
        ) {
          void this.requestNextPhraseBatch('playback');
        } else {
          this.prefetchForCurrentViewport();
        }
      }
    });

    this.lazyPhraseLoadPromise = loadPromise;
    return loadPromise;
  }

  /** Request more score when manual scrolling approaches the rendered edge. */
  private prefetchForCurrentViewport(): void {
    if (!this.hasUnrenderedPhrases() || this.lazyPhraseLoadPromise) return;
    const camera = this.cameras.main;
    const bounds = camera.getBounds();
    const shouldPrefetch = this.mobileView
      ? shouldPrefetchRepertoireForViewport(
          camera.scrollX,
          camera.width,
          bounds.width,
          this.phrases.length,
          this.totalPhraseCount,
        )
      : shouldPrefetchRepertoireForViewport(
          camera.scrollY,
          camera.height,
          bounds.height,
          this.phrases.length,
          this.totalPhraseCount,
        );
    if (shouldPrefetch) {
      void this.requestNextPhraseBatch('viewport');
    }
  }

  /**
   * Begin a repertoire load transaction. Short scores still render in full;
   * long scores render only an initial window and retain the remaining
   * MusicXML payloads for viewport/playback-driven batches.
   */
  async addAllPhrases(phraseDatas: OSMDPhraseDataEventPayload[]): Promise<void> {
    this.isAddingPhrases = true;
    this.phraseLoadGeneration++;
    const generation = this.phraseLoadGeneration;
    this.totalPhraseCount = phraseDatas.length;
    const initialPhraseCount = getInitialRepertoirePhraseCount(phraseDatas.length, this.mobileView);
    const initialPhraseDatas = phraseDatas.slice(0, initialPhraseCount);
    this.pendingPhraseDatas = phraseDatas.slice(initialPhraseCount);
    this.lazyPhraseLoadPromise = null;
    this.waitingForLazyPhraseAdvance = false;
    let loadCompleted = false;
    const initializedPhrases: Array<{
      osmdPhrase: OSMDPlayablePhrase;
      phraseRaw: Phrase;
      phraseData: RepertoirePhraseData;
      originalPayload: OSMDPhraseDataEventPayload;
    }> = [];
    let phraseBeingInitialized: OSMDPlayablePhrase | null = null;
    const abandonStaleLoad = (): boolean => {
      if (generation === this.phraseLoadGeneration) return false;

      const stalePhrases = new Set([
        ...initializedPhrases.map(({ osmdPhrase }) => osmdPhrase),
        ...(phraseBeingInitialized ? [phraseBeingInitialized] : []),
      ]);
      for (const phrase of stalePhrases) {
        try {
          phrase.destroy();
        } catch (destroyError) {
          Logger.error(
            '[OSMDPlayableScene] Failed to clean up a cancelled phrase load:',
            destroyError,
          );
        }
      }
      phraseBeingInitialized = null;
      return true;
    };

    try {
      // Clear any existing phrases to prevent duplicates if a stale queued
      // event replays alongside a fresh dispatch.
      if (this.phrases.length > 0) {
        this.phrases.forEach((phrase) => phrase.destroy());
        this.phrases = [];
        this.eventStream.reset();
      }
      this.measureCount = 0;
      this.maxPhraseContainerWidth = 0;

      const viewportWidth = this.cameras.main.width;

      // ---- Phase 1: Initialize all phrases (OSMD render only) ----
      // Each phrase gets its own container width via the two-pass adaptive
      // logic. We collect the raw payloads alongside the initialized phrases
      // so we can create cursors and events in phase 3.
      for (let i = 0; i < initialPhraseDatas.length; i++) {
        if (abandonStaleLoad()) return;
        const data = initialPhraseDatas[i];
        const { phrase, phraseRaw } = data;
        const isFirstPhrase = phrase.startTimestamp === 0;
        const phraseData: RepertoirePhraseData = {
          musicXml: phrase.musicXml,
          uuid: phrase.uuid,
          startTimestamp: phrase.startTimestamp,
          timeSignature: new TimeSignature(
            phrase.timeSignature.numerator,
            phrase.timeSignature.denominator,
            phrase.timeSignature.beatGrouping,
          ),
          showClef: this.mobileView ? isFirstPhrase : phrase.showClef,
          showBrace: this.mobileView ? isFirstPhrase : phrase.showBrace,
          showTimeSig: phrase.showTimeSig,
          transposeSemitones: phrase.transposeSemitones ?? 0,
        };

        const osmdPhrase = new OSMDPlayablePhrase(
          this,
          phraseData,
          phraseRaw,
          0,
          0,
          this.scoreTheme,
        );
        phraseBeingInitialized = osmdPhrase;

        await osmdPhrase.initialize(viewportWidth);
        if (abandonStaleLoad()) return;

        // Track the widest container width across all phrases
        const phraseContainerWidth = osmdPhrase.getResolvedContainerWidth();
        if (phraseContainerWidth > this.maxPhraseContainerWidth) {
          this.maxPhraseContainerWidth = phraseContainerWidth;
        }

        initializedPhrases.push({ osmdPhrase, phraseRaw, phraseData, originalPayload: data });
        phraseBeingInitialized = null;
      }

      // ---- Phase 2: Normalize container widths ----
      // Re-render any phrase whose container width doesn't match the max.
      // This ensures all phrases have the same originalWidth so unified
      // scaling produces equal on-screen widths. Done BEFORE cursors are
      // created so iterators always reference final notePositions.
      for (let i = 0; i < initializedPhrases.length; i++) {
        if (abandonStaleLoad()) return;
        const { osmdPhrase } = initializedPhrases[i];
        if (osmdPhrase.getResolvedContainerWidth() < this.maxPhraseContainerWidth) {
          await osmdPhrase.reRenderAtContainerWidth(this.maxPhraseContainerWidth);
          if (abandonStaleLoad()) return;
        }
      }

      // ---- Phase 3: Create cursors, add events, layout ----
      for (let i = 0; i < initializedPhrases.length; i++) {
        if (abandonStaleLoad()) return;
        const { osmdPhrase, phraseRaw, phraseData } = initializedPhrases[i];

        osmdPhrase.phraseStartingMeasure = this.measureCount;
        this.measureCount += osmdPhrase.notePositions.reduce(
          (maxMeasure, noteDataItem) => Math.max(maxMeasure, noteDataItem.measureNumber),
          0,
        );

        const phraseIndex = this.phrases.length;
        this.createCursorForPhrase(osmdPhrase, phraseRaw, phraseData, phraseIndex);
        osmdPhrase.cursorVisible = this.cursorVisible;
        this.phrases.push(osmdPhrase);

        const osmdEventIterator = new OSMDIteratorExtensible(
          osmdPhrase,
          osmdPhrase.notePositions,
          osmdPhrase.phraseModel,
          osmdPhrase.data.timeSignature,
          osmdPhrase.data.startTimestamp,
        );
        this.eventStream.addEvents2(osmdEventIterator, osmdPhrase.phraseModel.getStartTimestamp());
      }

      // Final layout and cursor dimension pass
      this.layoutPhrases();
      this.updateCursorDimensions();

      if (this.phrases.length > 0) {
        this.activatePhrase(0);
        this.computeMobileZoomBaseline();
      }

      // If looping was activated before phrases loaded (e.g. returning to
      // the screen with looping already on), re-trigger the bracket setup
      // now that phrases are available.
      if (this.isLoopingActive) {
        console.log(
          `[LOOP-DBG] addAllPhrases — looping already active, re-triggering setLoopingActive`,
        );
        this.isLoopingActive = false; // Reset so setLoopingActive runs fully
        this.setLoopingActive(true);
      }

      this.logMemoryDiagnostics('After addAllPhrases');

      Logger.log('[OSMDPlayableScene] addAllPhrases completed:', {
        renderedPhraseCount: this.phrases.length,
        totalPhraseCount: this.totalPhraseCount,
        pendingPhraseCount: this.pendingPhraseDatas.length,
      });
      loadCompleted = true;
      NotifyReactEvents.RepertoireScoreReady({
        loadId: phraseDatas[0]?.loadId,
        phraseCount: this.totalPhraseCount,
        renderedPhraseCount: this.phrases.length,
      });
      loadCompleted = true;
    } catch (error) {
      // addAllPhrases is transactional: phase 1 deliberately leaves every
      // initialized phrase at (0, 0) until the whole batch is known-good.
      // Roll all of them back if any later phrase fails instead of exposing
      // those unlaid-out containers as a jumbled partial score.
      const phrasesToDestroy = new Set([
        ...initializedPhrases.map(({ osmdPhrase }) => osmdPhrase),
        ...this.phrases,
        ...(phraseBeingInitialized ? [phraseBeingInitialized] : []),
      ]);
      for (const phrase of phrasesToDestroy) {
        try {
          phrase.destroy();
        } catch (destroyError) {
          Logger.error(
            '[OSMDPlayableScene] Failed to clean up phrase after batch load failure:',
            destroyError,
          );
        }
      }
      this.phrases = [];
      this.eventStream.reset();
      this.measureCount = 0;
      this.maxPhraseContainerWidth = 0;
      this.pendingPhraseDatas = [];
      this.totalPhraseCount = 0;
      this.pendingStartExercisePayload = null;
      Logger.error('[OSMDPlayableScene] addAllPhrases failed:', error);
      throw error;
    } finally {
      if (generation === this.phraseLoadGeneration) {
        this.isAddingPhrases = false;
      }
      if (loadCompleted && generation === this.phraseLoadGeneration) {
        this.flushPendingStartExercise();
      }
    }
  }

  /**
   * Create a single OSMDPlayablePhrase, initialise it asynchronously via OSMD,
   * create a cursor for it, and append it to the phrases array.
   *
   * NOTE: This is async but called from a synchronous event bus callback.
   * Errors are caught and logged so they don't silently disappear as
   * unhandled promise rejections.
   */
  async addPhrase(
    data: OSMDPhraseDataEventPayload,
    deferLayout: boolean = false,
    rethrowError: boolean = false,
    expectedGeneration?: number,
  ): Promise<boolean> {
    const { phrase, phraseRaw } = data;
    let osmdPhrase: OSMDPlayablePhrase | null = null;

    try {
      const isFirstPhrase = phrase.startTimestamp === 0;
      const phraseData: RepertoirePhraseData = {
        musicXml: phrase.musicXml,
        uuid: phrase.uuid,
        startTimestamp: phrase.startTimestamp,
        timeSignature: new TimeSignature(
          phrase.timeSignature.numerator,
          phrase.timeSignature.denominator,
          phrase.timeSignature.beatGrouping,
        ),
        showClef: this.mobileView ? isFirstPhrase : phrase.showClef,
        showBrace: this.mobileView ? isFirstPhrase : phrase.showBrace,
        showTimeSig: phrase.showTimeSig,
        transposeSemitones: phrase.transposeSemitones ?? 0,
      };

      osmdPhrase = new OSMDPlayablePhrase(
        this,
        phraseData,
        phraseRaw,
        0, // initial X
        0, // initial Y
        this.scoreTheme,
      );

      // Pass viewport width so OSMD can adapt its layout container width.
      // A wider container produces a wider, shorter rendering — important
      // for complex pieces that would otherwise be crammed vertically.
      const viewportWidth = this.cameras.main.width;
      await osmdPhrase.initialize(viewportWidth);
      if (expectedGeneration !== undefined && expectedGeneration !== this.phraseLoadGeneration) {
        osmdPhrase.destroy();
        return false;
      }

      // ---- Normalize all phrases to the same container width ----
      // Different phrases can resolve to different container widths based
      // on their individual aspect ratios. To ensure all phrases display
      // at the same on-screen width after unified scaling, we normalize
      // to the widest container width seen so far.
      const phraseContainerWidth = osmdPhrase.getResolvedContainerWidth();
      if (phraseContainerWidth > this.maxPhraseContainerWidth) {
        // This phrase is wider — re-render all earlier phrases at the new width.
        // Their cursors (if any) must also be recreated since the iterators
        // reference notePositions from the old render.
        const previousMaxWidth = this.maxPhraseContainerWidth;
        this.maxPhraseContainerWidth = phraseContainerWidth;
        if (previousMaxWidth > 0) {
          for (let existIdx = 0; existIdx < this.phrases.length; existIdx++) {
            const existingPhrase = this.phrases[existIdx];
            if (existingPhrase.getResolvedContainerWidth() < phraseContainerWidth) {
              await existingPhrase.reRenderAtContainerWidth(phraseContainerWidth);
              if (
                expectedGeneration !== undefined &&
                expectedGeneration !== this.phraseLoadGeneration
              ) {
                osmdPhrase.destroy();
                return false;
              }
              // Recreate cursor with updated notePositions
              if (existingPhrase.cursor) {
                existingPhrase.cursor.destroy();
                this.createCursorForPhrase(
                  existingPhrase,
                  existingPhrase.phraseModel,
                  existingPhrase.data,
                  existIdx,
                );
              }
            }
          }
        }
      } else if (phraseContainerWidth < this.maxPhraseContainerWidth) {
        // This phrase is narrower — re-render it at the established max
        await osmdPhrase.reRenderAtContainerWidth(this.maxPhraseContainerWidth);
        if (expectedGeneration !== undefined && expectedGeneration !== this.phraseLoadGeneration) {
          osmdPhrase.destroy();
          return false;
        }
      }

      // Track cumulative measure count
      osmdPhrase.phraseStartingMeasure = this.measureCount;
      this.measureCount += osmdPhrase.notePositions.reduce(
        (maxMeasure, noteDataItem) => Math.max(maxMeasure, noteDataItem.measureNumber),
        0,
      );

      const phraseIndex = this.phrases.length;

      // Create cursor for this phrase
      this.createCursorForPhrase(osmdPhrase, phraseRaw, phraseData, phraseIndex);
      osmdPhrase.cursorVisible = this.cursorVisible;

      this.phrases.push(osmdPhrase);

      // Re-layout all phrases after each addition so they stack vertically
      // and don't overlap. Also re-create cursor dimensions after layout.
      if (!deferLayout) {
        this.layoutPhrases();
        this.updateCursorDimensions();
      }

      // Add events for this phrase to the event stream
      const osmdEventIterator = new OSMDIteratorExtensible(
        osmdPhrase,
        osmdPhrase.notePositions,
        osmdPhrase.phraseModel,
        osmdPhrase.data.timeSignature,
        osmdPhrase.data.startTimestamp,
      );
      this.eventStream.addEvents2(osmdEventIterator, osmdPhrase.phraseModel.getStartTimestamp());

      Logger.debug('[OSMD-MIDI] Scene addPhrase — events added to stream', {
        phraseUuid: phrase.uuid,
        startTimestamp: osmdPhrase.phraseModel.getStartTimestamp(),
        totalNotesInStream: this.eventStream.totalNotesLength(),
        eventStreamId: this.eventStream.id,
      });

      // Activate first phrase once it's available
      if (this.phrases.length === 1) {
        Logger.debug('[OSMDRepertoire] addPhrase — activating first phrase');
        this.activatePhrase(0);
      }

      Logger.log('[OSMDPlayableScene] addPhrase completed:', {
        uuid: phrase.uuid,
        startTimestamp: phrase.startTimestamp,
        phraseIndex,
        totalPhrasesNow: this.phrases.length,
      });
      return true;
    } catch (error) {
      if (osmdPhrase && !this.phrases.includes(osmdPhrase)) {
        try {
          osmdPhrase.destroy();
        } catch (cleanupError) {
          Logger.warn(
            `[OSMDPlayableScene] Failed to clean up phrase ${phrase.uuid} after a render failure`,
            cleanupError,
          );
        }
      }
      Logger.error('[OSMDPlayableScene] addPhrase failed:', error);
      if (rethrowError) throw error;
      return false;
    }
  }

  /**
   * Create a RepertoireCursor and OSMDIteratorExtensible for a phrase, then attach
   * the cursor to the phrase.
   */
  private createCursorForPhrase(
    osmdPhrase: OSMDPlayablePhrase,
    phraseRaw: Phrase,
    phraseData: RepertoirePhraseData,
    phraseIndex: number,
  ): void {
    const actualPhraseHeight = osmdPhrase.getScaledHeight() || osmdPhrase.getHeight();
    const cursorHeight = actualPhraseHeight * 1.35;
    const cursorWidth = cursorHeight / 10;
    const cursorY = actualPhraseHeight / 2 - cursorHeight / 2 + osmdPhrase.container.y;
    const firstNotePosition = osmdPhrase.getFirstNotePosition();

    const osmdIterator = new OSMDIteratorExtensible(
      osmdPhrase,
      osmdPhrase.notePositions,
      phraseRaw,
      phraseData.timeSignature,
      phraseData.startTimestamp,
    );

    const phraseCursor = new RepertoireCursor(
      this,
      firstNotePosition,
      cursorY,
      cursorWidth,
      cursorHeight,
      this.eventStream,
      osmdIterator,
      this.onPlayingLastSlice,
      this.onFinishPlayingPhrase,
      this,
      phraseData.startTimestamp > 0,
      phraseData.startTimestamp,
      phraseIndex,
      getScoreThemePalette(this.scoreTheme).playbackCursorNumber,
    );

    phraseCursor.reset(
      new OSMDIteratorExtensible(
        osmdPhrase,
        osmdPhrase.notePositions,
        phraseRaw,
        phraseData.timeSignature,
        phraseData.startTimestamp,
      ),
      phraseData.startTimestamp,
      osmdPhrase.phraseStartingMeasure,
    );

    if (this.mobileView) {
      phraseCursor.skipEndFade = true;
    }
    phraseCursor.setLastNotePos(firstNotePosition);
    phraseCursor.setX(firstNotePosition - cursorWidth / 2);
    phraseCursor.disable();

    // If cursor visibility is toggled off, mark the cursor as render-hidden
    // so it never flashes when enable() is later called by activate().
    Logger.info('[CURSOR-VIS] Scene createCursorForPhrase', {
      phraseIndex,
      cursorVisible: this.cursorVisible,
      willSetRenderHidden: !this.cursorVisible,
    });
    if (!this.cursorVisible) {
      phraseCursor.setRenderHidden(true);
    }

    osmdPhrase.cursor = phraseCursor;
  }

  // ---------------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------------

  /**
   * Position all phrases vertically with padding and gaps.
   * Computes a unified scale across all phrases so they share the same width,
   * then stacks them vertically and sets camera bounds to allow scrolling.
   */
  private layoutPhrases(): void {
    if (this.phrases.length === 0) return;

    const vpWidth = this.cameras.main.width;
    const vpHeight = this.cameras.main.height;
    const dpr = window.devicePixelRatio;

    // ---- Gather original dimensions ----
    let maxOriginalWidth = 0;
    let maxOriginalHeight = 0;
    for (const phrase of this.phrases) {
      maxOriginalWidth = Math.max(maxOriginalWidth, phrase.getOriginalWidth());
      maxOriginalHeight = Math.max(maxOriginalHeight, phrase.getOriginalHeight());
    }

    // ====================================================================
    // Mobile: horizontal layout — phrases placed end-to-end, camera scrolls X
    // ====================================================================
    if (this.mobileView) {
      const mobileScale = vpHeight / maxOriginalHeight / 3;

      // Apply scale (bestFitWithScale sets scale, _scaledWidth, _scaledHeight)
      // then override X positioning to chain phrases horizontally.
      for (const phrase of this.phrases) {
        phrase.bestFitWithScale(mobileScale, vpWidth);
      }

      const maxScaledHeight = this.phrases.reduce(
        (max, phrase) => Math.max(max, phrase.getScaledHeight()),
        0,
      );

      // Center the tallest phrase vertically, then align all other phrases
      // by their first staff line so the staves connect visually. Without
      // staff-line alignment, phrases with different amounts of content
      // above the staff (ledger lines, articulations) would have their
      // staves at different vertical positions.
      const centerY = (vpHeight - maxScaledHeight) / 2;
      const referenceStaffTop = this.phrases.reduce(
        (max, phrase) => Math.max(max, phrase.getFirstStaffLineYLocal()),
        0,
      );

      // Position phrases end-to-end using precise content edges.
      // Each phrase's container X is offset so its left content edge
      // (brace or first barline) aligns with the previous phrase's
      // right content edge (last barline).
      let contentRightEdge = MOBILE_LEFT_PADDING;
      for (const phrase of this.phrases) {
        const leftLocal = phrase.getContentLeftEdgeLocal();
        const containerX = contentRightEdge - leftLocal * mobileScale;
        const staffTopLocal = phrase.getFirstStaffLineYLocal();
        const phraseY = centerY + (referenceStaffTop - staffTopLocal) * mobileScale;
        phrase.setPosition(containerX, phraseY);
        const rightLocal = phrase.getContentRightEdgeLocal();
        contentRightEdge = containerX + rightLocal * mobileScale;
      }
      for (const phrase of this.phrases) {
        phrase.showAfterLayout();
      }

      // Stop scrolling once the last barline (+ small padding) is on screen.
      // maxScrollX = boundsWidth - vpWidth, and we want the viewport's
      // right edge to just reach contentRightEdge + rightPad, so:
      //   maxScrollX + vpWidth = contentRightEdge + rightPad
      //   boundsWidth = contentRightEdge + rightPad
      const rightPad = MOBILE_CURSOR_CAMERA_PADDING;
      const finalBoundsWidth = Math.max(contentRightEdge + rightPad, vpWidth);
      this.cameras.main.setBounds(0, 0, finalBoundsWidth, vpHeight);
      console.log(
        `[REP-CAM-FOLLOW] setBounds(0, 0, ${finalBoundsWidth.toFixed(0)}, ${vpHeight.toFixed(0)}) ` +
          `contentRightEdge=${contentRightEdge.toFixed(0)} vpWidth=${vpWidth.toFixed(0)} ` +
          `maxScrollX=${(finalBoundsWidth - vpWidth).toFixed(0)} padding=${rightPad}`,
      );

      // maxZoomLevel: the camera zoom at which the tallest phrase fills
      // the viewport vertically (with a small padding margin).
      const verticalPadding = 20 * window.devicePixelRatio;
      // The max zoom level here is a bit less than what is ideal.
      //this.maxZoomLevel =
      this.maxZoomLevel =
        (maxScaledHeight > 0 ? vpHeight / (maxScaledHeight + verticalPadding * 2) : 1) + 0.5;

      // Apply current zoom level to the freshly computed maxZoomLevel.
      // Immediate (no tween) so the layout and zoom are in sync.
      this.cameras.main.zoom = this.zoomLevel * this.maxZoomLevel;

      // Recompute the baseline gap (positions may have changed after relayout)
      // and immediately snap the camera to the cursor.
      this.computeMobileZoomBaseline();
      this.updateMobileCameraScroll();

      this.addPhraseDebugBounds();

      Logger.debug('[OSMDRepertoire] layoutPhrases (mobile)', {
        phrasesCount: this.phrases.length,
        mobileScale: mobileScale.toFixed(3),
        maxZoomLevel: this.maxZoomLevel.toFixed(3),
        cameraZoom: this.cameras.main.zoom.toFixed(3),
        totalWidth: Math.round(contentRightEdge),
        vpWidth,
        vpHeight,
      });
      return;
    }

    // ====================================================================
    // Desktop: vertical layout — phrases stacked vertically, camera scrolls Y
    // ====================================================================

    // ---- Content aspect ratio (used by both width and height constraints) ----
    const contentAspectRatio = maxOriginalWidth / maxOriginalHeight;
    const upperAspectRatio = 3.5;
    const lowerAspectRatio = 2.0;

    // ---- Width constraint ----
    // Normal pieces target 76% of viewport width. Tall/complex pieces
    // (whose wider OSMD container produces a large maxOriginalWidth) need
    // a wider target width so the scale factor is large enough to produce
    // readable notation. At the relaxed end (AR ≤ 2.0) the target is 92%
    // of viewport width. This is adaptive: simple pieces stay at 76%.
    const edgePadding = 100;
    const rawTargetWidth = vpWidth - edgePadding * 2;
    const baseTargetWidthFraction = 0.76;
    const relaxedTargetWidthFraction = 0.92;

    let targetWidthFraction: number;
    if (contentAspectRatio >= upperAspectRatio) {
      targetWidthFraction = baseTargetWidthFraction;
    } else if (contentAspectRatio <= lowerAspectRatio) {
      targetWidthFraction = relaxedTargetWidthFraction;
    } else {
      const widthInterpolationFactor =
        (upperAspectRatio - contentAspectRatio) / (upperAspectRatio - lowerAspectRatio);
      targetWidthFraction =
        baseTargetWidthFraction +
        widthInterpolationFactor * (relaxedTargetWidthFraction - baseTargetWidthFraction);
    }

    const phraseTargetWidth = Math.min(rawTargetWidth, vpWidth * targetWidthFraction);

    // ---- Height constraint: "2 phrases visible" baseline ----
    // For normal-aspect-ratio content, at least 2 complete phrases should
    // fit in the viewport. We derive phraseMaxHeight from the viewport
    // height budget:
    //
    //   vpHeight = topPadding + 2 * phraseMaxHeight + gap + bottomMargin
    //
    // Padding and gap are proportional to phrase height:
    //   phraseMaxHeight = vpHeight / (topPaddingFrac + 2 + gapFrac + bottomMarginFrac)
    const topPaddingFraction = 0.12;
    const gapFraction = 0.08;
    const bottomMarginFraction = 0.15;
    const heightBudgetDivisor = topPaddingFraction + 2 + gapFraction + bottomMarginFraction;
    const baselinePhraseMaxHeight = vpHeight / heightBudgetDivisor;

    // ---- Aspect ratio adaptation (height) ----
    // Same interpolation as the width constraint above, applied to the
    // height ceiling. Normal pieces use the baseline 2-phrase budget.
    // Tall pieces get a relaxed ceiling (95% of viewport height) so the
    // width constraint drives sizing — producing ~2 visible phrases
    // instead of 3.
    const relaxedMaxHeight = vpHeight * 0.95;

    let phraseMaxHeight: number;
    if (contentAspectRatio >= upperAspectRatio) {
      // Normal or wide content: 2-phrase budget applies
      phraseMaxHeight = baselinePhraseMaxHeight;
    } else if (contentAspectRatio <= lowerAspectRatio) {
      // Very tall content: fully relaxed — width constraint drives sizing
      phraseMaxHeight = relaxedMaxHeight;
    } else {
      // Transition zone: linearly interpolate between baseline and relaxed
      const interpolationFactor =
        (upperAspectRatio - contentAspectRatio) / (upperAspectRatio - lowerAspectRatio);
      phraseMaxHeight =
        baselinePhraseMaxHeight +
        interpolationFactor * (relaxedMaxHeight - baselinePhraseMaxHeight);
    }

    // ---- Compute unified scale ----
    const unifiedScaleX = phraseTargetWidth / maxOriginalWidth;
    const unifiedScaleY = phraseMaxHeight / maxOriginalHeight;
    const repertoireScaleFactor = 0.9; // slightly smaller than lesson view for comfortable reading
    const unifiedScale = Math.min(unifiedScaleX, unifiedScaleY) * repertoireScaleFactor;

    // Apply the unified scale and center horizontally
    for (const phrase of this.phrases) {
      phrase.bestFitWithScale(unifiedScale, vpWidth);
    }

    // ---- Vertical stacking ----
    // Use the actual scaled height (which varies per phrase when content
    // heights differ) for positioning. The gap is a fixed fraction of the
    // average scaled height so spacing stays visually consistent.
    const averageScaledHeight =
      this.phrases.reduce((total, phrase) => total + phrase.getScaledHeight(), 0) /
      this.phrases.length;
    // The Phaser canvas extends behind the React toolbar overlay. When
    // title/composer are present, we create the text objects early (off-screen)
    // so we can measure their ACTUAL rendered heights and compute an exact
    // minimum top padding that guarantees at least 20 CSS px of visible space
    // between the toolbar bottom and the title top.
    const hasTitleOrComposer = this.titleString.length > 0 || this.composerString.length > 0;
    const naturalTopPadding = averageScaledHeight * topPaddingFraction;
    let topPadding: number;
    if (hasTitleOrComposer) {
      // Create/update text objects off-screen so we can measure their heights.
      // updateTitleComposerPosition() will reposition them later.
      this.ensureTitleComposerText(dpr);

      const toolbarHeight = Math.round(112 * dpr); // React header is 110 CSS px + 2px safety (device px)
      const visibleMargin = Math.round(48 * dpr); // guaranteed visible gap above title (device px)
      // Actual measured element heights (device px):
      const titleHeight = this.titleText?.height ?? 0;
      const titleGap = this.titleString ? Math.round(16 * dpr) : 0;
      const composerHeight = this.composerText?.height ?? 0;
      const composerGap = this.composerString ? Math.round(12 * dpr) : 0;
      const bpmArea = Math.round(28 * dpr) + Math.round(4 * dpr); // BPM note + gap

      const minTopPadding =
        toolbarHeight +
        visibleMargin +
        titleHeight +
        titleGap +
        composerHeight +
        composerGap +
        bpmArea;
      topPadding = Math.max(naturalTopPadding, minTopPadding);
    } else {
      topPadding = naturalTopPadding;
    }
    const gap = Math.max(8 * dpr, averageScaledHeight * gapFraction);
    let currentY = topPadding;

    for (let phraseIdx = 0; phraseIdx < this.phrases.length; phraseIdx++) {
      const phrase = this.phrases[phraseIdx];
      phrase.setY(currentY);
      const scaledHeight = phrase.getScaledHeight();
      currentY += scaledHeight + gap;
    }
    for (const phrase of this.phrases) {
      phrase.showAfterLayout();
    }

    const totalContentHeight = currentY;

    // Position title/composer above the BPM indicator in world space
    this.updateTitleComposerPosition();

    // Position the BPM indicator above the first phrase
    this.updateBpmTextPosition();

    // Position the footer text below the last phrase in world space
    // (scrolls with the music — visible only at the bottom of the score)
    let footerBottomY = totalContentHeight;
    if (this.hasUnrenderedPhrases()) {
      this.educationalText?.setVisible(false);
    } else {
      footerBottomY = this.updateFooterOverlay(totalContentHeight, vpWidth);
      this.educationalText?.setVisible(true);
    }

    // Set camera bounds to include all content plus the footer
    this.cameras.main.setBounds(0, 0, vpWidth, Math.max(footerBottomY, vpHeight));

    this.addPhraseDebugBounds();

    Logger.debug('[OSMDRepertoire] layoutPhrases', {
      phrasesCount: this.phrases.length,
      maxOriginalWidth: Math.round(maxOriginalWidth),
      maxOriginalHeight: Math.round(maxOriginalHeight),
      contentAspectRatio: contentAspectRatio.toFixed(2),
      unifiedScale: unifiedScale.toFixed(3),
      scaleXWins: unifiedScaleX < unifiedScaleY,
      phraseMaxHeight: Math.round(phraseMaxHeight),
      baselinePhraseMaxHeight: Math.round(baselinePhraseMaxHeight),
      averageScaledHeight: Math.round(averageScaledHeight),
      totalContentHeight: Math.round(totalContentHeight),
      footerBottomY: Math.round(footerBottomY),
      vpWidth,
      vpHeight,
    });
  }

  /**
   * DEBUG: draw a bounds overlay for each phrase so phrase extents and any
   * clipping are visible. For every phrase we add, as CHILDREN of the phrase
   * container (so they share the exact transform and reveal any container-level
   * clipping/masking):
   *   - a translucent fill over the content bounds (left→right edge, full height)
   *   - a thick border stroked around those same bounds
   *   - a bright vertical line at the content right edge (the chain seam)
   *   - a magenta vertical line at the staff-line right edge (where the
   *     horizontal staff lines actually end)
   *   - an index label at the top-left
   * Toggle via DEBUG_PHRASE_BOUNDS.
   */
  private addPhraseDebugBounds(): void {
    // Clear any overlay from a previous layout pass.
    for (const obj of this.phraseDebugObjects) obj.destroy();
    this.phraseDebugObjects = [];

    if (!DEBUG_PHRASE_BOUNDS) return;

    this.phrases.forEach((phrase, i) => {
      const container = phrase.getContainer();
      const sx = container.scaleX || 1;
      const color = DEBUG_PHRASE_COLORS[i % DEBUG_PHRASE_COLORS.length];

      const leftLocal = phrase.getContentLeftEdgeLocal();
      const rightLocal = phrase.getContentRightEdgeLocal();
      const heightLocal = phrase.getOriginalHeight();
      const widthLocal = rightLocal - leftLocal;

      // Staff-line right edge in local coords (getStaffLineRightEdgeX is world).
      const staffRightWorld = phrase.getStaffLineRightEdgeX();
      const staffRightLocal = (staffRightWorld - container.x) / sx;

      // Line widths are in local units; divide by scale so they render at a
      // consistent on-screen thickness regardless of the phrase's scale.
      const border = 4 / sx;
      const marker = 6 / sx;

      const g = this.add.graphics();
      // Translucent fill over the content bounds.
      g.fillStyle(color, 0.1);
      g.fillRect(leftLocal, 0, widthLocal, heightLocal);
      // Thick border around the content bounds — if THIS gets clipped, the
      // clipping is at the container/camera level, not inside OSMD's render.
      g.lineStyle(border, color, 1);
      g.strokeRect(leftLocal, 0, widthLocal, heightLocal);
      // Content right edge (chain seam) — bright vertical line.
      g.lineStyle(marker, color, 1);
      g.lineBetween(rightLocal, -heightLocal * 0.1, rightLocal, heightLocal * 1.1);
      // Staff-line right edge — magenta vertical line.
      g.lineStyle(marker, 0xff00ff, 1);
      g.lineBetween(staffRightLocal, -heightLocal * 0.1, staffRightLocal, heightLocal * 1.1);
      g.setDepth(10000);
      container.add(g);
      this.phraseDebugObjects.push(g);

      // Index + edge label at top-left of the phrase (scaled up so it stays
      // legible after the container's downscale).
      const label = this.add.text(
        leftLocal,
        -heightLocal * 0.12,
        `P${i}  L=${Math.round(leftLocal)} R=${Math.round(rightLocal)} staffR=${Math.round(staffRightLocal)}`,
        {
          fontFamily: 'monospace',
          fontSize: `${Math.round(14 / sx)}px`,
          color: '#000000',
          backgroundColor: '#ffff00',
        },
      );
      label.setDepth(10001);
      container.add(label);
      this.phraseDebugObjects.push(label);
    });

    console.log(
      `[REP-DEBUG-BOUNDS] drew bounds for ${this.phrases.length} phrases ` +
        `(border+fill+contentRight+staffRight markers)`,
    );
  }

  /** Create or reposition the BPM indicator text above the first phrase. */
  private updateBpmTextPosition(): void {
    if (this.mobileView) return;
    if (this.phrases.length === 0) return;

    const scorePalette = getScoreThemePalette(this.scoreTheme);
    const firstPhrase = this.phrases[0];
    const phraseX = firstPhrase.getContainer().x;
    const phraseY = firstPhrase.getContainer().y;
    const dpr = window.devicePixelRatio;
    const noteFontSize = Math.round(28 * dpr);
    const valueFontSize = Math.round(22 * dpr);

    if (!this.bpmNoteText) {
      this.bpmNoteText = this.add.text(0, 0, '♩', {
        fontFamily: 'Times New Roman, serif',
        fontSize: `${noteFontSize}px`,
        color: scorePalette.notationColor,
      });
    } else {
      this.bpmNoteText.setColor(scorePalette.notationColor);
    }

    if (!this.bpmValueText) {
      this.bpmValueText = this.add.text(0, 0, '', {
        fontFamily: 'Times New Roman, serif',
        fontSize: `${valueFontSize}px`,
        color: scorePalette.notationColor,
      });
    } else {
      this.bpmValueText.setColor(scorePalette.notationColor);
    }

    const yPos = phraseY - noteFontSize - 4 * dpr;

    this.bpmNoteText.setFontSize(noteFontSize);
    // The note the tempo counts: a quarter in x/4 (as always), a dotted
    // quarter in 6/8, a half in 2/2 (see MeterTiming).
    this.bpmNoteText.setText(tempoBeatUnitOf(this.getScoreTimeSignature()).glyph);
    this.bpmNoteText.setPosition(phraseX, yPos);

    this.bpmValueText.setFontSize(valueFontSize);
    this.bpmValueText.setText(`= ${this.performanceTempo}`);
    const noteWidth = this.bpmNoteText.width;
    const baselineOffset = (noteFontSize - valueFontSize) * 0.5;
    this.bpmValueText.setPosition(phraseX + noteWidth - 4 * dpr, yPos + baselineOffset);
  }

  /**
   * Create or update the title and composer text objects (off-screen) so that
   * their rendered heights can be measured before layout decisions are made.
   * This is called by layoutPhrases() before computing topPadding, and again
   * by updateTitleComposerPosition() which handles final positioning.
   */
  private ensureTitleComposerText(dpr: number): void {
    if (this.mobileView) return;

    const scorePalette = getScoreThemePalette(this.scoreTheme);
    // ---- Title ----
    if (this.titleString) {
      const titleFontSize = Math.round(47 * dpr);
      if (!this.titleText) {
        this.titleText = this.add.text(-9999, -9999, this.titleString, {
          fontFamily: 'Lato, sans-serif',
          fontSize: `${titleFontSize}px`,
          fontStyle: 'bold',
          color: scorePalette.notationColor,
        });
        this.titleText.setOrigin(0.5, 1); // anchor at center-bottom
      } else {
        this.titleText.setFontSize(titleFontSize);
        this.titleText.setColor(scorePalette.notationColor);
      }
    }

    // ---- Composer ----
    if (this.composerString) {
      const composerFontSize = Math.round(17 * dpr);
      if (!this.composerText) {
        this.composerText = this.add.text(-9999, -9999, this.composerString, {
          fontFamily: 'Lato, sans-serif',
          fontSize: `${composerFontSize}px`,
          fontStyle: 'bold',
          color: scorePalette.musicMarkingColor,
          wordWrap: { width: Math.round(250 * dpr) },
        });
        this.composerText.setOrigin(1, 1); // anchor at right-bottom
      } else {
        this.composerText.setFontSize(composerFontSize);
        this.composerText.setColor(scorePalette.musicMarkingColor);
        this.composerText.setWordWrapWidth(Math.round(250 * dpr));
      }
    }
  }

  /**
   * Position the title and composer text in world space above the first phrase.
   * Both scroll with the music and disappear when the user scrolls past.
   *
   * The first phrase's Y coordinate is set by layoutPhrases() with enough top
   * padding to ensure the title lands below the toolbar overlay.
   *
   * Layout (top-down):
   *   [toolbar overlay area — not rendered]
   *   Title  (centered, below toolbar)
   *   Composer  (right-aligned below title)
   *   BPM indicator  (handled by updateBpmTextPosition)
   *   First phrase
   */
  private updateTitleComposerPosition(): void {
    if (this.mobileView) return;
    if (this.phrases.length === 0) return;
    if (!this.titleString && !this.composerString) return;

    const firstPhrase = this.phrases[0];
    const staffLineRightEdgeX = firstPhrase.getStaffLineRightEdgeX();
    const vpWidth = this.cameras.main.width;
    const dpr = window.devicePixelRatio;

    // Ensure text objects exist (they should already from layoutPhrases, but
    // this is safe to call again — it's a no-op if they already exist).
    this.ensureTitleComposerText(dpr);

    // The BPM indicator sits just above the first phrase. Title and composer
    // are stacked above the BPM, working upward from the BPM's top edge.
    const phraseY = firstPhrase.getContainer().y;
    const bpmFontSize = Math.round(28 * dpr);
    const bpmGapAbovePhrase = Math.round(4 * dpr);
    const bpmTopY = phraseY - bpmFontSize - bpmGapAbovePhrase;

    // Work upward from the BPM indicator to place composer then title
    const gapBelowComposer = Math.round(4 * dpr);
    let anchorBottomY = bpmTopY - gapBelowComposer;

    // ---- Composer (right-aligned flush with the right edge of the staff lines) ----
    if (this.composerText) {
      this.composerText.setPosition(staffLineRightEdgeX, anchorBottomY);
      anchorBottomY -= this.composerText.height + Math.round(48 * dpr);
    }

    // ---- Title (centered) ----
    if (this.titleText) {
      this.titleText.setPosition(vpWidth / 2, anchorBottomY);
    }
  }

  /**
   * Position the "For educational purposes only" text below the last phrase
   * in world space. The text scrolls with the music and is only visible when
   * the user scrolls to the bottom of the score.
   *
   * @param contentBottomY - The Y coordinate of the bottom edge of the last phrase (device pixels)
   * @param worldWidth - The full world/viewport width (device pixels)
   * @returns The bottom Y coordinate of the footer content (for camera bounds calculation)
   */
  private updateFooterOverlay(contentBottomY: number, worldWidth: number): number {
    if (this.mobileView) return contentBottomY;
    const scorePalette = getScoreThemePalette(this.scoreTheme);
    const dpr = window.devicePixelRatio;
    const footerTopPadding = Math.round(30 * dpr);
    const footerBottomPadding = Math.round(30 * dpr);
    const fontSize = Math.round(12 * dpr);
    const footerTextY = contentBottomY + footerTopPadding;

    if (!this.educationalText) {
      this.educationalText = this.add.text(
        worldWidth / 2,
        footerTextY,
        'For educational purposes only',
        {
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: `${fontSize}px`,
          fontStyle: 'italic',
          color: scorePalette.footerTextColor,
        },
      );
      this.educationalText.setOrigin(0.5, 0);
    } else {
      this.educationalText.setPosition(worldWidth / 2, footerTextY);
      this.educationalText.setFontSize(fontSize);
      this.educationalText.setColor(scorePalette.footerTextColor);
    }

    // Return the bottom edge of the footer for camera bounds calculation
    return footerTextY + this.educationalText.height + footerBottomPadding;
  }

  // ---------------------------------------------------------------------------
  // Scrolling and phrase activation
  // ---------------------------------------------------------------------------

  /**
   * Smoothly scroll the camera to bring the given phrase into view.
   * Does NOT change activation — caller is responsible for activating/deactivating.
   */
  /**
   * Scroll the camera to show all phrases within the loop bracket range.
   * Positions the first bracket phrase just below the header. If all
   * bracket phrases fit in the viewport, no further scrolling is needed
   * during playback. Called when looping activates or the bracket changes.
   */
  /**
   * Check whether the given phrase is fully visible in the camera viewport,
   * using the full container height (including any padding). This is the
   * per-line visibility test used to gate auto-scroll during playback —
   * if any pixel of the active phrase is clipped, return false so the
   * caller can scroll to bring it fully into frame.
   */
  private isPhraseFullyVisible(phraseIndex: number): boolean {
    const phrase = this.phrases[phraseIndex];
    if (!phrase) return true;
    const camera = this.cameras.main;
    const phraseTop = phrase.getContainer().y;
    const phraseBottom = phraseTop + phrase.getScaledHeight();
    return phraseTop >= camera.scrollY && phraseBottom <= camera.scrollY + camera.height;
  }

  /**
   * Find the timestamp range of notes that fit on the mobile screen for
   * default bracket placement when looping is activated.
   *
   * Mobile-only. On desktop/iPad, default placement uses session storage
   * or a fixed 4-bar range (see calculateDefaultBracketPlacement).
   *
   * Algorithm: find the first note on or past the viewport's left edge,
   * then walk forward note-by-note, checking each note's screen pixel
   * position. Stop when the next note's bracket right edge (barline or
   * note X + padding) would extend past the viewport's right edge. This
   * accounts for the left-side container-edge extension (clef/time sig)
   * eating into available width.
   */
  private getVisibleNoteTimestampRange(
    noteData: BracketNoteData,
  ): { startTimestamp: number; endTimestamp: number } | null {
    const camera = this.cameras.main;
    // The Phaser canvas may be wider than the visible screen (e.g. canvas
    // is 960 CSS px but the phone viewport is only 667 CSS px). Use the
    // actual visible portion of the canvas to compute the world-space
    // viewport edges, otherwise the bracket extends into the clipped area.
    const canvasRect = this.game.canvas.getBoundingClientRect();
    const visibleRight = Math.min(canvasRect.right, window.innerWidth);
    const visibleFraction =
      canvasRect.width > 0 ? (visibleRight - Math.max(canvasRect.left, 0)) / canvasRect.width : 1;
    const effectiveCameraWidth = camera.width * visibleFraction;
    const viewLeftWorld = camera.getWorldPoint(0, 0).x;
    const rawViewRight = camera.getWorldPoint(effectiveCameraWidth, 0).x;
    // Reserve space for the end handle circle so it doesn't poke past the
    // viewport edge. Handle radius ≈ 18 * devicePixelRatio * 0.55 (mobile).
    const handleBuffer = 18 * (window.devicePixelRatio ?? 1) * 0.55;
    const viewRightWorld = rawViewRight - handleBuffer;

    // Collect all non-rest notes with world X, sorted left to right.
    const allNotes: Array<{
      timestamp: number;
      worldX: number;
      phraseIdx: number;
    }> = [];
    for (let pi = 0; pi < this.phrases.length; pi++) {
      const phrase = this.phrases[pi];
      const container = phrase.getContainer();
      const scale = container.scaleX;
      for (const notePos of phrase.notePositions) {
        if (notePos.isRest) continue;
        allNotes.push({
          timestamp: notePos.timestamp,
          worldX: container.x + notePos.x * scale,
          phraseIdx: pi,
        });
      }
    }

    if (allNotes.length < 2) return null;
    allNotes.sort((a, b) => a.worldX - b.worldX);

    // Find the first note whose worldX is at or past the viewport left.
    let startIdx = allNotes.findIndex((n) => n.worldX >= viewLeftWorld);
    if (startIdx === -1) startIdx = allNotes.length - 1;

    // Collect all barline positions across phrases for right-edge estimation.
    const allBarlines: number[] = [];
    for (const phrase of this.phrases) {
      allBarlines.push(...phrase.getMeasureBarlineXPositions());
    }
    allBarlines.sort((a, b) => a - b);

    // Select all notes whose worldX is within the viewport.
    let endIdx = startIdx;
    for (let i = startIdx + 1; i < allNotes.length; i++) {
      if (allNotes[i].worldX > viewRightWorld) break;
      endIdx = i;
    }

    // Trim from the right until the bracket's actual right edge fits on
    // screen. computeBracketRows finds the first excluded note by
    // TIMESTAMP (not worldX), so we must do the same: compute the
    // endTimestamp for the current selection, find the first note with
    // onset >= endTimestamp, and use its worldX to estimate the bracket
    // right edge.
    const bracketPadding = 20;

    // All notes sorted by timestamp for excluded-note lookup.
    const notesByTimestamp = [...allNotes].sort((a, b) => a.timestamp - b.timestamp);

    while (endIdx > startIdx) {
      // Compute endTimestamp for current selection (offset of last note by timestamp).
      const selected = allNotes.slice(startIdx, endIdx + 1);
      selected.sort((a, b) => a.timestamp - b.timestamp);
      const lastOnsetTs = selected[selected.length - 1].timestamp;
      const endTs = noteData.maxOffsetByOnset.get(lastOnsetTs) ?? lastOnsetTs;

      // Find the first excluded note by timestamp (mirrors computeBracketRows).
      const firstExcluded = notesByTimestamp.find((n) => n.timestamp >= endTs);
      const lastIncludedX = Math.max(...selected.map((n) => n.worldX));

      let bracketRightEdge: number;
      if (firstExcluded) {
        const barlineInGap = allBarlines.find(
          (bx) => bx > lastIncludedX && bx < firstExcluded.worldX,
        );
        bracketRightEdge = barlineInGap ?? firstExcluded.worldX - bracketPadding;
      } else {
        const barlineAfter = allBarlines.find((bx) => bx > lastIncludedX);
        bracketRightEdge = barlineAfter ?? lastIncludedX + bracketPadding;
      }

      console.log(
        `[VISIBLE-RANGE] trim endIdx=${endIdx} lastIncX=${lastIncludedX.toFixed(0)} endTs=${endTs} excludedX=${firstExcluded?.worldX.toFixed(0) ?? 'none'} bracketR=${bracketRightEdge.toFixed(0)} viewR=${viewRightWorld.toFixed(0)} fits=${bracketRightEdge <= viewRightWorld}`,
      );

      if (bracketRightEdge <= viewRightWorld) break;
      endIdx--;
    }

    if (startIdx === endIdx) {
      if (endIdx < allNotes.length - 1) endIdx++;
      else if (startIdx > 0) startIdx--;
      else return null;
    }

    const selectedNotes = allNotes.slice(startIdx, endIdx + 1);
    selectedNotes.sort((a, b) => a.timestamp - b.timestamp);
    const firstOnset = selectedNotes[0].timestamp;
    const lastOnset = selectedNotes[selectedNotes.length - 1].timestamp;
    const lastOffset = noteData.maxOffsetByOnset.get(lastOnset) ?? lastOnset;

    const cam = this.cameras.main;
    console.log(
      `[VISIBLE-RANGE] viewL=${viewLeftWorld.toFixed(0)} viewR=${viewRightWorld.toFixed(0)} selected ${selectedNotes.length} notes startTs=${firstOnset} endTs=${lastOffset}`,
    );
    console.log(
      `[VISIBLE-RANGE] camera: scrollX=${cam.scrollX.toFixed(1)} zoom=${cam.zoom.toFixed(3)} width=${cam.width} height=${cam.height} worldView={x:${cam.worldView.x.toFixed(1)} y:${cam.worldView.y.toFixed(1)} w:${cam.worldView.width.toFixed(1)} h:${cam.worldView.height.toFixed(1)}}`,
    );
    const logRect = this.game.canvas.getBoundingClientRect();
    console.log(
      `[VISIBLE-RANGE] canvas: w=${this.game.canvas.width} h=${this.game.canvas.height} cssW=${this.game.canvas.style.width} cssH=${this.game.canvas.style.height} dpr=${window.devicePixelRatio}`,
    );
    console.log(
      `[VISIBLE-RANGE] screen: innerW=${window.innerWidth} innerH=${window.innerHeight} canvasRect={left:${logRect.left.toFixed(0)} right:${logRect.right.toFixed(0)} width:${logRect.width.toFixed(0)}} visibleCanvasW=${(Math.min(logRect.right, window.innerWidth) - Math.max(logRect.left, 0)).toFixed(0)} visFrac=${visibleFraction.toFixed(3)}`,
    );

    return { startTimestamp: firstOnset, endTimestamp: lastOffset };
  }

  /**
   * Check whether all phrases in the bracket range are fully visible
   * in the current camera viewport. Used only by the bracket-overlay
   * activation path (setLoopingActive) to decide whether the initial
   * loop-range anchor scroll is needed.
   */
  private isBracketRangeFullyVisible(): boolean {
    if (!this.bracketOverlay) return true;
    const bracketStart = this.bracketOverlay.getStartTimestamp();
    const bracketEnd = this.bracketOverlay.getEndTimestamp();

    const camera = this.cameras.main;
    const viewTop = camera.scrollY;
    const viewBottom = camera.scrollY + camera.height;

    // Find the top of the first bracket phrase and the bottom of the last.
    let rangeTop = Infinity;
    let rangeBottom = -Infinity;

    for (const phrase of this.phrases) {
      const nonRest = phrase.notePositions.filter((n) => !n.isRest);
      if (nonRest.length === 0) continue;
      const phraseMin = nonRest[0].timestamp;
      const phraseMax = nonRest[nonRest.length - 1].timestamp;
      if (phraseMax < bracketStart || phraseMin > bracketEnd) continue;

      const container = phrase.getContainer();
      rangeTop = Math.min(rangeTop, container.y);
      // Use the top of the phrase + a fixed staff height rather than the
      // full container height, which includes bottom padding/whitespace.
      // The notes sit in roughly the top half of the container.
      rangeBottom = Math.max(rangeBottom, container.y + phrase.getScaledHeight() * 0.6);
    }

    if (rangeTop === Infinity) return true;
    return rangeTop >= viewTop && rangeBottom <= viewBottom;
  }

  private scrollToShowBracketRange(): void {
    if (!this.bracketOverlay) return;
    const bracketStart = this.bracketOverlay.getStartTimestamp();

    // Find the first phrase that overlaps with the bracket
    const firstBracketPhraseIdx = this.phrases.findIndex((phrase) => {
      const nonRest = phrase.notePositions.filter((n) => !n.isRest);
      if (nonRest.length === 0) return false;
      const phraseMax = nonRest[nonRest.length - 1].timestamp;
      return phraseMax >= bracketStart;
    });
    if (firstBracketPhraseIdx === -1) return;

    this.scrollToPhraseSmooth(firstBracketPhraseIdx);
  }

  private scrollToPhraseSmooth(phraseIndex: number): void {
    if (this.mobileView) return;
    const targetPhrase = this.phrases[phraseIndex];
    if (!targetPhrase) return;

    const camera = this.cameras.main;
    const cameraBounds = camera.getBounds();
    const maxScrollY = Math.max(0, cameraBounds.height - camera.height);

    let clampedY: number;
    if (phraseIndex === 0 && !this.isLoopingActive) {
      // Non-loop first phrase: keep the title/composer visible by staying
      // at the top. The initial layout already positions everything below
      // the toolbar.
      clampedY = 0;
    } else {
      // Subsequent phrases — and the first phrase during looping — scroll
      // so the phrase appears just below the toolbar overlay (110 CSS px)
      // with some breathing room. During looping the title isn't relevant;
      // the loop line is what should sit at the top of the viewport.
      const dpr = window.devicePixelRatio;
      const toolbarClearance = Math.round(120 * dpr); // toolbar (110 CSS px) + 10px padding
      const idealY = targetPhrase.getContainer().y - toolbarClearance;
      clampedY = Phaser.Math.Clamp(idealY, 0, maxScrollY);
    }

    Logger.debug('[OSMDRepertoire] scrollToPhraseSmooth called', {
      phraseIndex,
      clampedY,
      maxScrollY,
      currentScrollY: camera.scrollY,
      playbackStarted: this.playbackStarted,
      shouldUpdate: this.shouldUpdate,
      stack: new Error().stack?.split('\n').slice(1, 5).join(' <- '),
    });

    this.isAutoScrolling = true;
    this.tweens.add({
      targets: camera,
      scrollY: clampedY,
      duration: 400,
      ease: 'Cubic.easeInOut',
      onComplete: () => {
        this.isAutoScrolling = false;
      },
    });
  }

  /**
   * Capture the world-space gap between the cursor center and the left
   * content edge of the first phrase. Called once after the initial layout
   * and whenever phrases are re-laid-out (e.g. on resize).
   */
  private computeMobileZoomBaseline(): void {
    if (!this.mobileView) return;
    const firstPhrase = this.phrases[0];
    if (!firstPhrase?.cursor) return;
    const cursorWorldX = firstPhrase.cursor.x + firstPhrase.cursor.width / 2;
    const container = firstPhrase.getContainer();
    const leftEdgeWorld = container.x + firstPhrase.getContentLeftEdgeLocal() * container.scaleX;
    this.mobileZoomBaselineGap = cursorWorldX - leftEdgeWorld + CURSOR_FOLLOW_EXTRA_PADDING_LEFT;
  }

  /**
   * Set camera.scrollX so the active cursor sits at a fixed **world-space**
   * distance ({@link mobileZoomBaselineGap}) from the left viewport edge.
   *
   * Because the padding is in world units, the brace/left-edge of the first
   * phrase stays visible regardless of zoom: at higher zoom the world-space
   * gap maps to more screen pixels, at lower zoom it maps to fewer, but the
   * same amount of content is always between the viewport edge and the cursor.
   *
   * Direct, stateless formula derived from the Phaser camera transform
   * (center-origin):  screenX = (worldX - scrollX - w/2) * zoom + w/2
   * Solving for scrollX with a world-space gap G:
   *   scrollX = cursorWorldX - G - w/2 + w/(2*zoom)
   *
   * No-op on desktop (Y-scroll layout) or before baseline is captured.
   */
  private updateMobileCameraScroll(): void {
    if (!this.mobileView || this.mobileZoomBaselineGap === undefined) return;
    // While the user has manually drag-scrolled the view, keep the camera
    // where they left it. Cleared on the next playback start (startExercise).
    if (this.mobileUserScrolledX) return;
    const cursor = this.phrases[this.activePhrase]?.cursor;
    if (!cursor) return;
    const cursorWorldX = cursor.x + cursor.width / 2;
    const targetScrollX = this.mobileScrollXForCursorWorldX(cursorWorldX);
    const currentScrollX = this.cameras.main.scrollX;
    const lerpFactor = 0.15;
    this.cameras.main.scrollX = currentScrollX + (targetScrollX - currentScrollX) * lerpFactor;
  }

  /**
   * Compute camera.scrollX such that a cursor at the given world X sits at
   * the same fixed gap from the left viewport edge as during cursor-follow.
   * Mirrors the formula in {@link updateMobileCameraScroll}.
   */
  private mobileScrollXForCursorWorldX(cursorWorldX: number): number {
    const camera = this.cameras.main;
    const halfW = camera.width / 2;
    const gap = this.mobileZoomBaselineGap ?? 0;
    return cursorWorldX - gap - halfW + halfW / camera.zoom;
  }

  /**
   * Min/max camera.scrollX for manual horizontal drag-scroll on mobile.
   * Bounds match the range produced by cursor-follow as the cursor
   * traverses from the first note of the first phrase to the last note
   * of the last phrase, so dragging can reach every position cursor-follow
   * can reach (and no further).
   */
  private getMobileScrollXBounds(): { min: number; max: number } {
    const firstPhrase = this.phrases[0];
    const lastPhrase = this.phrases[this.phrases.length - 1];
    if (!firstPhrase || !lastPhrase) return { min: 0, max: 0 };

    const firstNoteWorldX = firstPhrase.getFirstNotePosition();
    const lastContainer = lastPhrase.getContainer();
    const nonRestNotes = lastPhrase.notePositions.filter((n) => !n.isRest);
    const lastNoteWorldX =
      nonRestNotes.length > 0
        ? nonRestNotes.reduce((maxN, n) => (n.x > maxN.x ? n : maxN), nonRestNotes[0]).x *
            lastContainer.scaleX +
          lastContainer.x
        : lastContainer.x + lastPhrase.getContentRightEdgeLocal() * lastContainer.scaleX;

    return {
      min: this.mobileScrollXForCursorWorldX(firstNoteWorldX),
      max: this.mobileScrollXForCursorWorldX(lastNoteWorldX),
    };
  }

  /**
   * Check whether the active phrase is visible in the camera viewport.
   * If it is out of view, smoothly scroll to bring it on screen.
   * Called on noteOn so the view follows the player during playback.
   */
  private ensureActivePhraseVisible(): void {
    // On mobile the cursor drives camera.scrollX directly — no scene-level
    // scroll management needed.
    if (this.mobileView) return;

    // If a programmatic scroll tween is running OR we're in the gap between
    // the tween completing and onFinishPlayingPhrase updating activePhrase,
    // don't fight it by scrolling back to the (possibly stale) activePhrase.
    if (this.isAutoScrolling || this.pendingPhraseTransition !== null) return;
    if (!this.phrases[this.activePhrase]) return;

    // Per-line heuristic: if the active phrase isn't fully in frame,
    // scroll to bring it down. Applies during both looping and non-looping
    // playback. Previously this was gated by isBracketRangeFullyVisible()
    // during loops, which uses a smaller staff-only height calculation and
    // could report "fully visible" while the active phrase was actually
    // clipped — suppressing the scroll the user expected to see.
    if (this.isPhraseFullyVisible(this.activePhrase)) return;

    this.scrollToPhraseSmooth(this.activePhrase);
  }

  /**
   * Deactivate all phrases, then activate the phrase at the given index.
   * @param index - The phrase index to activate
   * @param unpause - If true, also unpause the cursor (start it running).
   *                  Defaults to false to prevent cursors from running during initial loading.
   */
  private activatePhrase(index: number, unpause: boolean = false): void {
    for (const phrase of this.phrases) {
      phrase.deactivate();
    }
    this.activePhrase = index;
    const targetPhrase = this.phrases[index];
    if (targetPhrase) {
      targetPhrase.activate(unpause);
    }
    this.updateMobileCameraScroll();
    if (shouldPrefetchRepertoireForPlayback(index, this.phrases.length, this.totalPhraseCount)) {
      void this.requestNextPhraseBatch('playback');
    }
  }

  /**
   * Show or hide all cursors in response to the user toggling the cursor
   * setting. Uses `RepertoireCursor.setRenderHidden()` which forces alpha
   * to 0 and suppresses all visual operations (blink, fade-in, fade-out)
   * while the cursor continues tracking position internally.
   */
  setCursorVisible({ visible }: { visible: boolean }): void {
    Logger.info('[CURSOR-VIS] Scene setCursorVisible received', {
      visible,
      phraseCount: this.phrases.length,
      cursorsToUpdate: this.phrases.filter((phrase) => phrase.cursor).length,
    });
    this.cursorVisible = visible;
    for (const phrase of this.phrases) {
      phrase.cursorVisible = visible;
      if (phrase.cursor) {
        phrase.cursor.setRenderHidden(!visible);
      }
    }
  }

  /**
   * Set the user-controlled zoom level via camera zoom (no relayout).
   * The factor is clamped to [0.5, 1.0] and the actual camera zoom is
   * factor * maxZoomLevel — matching the SightReadingPhaser approach.
   */
  setZoomLevel = ({ zoomLevel }: { zoomLevel: number }): void => {
    if (zoomLevel === undefined) return;
    const clamped = Math.max(0.5, Math.min(zoomLevel, 1));
    Logger.debug('[OSMDRepertoire] setZoomLevel', { factor: clamped, maxZoom: this.maxZoomLevel });
    this.zoomLevel = clamped;

    this.zoomTween?.stop();
    this.zoomTween = this.tweens.add({
      targets: this.cameras.main,
      zoom: clamped * this.maxZoomLevel,
      duration: 300,
      ease: 'Power2',
      onUpdate: () => {
        if (this.mobileView) {
          this.updateMobileCameraScroll();
        }
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Phrase completion callbacks
  // ---------------------------------------------------------------------------

  /**
   * Called by RepertoireCursor when the cursor reaches the last slice of the current phrase.
   * Pre-positions the next phrase's cursor for a seamless transition.
   *
   * Invoked by the cursor via iterator.next()'s onLastSlice?.bind(ctx)(), which
   * binds `this` explicitly at the call site, so a prototype method receives the
   * correct context.
   */
  onPlayingLastSlice(_start: number, _duration: number, _durationInSecs: number): void {
    console.log(
      `[LOOP-DBG] onPlayingLastSlice called isLoopingActive=${this.isLoopingActive} activePhrase=${this.activePhrase}`,
    );
    if (this.isLoopingActive) {
      // When looping, allow phrase transitions only if any part of the next
      // phrase lies inside the loop. Use the phrase's startTimestamp rather
      // than its first non-rest note: when a bracket ends exactly on the
      // next phrase's first note (e.g. loop "full line 1 + N beats into
      // line 2" where line 2's first note falls on the boundary), the
      // first-note check makes `firstNote == effectiveLoopEnd` and the `>=`
      // guard wrongly suppresses the transition, leaving the cursor on the
      // finished phrase to fade to alpha 0 over the rest of the iteration.
      const nextIndex = this.activePhrase + 1;
      const nextPhraseStart =
        nextIndex < this.phrases.length ? this.phrases[nextIndex].data.startTimestamp : Infinity;
      if (nextPhraseStart >= this.effectiveLoopEndTimestamp) {
        console.log(
          `[LOOP-DBG] onPlayingLastSlice — suppressed (next phrase ${nextIndex} start=${nextPhraseStart} >= effectiveLoopEnd=${this.effectiveLoopEndTimestamp})`,
        );
        return;
      }
      console.log(
        `[LOOP-DBG] onPlayingLastSlice — allowing transition to phrase ${nextIndex} (start=${nextPhraseStart} < effectiveLoopEnd=${this.effectiveLoopEndTimestamp})`,
      );
    }

    const nextIndex = this.activePhrase + 1;
    if (nextIndex < this.phrases.length) {
      const nextPhrase = this.phrases[nextIndex];
      const firstNotePos = nextPhrase.getFirstNotePosition();

      // If the phrase starts with rests, position the cursor at the
      // container's left edge so the count-in lerp moves left → right
      // (coming in from the left). Without this, the cursor starts at
      // the first note and lerps backward toward the rest position.
      const phraseStartsWithRest =
        nextPhrase.notePositions.length > 0 && nextPhrase.notePositions[0].isRest;
      const countInStartX = phraseStartsWithRest
        ? nextPhrase.getContainer().x
        : firstNotePos - (nextPhrase.cursor?.width ?? 0);

      // Reset the next phrase's cursor iterator before it counts in. After a
      // full (non-loop) playthrough every phrase's iterator is left at
      // endReached=true; once this cursor becomes `running`, update()'s
      // end-of-phrase fade-out branch fires immediately (EndReached true while
      // the local timestamp is near the phrase START), driving alpha to
      // out-of-range / NaN values and flashing the cursor — and left()
      // returning the phrase-end X corrupts its position. restartLoop() already
      // resets in-bracket phrases, but only at the loop boundary — i.e. AFTER
      // this first transition — so the first pass into the phrase needs its own
      // reset here. On a genuinely fresh phrase (first non-loop playthrough)
      // this is a no-op since the iterator is already at its start.
      const nextIter = nextPhrase.cursor?.Iterator as unknown as {
        resetIterator?: () => void;
        lastPlayedMeasure?: number;
        shouldIncrementMeasureIndex?: boolean;
      };
      if (nextIter?.resetIterator) {
        nextIter.resetIterator();
        nextIter.lastPlayedMeasure = 0;
        nextIter.shouldIncrementMeasureIndex = false;
      }

      nextPhrase.cursor?.setX(countInStartX);
      nextPhrase.activate();
      nextPhrase.cursor?.startCountingIn(_start, _duration, _duration);

      // Mobile horizontal layout: phrases are connected end-to-end, so
      // the sliding-in second cursor is visually distracting. Hide it
      // during the count-in; onFinishPlayingPhrase will reveal it.
      // Also tell the current cursor to animate toward the next phrase's
      // first note instead of the end barline, so the transition is seamless.
      if (this.mobileView) {
        nextPhrase.cursor?.setRenderHidden(true);
        const currentCursor = this.phrases[this.activePhrase]?.cursor;
        if (currentCursor) {
          // Cursor and firstNotePos are both in world coordinates.
          currentCursor.endTargetOverrideX = firstNotePos;
        }
      }

      // Mark the transition as pending. This prevents ensureActivePhraseVisible
      // from scrolling back to the old activePhrase during the gap between the
      // scroll tween completing and onFinishPlayingPhrase updating activePhrase.
      this.pendingPhraseTransition = nextIndex;

      // Scroll to the next phrase while the current phrase's cursor is
      // still fading out, so the next line is visible before the user has
      // to play it. Non-loop playback always advances the view one line at
      // a time (the original behavior — keeps each new active line at the
      // top of the viewport for maximum lookahead). Loop playback only
      // scrolls when the next phrase doesn't fully fit — covers the 1- and
      // 2-line loop cases where everything is visible and no auto-scroll
      // is wanted.
      if (!this.isLoopingActive || !this.isPhraseFullyVisible(nextIndex)) {
        this.scrollToPhraseSmooth(nextIndex);
      }
    } else if (this.hasUnrenderedPhrases() || this.lazyPhraseLoadPromise) {
      // This is only the end of the currently rendered window. Start (or
      // join) the next batch and leave pieceCompleted false.
      void this.requestNextPhraseBatch('boundary');
    } else {
      // Last phrase — mark piece as completing early so the upcoming
      // PauseExercise (from the context's onCalcTheEnd scheduler timeout)
      // skips cursor repositioning. The scheduler timeout fires BEFORE
      // the cursor's update() detects totalDuration exceeded, so we
      // must set the flag here rather than in onFinishPlayingPhrase.
      Logger.info('[OSMDRepertoire] onPlayingLastSlice — last phrase, setting pieceCompleted');
      this.pieceCompleted = true;
    }
  }

  /**
   * Called by RepertoireCursor when the active phrase finishes playing.
   * Linearly advances to the next phrase, or finishes if this was the last.
   *
   * Invoked by the cursor via onFinishPlaying?.call(callbackScope), which sets
   * `this` explicitly at the call site, so a prototype method receives the
   * correct context.
   */
  onFinishPlayingPhrase(): void {
    console.log(
      `[LOOP-DBG] onFinishPlayingPhrase called isLoopingActive=${this.isLoopingActive} activePhrase=${this.activePhrase} pieceCompleted=${this.pieceCompleted}`,
    );
    if (this.isLoopingActive) {
      // Only transition to the next phrase if any part of it lies inside the
      // loop. Use the phrase's startTimestamp, not its first non-rest note —
      // when a bracket ends exactly at the next phrase's first playable note
      // (e.g. loop "full line 1 + 2 beats into line 2" where line 2's first
      // note happens to fall on the loop boundary), the first-note check
      // wrongly returns `firstNote == effectiveLoopEnd` and the `>=` guard
      // suppresses the transition. The cursor then stays on the finished
      // phrase, whose per-frame end-of-phrase fade-out drives alpha to 0 —
      // leaving line 2 with no visible cursor for the rest of the iteration.
      // startTimestamp correctly answers "does the next phrase visually
      // belong inside this loop?".
      const nextIndex = this.activePhrase + 1;
      const nextPhraseStart =
        nextIndex < this.phrases.length ? this.phrases[nextIndex].data.startTimestamp : Infinity;
      if (nextPhraseStart >= this.effectiveLoopEndTimestamp) {
        console.log(
          `[LOOP-DBG] onFinishPlayingPhrase — suppressed (next phrase ${nextIndex} start=${nextPhraseStart} >= effectiveLoopEnd=${this.effectiveLoopEndTimestamp})`,
        );
        return;
      }
      console.log(
        `[LOOP-DBG] onFinishPlayingPhrase — allowing transition to phrase ${nextIndex} (start=${nextPhraseStart} < effectiveLoopEnd=${this.effectiveLoopEndTimestamp})`,
      );
    }

    const finishedIndex = this.activePhrase;

    if (
      finishedIndex === this.phrases.length - 1 &&
      (this.hasUnrenderedPhrases() || this.lazyPhraseLoadPromise)
    ) {
      this.pendingPhraseTransition = null;
      this.waitingForLazyPhraseAdvance = true;
      void this.requestNextPhraseBatch('boundary');
      return;
    }

    // Clear the pending transition flag — activePhrase is about to be updated,
    // so ensureActivePhraseVisible will read the correct value from now on.
    this.pendingPhraseTransition = null;

    // Suppress phrase-completed analytics events during looping. Repeated
    // playback of the same phrase within a loop should not generate per-phrase
    // completion records — only the periodic RepertoirePlayingEvent (which
    // carries elapsed play time) should continue to fire.
    //
    // The LAST phrase's emission is deferred to onFinishAllPhrases so the
    // scheduler-timeout path (_pauseExercise with pieceCompleted=true) — which
    // bypasses this function — also gets it. Without that deferral, the last
    // phrase never produced a rep_phrase_completed event in practice, because
    // _pauseExercise fires before the cursor detects currentTimestamp >=
    // totalDuration.
    const isLastPhrase = finishedIndex === this.phrases.length - 1;
    if (!this.isLoopingActive && !isLastPhrase) {
      NotifyReactEvents.RepertoirePhraseCompleted({ phraseIndex: finishedIndex });
    } else if (this.isLoopingActive) {
      console.log(
        `[NOTE-REGISTER-DBG] onFinishPlayingPhrase — suppressing RepertoirePhraseCompleted event during looping (finishedIndex=${finishedIndex})`,
      );
    }

    const nextIndex = finishedIndex + 1;
    if (nextIndex < this.phrases.length) {
      // Normal mid-piece transition: deactivate finished phrase, activate next.
      // Scrolling already started in onPlayingLastSlice (when the cursor
      // reached the last note), so we don't scroll again here.
      if (this.mobileView) {
        // Mobile horizontal layout: keep the transition minimal to avoid
        // visible cursor jumps. The old cursor stays at its final position
        // (skipEndFade keeps it opaque) and the new cursor starts from its
        // first note. The camera lerp smooths any positional gap.
        this.activePhrase = nextIndex;
        // Reveal the transition cursor only when the user's cursor setting
        // is enabled. Hard-coding false here made the cursor reappear on
        // phones after the first phrase even when it had been toggled off.
        this.phrases[nextIndex]?.cursor?.setRenderHidden(!this.cursorVisible);
        this.phrases[nextIndex]?.activate(true);
        // Deactivate the old cursor AFTER the new one is active so there's
        // no frame without a visible cursor.
        this.phrases[finishedIndex]?.deactivate();
      } else {
        this.phrases[finishedIndex]?.deactivate();
        this.activePhrase = nextIndex;
        this.phrases[nextIndex]?.activate(true);
        this.updateMobileCameraScroll();
      }
      if (
        shouldPrefetchRepertoireForPlayback(
          this.activePhrase,
          this.phrases.length,
          this.totalPhraseCount,
        )
      ) {
        void this.requestNextPhraseBatch('playback');
      }
    } else {
      // Last phrase: fade the cursor out gracefully instead of snapping it off.
      // The cursor's own fade-out logic (in RepertoireCursor.update) has
      // already been reducing alpha as it approaches the end; this tween
      // continues from whatever alpha remains and brings it to 0.
      this.fadeOutLastCursor(finishedIndex);
      this.onFinishAllPhrases();
    }
  }

  /**
   * Fade the cursor of the finished phrase out over 600ms instead of
   * instantly hiding it. Stops the phrase from updating (isActive = false)
   * and pauses the cursor so no further position changes occur, but keeps
   * the cursor visible while the alpha tweens to 0.
   *
   * Idempotent: safe to call multiple times (from onFinishPlayingPhrase
   * and from _pauseExercise) — a second call is a no-op because the
   * phrase is already inactive.
   */
  private fadeOutLastCursor(phraseIndex: number): void {
    const phrase = this.phrases[phraseIndex];
    if (!phrase?.cursor) return;

    // Guard: if already fading / already disabled, don't start another tween
    if (!phrase.isActive) return;

    Logger.info('[OSMDRepertoire] fadeOutLastCursor — starting fade', { phraseIndex });

    // Stop the cursor from advancing but keep it visible
    phrase.cursor.pause();
    phrase.isActive = false;

    const cursor = phrase.cursor;
    this.tweens.add({
      targets: cursor,
      alpha: 0,
      duration: 600,
      ease: 'Cubic.easeOut',
      onUpdate: () => cursor.draw(),
      onComplete: () => cursor.disable(),
    });
  }

  /**
   * Called when the last phrase finishes playing. Sets the pieceCompleted
   * flag (to prevent cursor reposition on the subsequent pause) and emits
   * the completion event to React.
   *
   * Idempotent: guarded by repertoireCompletedEmitted so it's safe to call
   * from both _pauseExercise (scheduler timeout path) and
   * onFinishPlayingPhrase (cursor finish path).
   */
  private onFinishAllPhrases(): void {
    // Suppress completion entirely during looping. A loop that happens to
    // include the last phrase should keep iterating — not mark the piece
    // completed and not emit the analytics event. restartLoop should intercept
    // at the bracket boundary before this is reached, but guard here too.
    if (this.isLoopingActive) {
      console.log(
        `[NOTE-REGISTER-DBG] onFinishAllPhrases — suppressing RepertoireCompleted event during looping`,
      );
      return;
    }
    this.pieceCompleted = true;
    if (!this.repertoireCompletedEmitted) {
      this.repertoireCompletedEmitted = true;
      // Emit the last phrase's per-phrase completion event here. The
      // cursor-finish path in onFinishPlayingPhrase intentionally skips it
      // for the last phrase so both end-of-piece paths (cursor finish AND
      // scheduler-timeout via _pauseExercise) reach this emission. Idempotent
      // because the surrounding repertoireCompletedEmitted guard ensures the
      // block only runs once per playthrough.
      const lastPhraseIndex = this.phrases.length - 1;
      if (lastPhraseIndex >= 0) {
        NotifyReactEvents.RepertoirePhraseCompleted({ phraseIndex: lastPhraseIndex });
      }
      Logger.info('[OSMDRepertoire] onFinishAllPhrases — emitting RepertoireCompleted');
      NotifyReactEvents.RepertoireCompleted(undefined);
    }
  }

  // ---------------------------------------------------------------------------
  // Update loop
  // ---------------------------------------------------------------------------

  update(): void {
    if (this.pauseQueued) {
      this._pauseExercise();
    }

    // Mobile: smooth-lerp camera.scrollX to follow the active cursor.
    // Runs even when shouldUpdate is false (paused) so the camera tracks
    // the cursor after repositionCursorOnPause / softReset.
    if (this.mobileView) {
      this.updateMobileCameraScroll();

      const camera = this.cameras.main;
      this.debugText?.setText(
        `zoom: ${this.zoomLevel.toFixed(2)} × max ${this.maxZoomLevel.toFixed(2)} = ${camera.zoom.toFixed(2)}`,
      );
    }

    // Perfect-phrase checkbox badge: glue it to the top-right of the loop
    // bracket. Runs BEFORE the shouldUpdate guard so the (empty) checkboxes
    // appear the moment looping is enabled and the bracket is drawn — not only
    // once playback starts — and disappear immediately when looping is off.
    this.updatePerfectPhraseBadge();

    this.updateCountInCountdown();

    if (!this.shouldUpdate) return;

    this.releasePausedCursorMeasureOnceMusicPlays();

    this.countInCursorHold.update(this.eventStream.timeKeeper, this.pickupMeasures());

    // Defense-in-depth: skip cursor updates while the TimeKeeper is paused.
    // The primary fix lives in RepertoirePlayContainerOSMD, which calls
    // startScheduler() (recalibrating TimeKeeper.startTime to the current
    // Tone.context.currentTime) BEFORE sending StartExercise on resume.
    // This guard catches any residual race where the scene's update() runs
    // before the TimeKeeper has been unpaused, preventing stale timestamps
    // from causing the cursor to jump through all remaining phrases.
    // Sampled log during active looping playback to track TK timestamp progression
    if (this.isLoopingActive && this.playbackStarted && Math.random() < 0.005) {
      const tkTs = this.eventStream?.timeKeeper?.audioTimeToMeasureTimestamp();
      const bracketEnd = this.bracketOverlay?.getEndTimestamp();
      console.log(
        `[LOOP-DBG] update() playback tkTimestamp=${tkTs?.toFixed(3)} bracketEnd=${bracketEnd} willLoop=${tkTs !== undefined && bracketEnd !== undefined && tkTs >= bracketEnd}`,
      );
    }
    if (this.playbackStarted && this.eventStream.timeKeeper.isPaused()) {
      Logger.debug('[RESUME-DIAG] Scene update blocked — TimeKeeper still paused', {
        playbackStarted: this.playbackStarted,
        shouldUpdate: this.shouldUpdate,
      });
      return;
    }

    // Log once per second during loop count-in to avoid flooding
    if (this.isLoopCountInActive && Math.random() < 0.005) {
      console.log(
        `[LOOP-DBG] update() loop count-in playbackStarted=${this.playbackStarted} mainTKPaused=${this.eventStream?.timeKeeper?.isPaused()} auxTKTimestamp=${this.timeKeeperAuxiliary?.audioTimeToMeasureTimestamp()} lastTickTime=${this.lastTickTime}`,
      );
    }

    // During loop count-in the main TimeKeeper is paused, so phrase
    // updates and loop-boundary checks would operate on stale timestamps.
    // Skip them entirely until the user plays the first note.
    if (!this.isLoopCountInActive) {
      this.phrases.forEach((phrase) => phrase.update());

      // Clamp the active cursor's X so it never visually advances past the
      // bracket end during looping. The cursor interpolates toward the next
      // iterator note (which may be past the bracket), so without this clamp
      // the cursor briefly visits notes outside the bracket before
      // checkLoopBoundary fires restartLoop.
      if (this.isLoopingActive && this.playbackStarted && this.bracketOverlay) {
        const cursor = this.phrases[this.activePhrase]?.cursor;
        if (cursor) {
          const bracketEndX = this.getBracketEndWorldX();
          // Only clamp if the bracket end is on the SAME phrase as the cursor.
          // If the bracket spans multiple phrases, the cursor on an earlier
          // phrase should advance freely to its phrase end — the phrase
          // transition handles moving to the next line.
          const bracketEndOnActivePhrase = this.isBracketEndOnPhrase(this.activePhrase);
          if (bracketEndX !== undefined && bracketEndOnActivePhrase && cursor.x > bracketEndX) {
            cursor.setX(bracketEndX);
          }
        }
      }

      // Perfect-phrase: poll running accuracy before the boundary check so a
      // mid-loop dip is recorded against the current iteration (before
      // checkLoopBoundary may complete it via restartLoop).
      if (this.isLoopingActive && this.playbackStarted) {
        this.pollPerfectPhraseAccuracy();
      }

      this.checkLoopBoundary();
    }

    this.updateBeatClock();
  }

  /**
   * Timing / metronome tick logic: fire tickUpdate once per beat of the clock
   * in charge (the auxiliary TimeKeeper during the count-in, the main one once
   * playback has started). A beat follows the meter's beat grouping (a quarter
   * in 4/4, a dotted quarter in 6/8, the 2+2+3 eighths of 7/8: see
   * MeterTiming). The count-in starts on beat 1, so the clock walks the bar's
   * beats from the first one again whenever lastTickTime is re-seeded.
   */
  private updateBeatClock(): void {
    const timeKeeper = this.playbackStarted
      ? this.eventStream.timeKeeper
      : this.timeKeeperAuxiliary;

    if (timeKeeper === undefined) {
      Logger.warn('[OSMDPlayableScene] timeKeeper unavailable');
      return;
    }

    const timestamp = timeKeeper.audioTimeToMeasureTimestamp();

    if (this.lastTickTime === undefined) {
      this.lastTickTime = timestamp - TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME;
    }

    if (this.lastTickTime !== this.beatClockLastTickTime) {
      this.beatClockBeatIndex = 0;
    }

    // The beat now running, in the whole-note units the timestamp is in.
    const meter = this.beatClockMeterOf(timeKeeper);
    const beatFractions = beatLengthsInWholeNotes(meter);
    const beatFraction = beatFractions[this.beatClockBeatIndex % beatFractions.length];
    const measureDurationSinceLastTick = timestamp - this.lastTickTime;

    if (measureDurationSinceLastTick > beatFraction) {
      this.beatClockBeatIndex += 1;
      const beatSecs = beatLengthsInSecs(this.tempo ?? 72, meter);
      const beatDuration = beatSecs[this.beatClockBeatIndex % beatSecs.length] * 1000;
      this.tickUpdate(beatDuration);
      this.lastTickTime =
        this.lastTickTime === undefined
          ? undefined
          : timestamp - (timestamp - this.lastTickTime - beatFraction);
    }
    this.beatClockLastTickTime = this.lastTickTime;
  }

  /** A clock's meter; a clock with no meter yet keeps the quarter-note beat (4/4). */
  private beatClockMeterOf(timeKeeper: ITimeKeeper): TimeSignature {
    const meter = timeKeeper.getTimeSignature();
    return isPlayableTimeSignature(meter) ? meter : new TimeSignature(4, 4);
  }

  /** Milliseconds of the score's first beat at the tempo (one count-in beat). */
  private firstBeatMs(): number {
    return beatLengthsInSecs(this.tempo, this.getScoreTimeSignature())[0] * 1000;
  }

  /**
   * Called on each metronome tick.
   * During count-in (before first MIDI note): plays click audio and blinks cursor.
   * After playback starts: no-op — Scheduler2 handles metronome audio and the
   * cursor advances via the shared TimeKeeper in RepertoireCursor.update().
   * Mirrors OSMDSightReadingScene.tickUpdate().
   */
  private tickUpdate(beatDuration: number): void {
    if (!this.playbackStarted) {
      // Both initial and loop count-in: play scene metronome click (no downbeat)
      // and blink cursor. The Scheduler2's interval is stopped during loop
      // count-in, so the scene handles all audio.
      this.metronomeClick();
      this.phrases[this.activePhrase]?.cursor?.blink(beatDuration);
    }
  }

  // ---------------------------------------------------------------------------
  // Metronome
  // ---------------------------------------------------------------------------

  /**
   * Play a metronome click sound.
   * Used during count-in phase. After the first MIDI note, the context's
   * Scheduler2 takes over metronome audio via its own tick loop.
   * Mirrors OSMDSightReadingScene.metronomeClick().
   */
  private metronomeClick(): void {
    // Respect the user's "metronome sound" preference. Scheduler2 already gates
    // its beat audio (after the first note) on repMetronomeSound; the flexible
    // count-in clicks live here in the scene, so honor the same flag to keep the
    // count-in consistent with the setting. The flag is synced from
    // userData.rep_metronome_sound via RepertoirePlayContext → changeMetronomeAudio.
    // Default to playing when no scheduler is injected yet.
    if (this.scheduler2 && !this.scheduler2.repMetronomeSound) return;
    notifyAppAudioOutput({ reason: 'metronome' });
    if (isCapacitorNative()) {
      MuseFlowAudio.playSoundEffect({ name: 'click' });
    } else {
      if (this.clickAudio === undefined) {
        Logger.warn('[OSMDPlayableScene] metronome audio not loaded!');
        return;
      }
      this.clickAudio.stop();
      this.clickAudio.play(metronomeSoundConfig());
    }
  }

  // ---------------------------------------------------------------------------
  // Tempo
  // ---------------------------------------------------------------------------

  /**
   * Update the tempo used for count-in timing and cursor blink speed.
   * Called when the user adjusts the tempo slider while the scene is active.
   * Mirrors OSMDSightReadingScene.changeTempo().
   */
  changeTempo({ tempo }: { tempo: number }): void {
    // Perfect-phrase: any tempo change (either direction) breaks the streak.
    if (tempo !== this.tempo) {
      this.resetPerfectPhrase(`tempo-changed ${this.tempo}→${tempo}`);
    }

    this.tempo = tempo;
    this.timeKeeperAuxiliary!.setBpm(tempo);

    // MUS-243: keep machine context tempo in sync with the slider. Without
    // this dispatch, the machine's context.tempo stays at the value captured
    // when startExercise first ran. unpauseMainTKAt / unpauseScheduler2At
    // (on NOTE_PLAYED → playing) then read the stale context.tempo and
    // unpause the TK + scheduler at the pre-slider BPM, snapping the user's
    // playback back to the old tempo.
    this.dispatchPlaybackEvent({ type: 'SET_TEMPO', tempo });

    if (!this.playbackStarted) {
      const activePhrase = this.phrases[this.activePhrase];
      activePhrase?.cursor?.blink((60 / tempo) * 1000);
    }
  }

  // ---------------------------------------------------------------------------
  // Meter
  // ---------------------------------------------------------------------------

  /**
   * The score's meter: the one the main transport runs in (the host seeds the
   * main TimeKeeper from the score), else the first phrase's, else 4/4. A long
   * bar (8/4, or 12/8 written as one bar) is an ordinary meter here, and a
   * hidden time signature is still the score's meter - hiding only skips
   * drawing it.
   */
  protected getScoreTimeSignature(): TimeSignature {
    const candidates: Array<TimeSignature | undefined> = [
      this.eventStream?.timeKeeper?.getTimeSignature(),
      this.phrases[0]?.data?.timeSignature,
    ];
    return candidates.find(isPlayableTimeSignature) ?? new TimeSignature(4, 4);
  }

  // ---------------------------------------------------------------------------
  // Start / pause
  // ---------------------------------------------------------------------------

  /**
   * Begin the count-in phase: start the auxiliary TimeKeeper, play the first
   * metronome click, activate the first phrase's cursor, and start blinking.
   * Extracted from startExercise so softReset can also invoke it for auto-start.
   *
   * The auxiliary TimeKeeper takes the score's meter, so the count-in beat is
   * the transport's beat: one unaccented click per beat of the meter's beat
   * grouping (a quarter in 4/4, a dotted quarter in 6/8, 2+2+3 eighths in 7/8)
   * at the tempo, whatever the bar length and whether the time signature is
   * shown. The count-in stays open-ended - it
   * clicks until the first played note starts the transport - so a long bar
   * never means a long fixed count-in.
   */
  private beginCountIn(): void {
    console.log(
      `[LOOP-DBG] Scene beginCountIn() tempo=${this.tempo} playbackStarted=${this.playbackStarted} isLoopingActive=${this.isLoopingActive} activePhrase=${this.activePhrase} cursorStartTimestamp=${this.cursorStartTimestamp}`,
    );
    this.metronomeClick();
    const scoreMeter = this.getScoreTimeSignature();
    const secondsPerMeasure = measureDurationSecs(this.tempo, scoreMeter);
    const measureOffset = TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
    if (!isCapacitorNative()) {
      Tone.start();
    }
    this.timeKeeperAuxiliary!.setTimeSignatures(0, [scoreMeter]);
    this.timeKeeperAuxiliary!.resetTime();
    this.timeKeeperAuxiliary!.unpause(this.tempo, 0, measureOffset, NoteType._32nd);
    this.lastTickTime = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();

    // First play starts at phrase 0 unless the user placed the cursor.
    // Resume count-ins keep the active phrase that pause positioned.
    const startPhraseIndex =
      this.hasEverPlayed || this.cursorStartTimestamp !== null ? this.activePhrase : 0;
    if (startPhraseIndex !== this.activePhrase) {
      this.phrases[this.activePhrase]?.deactivate();
      this.activePhrase = startPhraseIndex;
    }

    const countInCursor = this.phrases[startPhraseIndex]?.cursor;
    const countInIter = countInCursor?.Iterator as unknown as
      | {
          starts?: number[];
          cursorData?: unknown[];
          indx?: number;
          currentMeasureIndex?: number;
          endReached?: boolean;
        }
      | undefined;
    console.log(
      `[CURSOR-DBG] beginCountIn — startPhraseIndex=${startPhraseIndex} activePhrase=${this.activePhrase} cursorStartTimestamp=${this.cursorStartTimestamp} cursorExists=${!!countInCursor} cursorX=${countInCursor?.x?.toFixed(1)} iterStarts=${JSON.stringify(countInIter?.starts)} iterIndx=${countInIter?.indx} iterMeasureIdx=${countInIter?.currentMeasureIndex} iterEndReached=${countInIter?.endReached} cursorDataLen=${countInIter?.cursorData?.length}`,
    );

    if (!this.hasEverPlayed && this.cursorStartTimestamp === null && countInCursor) {
      const firstNotePos = this.phrases[startPhraseIndex]?.getFirstNotePosition() ?? 0;
      countInCursor.setX(firstNotePos - countInCursor.width / 2);
      try {
        countInCursor.setStart(firstNotePos, 0, 0);
      } catch {
        // Iterator can be unavailable during the earliest load frames; the visual jump is enough.
      }
    }

    this.phrases[startPhraseIndex]?.activate();
    this.phrases[startPhraseIndex]?.cursor?.blink(this.firstBeatMs());
    this.countInActive = true;
  }

  private clearAutoPlaybackCountInTimer(): void {
    this.autoPlaybackCountInTimer?.remove(false);
    this.autoPlaybackCountInTimer = undefined;
  }

  private beginAutoPlayback(): void {
    this.clearAutoPlaybackCountInTimer();

    if (this.hasEverPlayed || !this.eventStream.timeKeeper.isPaused()) {
      void this.startAutoPlaybackNow();
      return;
    }

    this.beginCountIn();

    if (this.isLoopingActive) {
      this.scrollToShowBracketRange();
    } else {
      const scrollTarget = this.cursorStartTimestamp !== null ? this.activePhrase : 0;
      this.scrollToPhraseSmooth(scrollTarget);
    }

    this.autoPlaybackCountInTimer = this.time.delayedCall(
      this.firstBeatMs(),
      this.completeAutoPlaybackCountIn,
      [],
      this,
    );
  }

  private completeAutoPlaybackCountIn(): void {
    this.autoPlaybackCountInTimer = undefined;
    void this.startAutoPlaybackNow();
  }

  private async startAutoPlaybackNow(): Promise<void> {
    if (!isCapacitorNative()) {
      Tone.start();
    }
    this.timeKeeperAuxiliary?.pause();
    this.timeKeeperAuxiliary?.resetTime();

    const startPhraseIndex =
      this.hasEverPlayed || this.cursorStartTimestamp !== null ? this.activePhrase : 0;

    this.dispatchPlaybackEvent({ type: 'COUNT_IN_FINISHED' });
    await this.waitForSchedulerUnpause();

    this.activePhrase = startPhraseIndex;
    this.phrases[startPhraseIndex]?.activate();
    this.phrases[startPhraseIndex]?.cursor?.unpause(0);

    this.playbackStarted = true;
    this.hasEverPlayed = true;
    this.countInActive = false;
    this.isLoopCountInActive = false;

    NotifyReactEvents.StartedPlaying({
      measureOffset: this.cursorStartTimestamp ?? 0,
      processEventsQueue: [() => undefined, this],
    });

    if (this.isLoopingActive) {
      this.scrollToShowBracketRange();
    } else {
      this.scrollToPhraseSmooth(startPhraseIndex);
    }
  }

  // ---------------------------------------------------------------------------
  // Ungraded play (Play Along): timed count-ins
  //
  // Graded play waits in a count-in state for the player's first note.
  // Ungraded play has no note to wait for: every count-in wait is left at
  // once with COUNT_IN_FINISHED, and the scheduler starts the transport far
  // enough ahead that its own metronome clicks are the count-in, on the
  // audio clock. The cursor waits on the first note meanwhile and pulses on
  // each click (CountInCursorHold).
  // ---------------------------------------------------------------------------

  /** The pickup's missing duration in measures (0 without a pickup). */
  private pickupMeasures(): number {
    const wholeNotesPerMeasure = this.eventStream?.timeKeeper?.measureDurationAt(0) ?? 0;
    return this.scheduler2 && wholeNotesPerMeasure > 0
      ? this.scheduler2.pickUpMeasureOffset / wholeNotesPerMeasure
      : 0;
  }

  /**
   * Ungraded: the countdown number and closing ring over the count-in, read
   * from the count-in the scheduler queued. Graded play has no count-in plan,
   * so nothing is drawn.
   */
  private updateCountInCountdown(): void {
    const timeKeeper = this.eventStream?.timeKeeper;
    if (!timeKeeper) return;
    const showing = this.shouldUpdate && this.ungradedPlay;
    this.countInCountdown.update({
      countIn: showing ? (this.scheduler2?.getActiveCountIn() ?? null) : null,
      timeKeeper,
      pickupMeasures: this.pickupMeasures(),
      anchor: this.phrases[this.activePhrase]?.cursor ?? null,
      entryHeadOf: (plan) => this.entryNoteHeadPosition(plan),
      textObstaclesOf: () => this.scoreTextBoxes(),
      coveredCanvasTopOf: () => this.toolbarCoveredCanvasTop(),
    });
  }

  /**
   * How much of the top of the canvas the play screen's toolbar covers, in
   * canvas pixels (the canvas runs on behind it). 0 when it is not there or
   * has slid away.
   */
  private toolbarCoveredCanvasTop(): number {
    const toolbar = document.getElementById('rep-header-container');
    const canvas = this.game?.canvas;
    if (!toolbar || !canvas) return 0;
    const canvasBox = canvas.getBoundingClientRect();
    const canvasPxPerCssPx = canvasBox.height > 0 ? canvas.height / canvasBox.height : 1;
    return Math.max(0, (toolbar.getBoundingClientRect().bottom - canvasBox.top) * canvasPxPerCssPx);
  }

  /**
   * The score's text near the count-in, in world space: the title, composer
   * and tempo mark the scene draws above the first phrase, and the active
   * phrase's own text (fingerings). The countdown number keeps clear of it.
   */
  private scoreTextBoxes(): WorldBox[] {
    const textsIn = (children: Phaser.GameObjects.GameObject[]): Phaser.GameObjects.Text[] =>
      children.flatMap((child) => {
        if (child instanceof Phaser.GameObjects.Text) return [child];
        if (child instanceof Phaser.GameObjects.Container) return textsIn(child.list);
        return [];
      });
    const header = [this.titleText, this.composerText, this.bpmNoteText, this.bpmValueText];
    const container = this.phrases[this.activePhrase]?.container;
    return [
      ...header.filter((text): text is Phaser.GameObjects.Text => text !== undefined),
      ...(container ? textsIn(container.list) : []),
    ]
      .filter((text) => text.visible && text.text.length > 0)
      .map((text) => {
        const bounds = text.getBounds();
        return { left: bounds.left, top: bounds.top, right: bounds.right, bottom: bounds.bottom };
      });
  }

  /**
   * The centre of the highest notehead the count-in leads into, in world
   * space; null when it is not drawn (the ring then circles the cursor).
   */
  private entryNoteHeadPosition(plan: CountInPlan): WorldPoint | null {
    const notes = this.eventStream.getNoteEventsStartingAt(
      entryWholeNotes(
        plan,
        this.pickupMeasures(),
        this.eventStream.timeKeeper.measureDurationAt(0),
      ),
    );
    const top = highestNote(notes);
    const id = top?.Graphics[0]?.staveNote.id;
    if (!top || id === undefined) return null;
    // A same-hand chord is one container: find the top note's head among its heads.
    const container = notes
      .filter((note) => note.Graphics[0]?.staveNote.id === id)
      .sort((low, high) => low.MidiNote - high.MidiNote);
    return (
      this.getNoteContainer(id)?.getNoteHeadWorldCenter(container.indexOf(top), container.length) ??
      null
    );
  }

  /** Read graded or ungraded for this play and set the scheduler up for it. */
  private prepareUngradedStart(autoStart: boolean): void {
    // Listening (autoStart) keeps its own short lead-in.
    this.ungradedPlay = !autoStart && isPlayAlongCountInUngraded();
    this.dispatchPlaybackEvent({ type: 'SET_UNGRADED', ungraded: this.ungradedPlay });
    this.scheduler2?.setPlayAlongLoop(
      playAlongLoop(this.ungradedPlay, this.getScoreTimeSignature()),
    );
    // A resume goes straight from paused to playing, so ask before USER_PLAY.
    this.requestUngradedCountIn(false);
  }

  private requestUngradedCountIn(continuesLoop: boolean): void {
    this.scheduler2?.requestCountIn(
      countInRequestFor({
        ungraded: this.ungradedPlay,
        timeKeeper: this.eventStream.timeKeeper,
        eventStream: this.eventStream,
        continuesLoop,
      }),
    );
  }

  /**
   * Ungraded: leave a count-in wait on the timer. Returns false (and does
   * nothing) in graded play or when the machine is not waiting.
   */
  private leaveCountInWaitWhenUngraded(continuesLoop: boolean): boolean {
    const phase = this.getPlaybackPhase();
    if (!this.ungradedPlay || (phase !== 'normalCountIn' && phase !== 'loopCountInWait')) {
      return false;
    }
    this.requestUngradedCountIn(continuesLoop);
    this.dispatchPlaybackEvent({ type: 'COUNT_IN_FINISHED' });
    this.scheduler2?.requestCountIn(null);
    this.enterUngradedPlaying();
    return true;
  }

  /** Scene flags for playing, with the cursor held through the count-in. */
  private enterUngradedPlaying(): void {
    if (this.getPlaybackPhase() !== 'playing') return;
    this.playbackStarted = true;
    this.hasEverPlayed = true;
    this.countInActive = false;
    this.isLoopCountInActive = false;
    this.phrases[this.activePhrase]?.activate();
    const cursor = this.phrases[this.activePhrase]?.cursor;
    if (cursor) this.countInCursorHold.hold(cursor);
    // The container's StartedPlaying listener starts the event-stream loops.
    NotifyReactEvents.StartedPlaying(undefined);
  }

  /** startExercise for ungraded play: first play, resume or loop start. */
  private startUngradedPlayback(): void {
    if (!isCapacitorNative()) {
      Tone.start();
    }
    const phase = this.getPlaybackPhase();
    if (phase === 'normalCountIn' && !this.hasEverPlayed) {
      this.placeCursorForFirstPlay();
    }
    if (!this.leaveCountInWaitWhenUngraded(false)) {
      // Resume: paused -> playing already started the transport with a count-in.
      this.scheduler2?.requestCountIn(null);
      this.enterUngradedPlaying();
    }
    if (this.isLoopingActive) {
      this.scrollToShowBracketRange();
    } else {
      this.scrollToPhraseSmooth(this.activePhrase);
    }
    this.updateMobileCameraScroll();
  }

  /** First play: the cursor waits on the piece's first note (or the placed start). */
  private placeCursorForFirstPlay(): void {
    const startPhraseIndex = this.cursorStartTimestamp !== null ? this.activePhrase : 0;
    if (startPhraseIndex !== this.activePhrase) {
      this.phrases[this.activePhrase]?.deactivate();
      this.activePhrase = startPhraseIndex;
    }
    const cursor = this.phrases[startPhraseIndex]?.cursor;
    if (this.cursorStartTimestamp !== null || !cursor) return;
    const firstNotePos = this.phrases[startPhraseIndex]?.getFirstNotePosition() ?? 0;
    cursor.setX(firstNotePos - cursor.width / 2);
    try {
      cursor.setStart(firstNotePos, 0, 0);
    } catch {
      // Iterator can be unavailable during the earliest load frames; the visual jump is enough.
    }
  }

  private flushPendingStartExercise(): void {
    if (!this.pendingStartExercisePayload || this.isAddingPhrases || this.phrases.length === 0) {
      return;
    }

    const payload = this.pendingStartExercisePayload;
    this.pendingStartExercisePayload = null;
    this.startExercise(payload);
  }

  startExercise({ tempo, autoStart }: StartExercisePayload): void {
    console.log(
      `[EXERCISE-DBG] [OSMDScene.startExercise] tempo=${tempo} autoStart=${autoStart} playbackStarted=${this.playbackStarted} phrasesCount=${this.phrases.length}`,
    );
    console.log(
      `[LOOP-DBG] Scene startExercise() entry tempo=${tempo} autoStart=${autoStart} playbackStarted=${this.playbackStarted} shouldUpdate=${this.shouldUpdate} countInActive=${this.countInActive} isLoopingActive=${this.isLoopingActive} isLoopCountInActive=${this.isLoopCountInActive}`,
    );
    Logger.debug('[OSMD-MIDI] Scene startExercise', {
      tempo,
      autoStart,
      playbackStarted: this.playbackStarted,
      shouldUpdate: this.shouldUpdate,
      countInActive: this.countInActive,
      phrasesCount: this.phrases.length,
    });

    if (this.isAddingPhrases || this.phrases.length === 0) {
      Logger.info('[OSMDPlayableScene] deferring StartExercise until phrases are ready', {
        isAddingPhrases: this.isAddingPhrases,
        phrasesCount: this.phrases.length,
        tempo,
        autoStart,
      });
      this.pendingStartExercisePayload = { tempo, autoStart };
      return;
    }

    if (tempo) {
      this.tempo = tempo;
      // MUS-243: keep machine context tempo in sync with the legacy field.
      this.dispatchPlaybackEvent({ type: 'SET_TEMPO', tempo });
    }
    this.shouldUpdate = true;

    this.prepareUngradedStart(autoStart === true);

    // MUS-243: route through the playback machine. The machine's transitions
    // from `stopped` (or `paused`) handle aux-TK arm, cursor positioning,
    // error recognition, etc. via the facade. The remaining imperative body
    // below covers scene-specific behavior (scroll, phrase activate, the
    // autoStart fast-path) that isn't modeled in the machine actions.
    this.dispatchPlaybackEvent({ type: 'USER_PLAY' });

    // MUS-243: the playback machine (via the USER_PLAY dispatch above) now
    // owns every lifecycle side effect — main/aux TimeKeeper pause+unpause,
    // Scheduler2, cursor pause/unpause/blink, error-recognition toggling.
    // The branches below retain ONLY scene-mechanic concerns the machine
    // doesn't model: scene flags that gate `update()`, phrase activation,
    // camera scrolling, and (for non-loop first play) positioning the cursor
    // at the piece's first note via `beginCountIn()`. The previous imperative
    // TK/cursor/error-rec lines were removed because they double-fired against
    // the machine actions (metronome bail, resume drift, double rewind).

    // User is starting playback — revert any manual drag-scroll so the
    // camera resumes following the cursor.
    this.mobileUserScrolledX = false;
    this.updateMobileCameraScroll();

    // Fade bracket handles so they don't obscure notes during playback.
    if (this.isLoopingActive && this.bracketOverlay) {
      this.bracketOverlay.setHandlesPlaybackAlpha(true);
    }

    if (this.ungradedPlay) {
      this.startUngradedPlayback();
      return;
    }

    if (!this.playbackStarted) {
      if (autoStart) {
        this.beginAutoPlayback();
        return;
      }

      if (this.countInActive) {
        // Machine re-armed the aux TK on USER_PLAY; only the BPM hint is a
        // scene concern. (setBpm is harmless if the aux TK isn't running.)
        this.timeKeeperAuxiliary!.setBpm(this.tempo);
        return;
      }

      const tkAlreadyRunning = !this.eventStream.timeKeeper.isPaused();
      if (this.isLoopingActive && tkAlreadyRunning && this.hasEverPlayed) {
        // Mid-loop resume — machine handled TK/cursor. Scene flags + scroll only.
        this.playbackStarted = true;
        this.isLoopCountInActive = false;
        this.countInActive = false;
        this.phrases[this.activePhrase]?.activate(true);

        // Resume continues from wherever the user paused; scroll only if
        // the active phrase itself isn't fully in frame.
        if (!this.isPhraseFullyVisible(this.activePhrase)) {
          this.scrollToPhraseSmooth(this.activePhrase);
        }
        return;
      } else if (this.isLoopingActive && tkAlreadyRunning) {
        // Looping restart → count-in — machine handled TK pause, error
        // recognition, aux TK, and cursor blink. Scene flags + scroll only.
        // This is the loop-restart-from-end-of-bracket path, so it is a
        // reset back to the top of the loop — always scroll there.
        this.isLoopCountInActive = true;
        this.countInActive = true;
        this.lastTickTime = undefined;

        if (!this.isBracketRangeFullyVisible()) {
          this.scrollToShowBracketRange();
        }
        this.updateMobileCameraScroll();
        return;
      }

      // Non-loop (or first-ever) play: beginCountIn() positions the cursor at
      // the piece's first note (scene mechanic). Its aux-TK arming was
      // stripped — the machine's armAuxTK owns the count-in clock now.
      this.beginCountIn();

      if (this.isLoopingActive) {
        this.scrollToShowBracketRange();
      } else {
        const scrollTarget = this.cursorStartTimestamp !== null ? this.activePhrase : 0;
        this.scrollToPhraseSmooth(scrollTarget);
      }
      this.updateMobileCameraScroll();
    } else {
      // Resume from pause: the machine's paused → playing transition already
      // unpaused the main TK + Scheduler2 + cursor at the rewound position.
      // Do NOT call cursor.unpause() here — a second unpause re-advances the
      // cursor's interpolation refs and drifts playback ahead of the cursor.
      if (!this.isLoopingActive) {
        this.scrollToPhraseSmooth(this.activePhrase);
      }
      this.updateMobileCameraScroll();

      // StartedPlaying is now only a Container loop-start trigger (its
      // scheduler/TK handling was removed) — emit it so the event-stream and
      // accuracy update loops start.
      NotifyReactEvents.StartedPlaying(undefined);
      this.phrases[this.activePhrase]?.activate();
    }
  }

  pauseExercise(): void {
    // MUS-243: route through the playback machine. The machine's `paused`
    // state entry triggers all subsystem pauses + cursor repositioning via
    // the facade; the queueing pattern (`pauseQueued` consumed in `update()`)
    // remains for legacy in-frame coordination.
    console.log(
      `[LOOP-DBG-243] Scene pauseExercise() (via PauseExercise event) → dispatching USER_PAUSE. phase=${this.getPlaybackPhase()}`,
    );
    this.dispatchPlaybackEvent({ type: 'USER_PAUSE' });
    this.pauseQueued = true;
  }

  private _pauseExercise(): void {
    // MUS-243 (cursor-disappears-on-rapid-pause/play, MUS-7 family): this
    // method is the deferred half of pauseExercise() — pauseExercise()
    // dispatches USER_PAUSE synchronously (machine paused.entry runs the
    // real pause work), then sets pauseQueued=true so this legacy body runs
    // on the NEXT frame in update().
    //
    // If the user presses play BEFORE that next frame fires, the machine
    // moves out of `paused` (paused → playing or paused → loopCountInWait),
    // and the play path sets shouldUpdate=true plus the appropriate count-in
    // flags. Without this guard, this method would then stomp shouldUpdate /
    // countInActive / isLoopCountInActive back to false, call cursor.pause()
    // on the active phrase, cursor.disable() (alpha=0, enabled=false) on the
    // others, and un-suppress error recognition mid-resume — leaving the
    // scene frozen with the cursor invisible or stuck. Skipping the legacy
    // body when the machine has moved past `paused` is the structural fix.
    // We always consume the queue flag so it can't accumulate across frames.
    this.pauseQueued = false;
    if (this.getPlaybackPhase() !== 'paused') {
      console.log(
        `[LOOP-DBG-243] _pauseExercise SKIPPED — phase=${this.getPlaybackPhase()} (user rapid-played; machine moved past paused, legacy stomp suppressed)`,
      );
      return;
    }
    console.log(
      `[LOOP-DBG] Scene _pauseExercise() entry playbackStarted=${this.playbackStarted} shouldUpdate=${this.shouldUpdate} isLoopingActive=${this.isLoopingActive} isLoopCountInActive=${this.isLoopCountInActive} pieceCompleted=${this.pieceCompleted} activePhrase=${this.activePhrase} mainTKPaused=${this.eventStream?.timeKeeper?.isPaused()}`,
    );
    this.clearAutoPlaybackCountInTimer();
    this.playbackStartPending = false;
    this.pendingPlaybackStartEvents = [];
    this.pendingSchedulerUnpause = null;

    // During loop count-in the main TK is already paused. Stop the
    // auxiliary TK too so cursor blinking stops. We do NOT unpause the
    // main TK — that would cause the Scheduler2's metronome to resume.
    // The Scheduler2's pauseTicks() will bail out (TK already paused)
    // but that's OK: no new metronome events will be scheduled because
    // scheduleMetronomeAudio reads frozen timestamps from the paused TK.
    if (this.isLoopCountInActive && this.timeKeeperAuxiliary) {
      console.log('[LOOP-DBG] _pauseExercise during loop count-in — pausing auxiliary TK');
      this.timeKeeperAuxiliary.pause();
    }

    this.shouldUpdate = false;
    this.lastTickTime = undefined;
    // pauseQueued is consumed at the top of the method (before the phase guard)
    this.pendingPhraseTransition = null;
    this.countInActive = false;
    this.isLoopCountInActive = false;

    // Restore bracket handle opacity after playback stops.
    if (this.isLoopingActive && this.bracketOverlay) {
      this.bracketOverlay.setHandlesPlaybackAlpha(false);
    }

    // If a loop count-in suppressed error recognition, restore it now. Otherwise
    // the next play attempt after this pause will leave error recognition
    // disabled, causing handleNoteOnEvent to bail out and notes to never
    // register. This happens when the user pauses during the cursor-blink
    // wait state (after restartLoop) before playing the first MIDI note.
    // Safe to call unconditionally — it no-ops when nothing was suppressed.
    if (this.eventStream.errorRecognitionSuppressed) {
      console.log(
        `[NOTE-REGISTER-DBG] _pauseExercise — restoring userErrorRecognitionActive=${JSON.stringify(this.eventStream.userErrorRecognitionActive)} (was suppressed during loop count-in)`,
      );
    }
    this.eventStream.unsuppressErrorRecognition();

    // When the piece has finished (or is finishing), the cursor should
    // fade out instead of jumping back. The flag is set in onPlayingLastSlice
    // when the cursor reaches the last note of the last phrase — before
    // the scheduler timeout fires PauseExercise.
    if (this.pieceCompleted) {
      Logger.info(
        '[OSMDRepertoire] _pauseExercise — piece completed, fading cursor instead of repositioning',
      );
      // Trigger the fade-out (idempotent — no-op if already fading)
      this.fadeOutLastCursor(this.activePhrase);
      // Ensure the completion event fires even when the scheduler timeout
      // pauses the scene before the cursor's update() detects finish.
      // onFinishAllPhrases is idempotent — safe if already called by
      // onFinishPlayingPhrase.
      this.onFinishAllPhrases();
      NotifyReactEvents.ExercisePaused({
        lastPlayedMeasure: this.measureCount,
        pausedAtTimestamp: undefined,
      });
      return;
    }

    // Stop ALL phrase cursors — not just the active one. During multi-phrase
    // loops, cursors on earlier phrases may still be running (e.g., fading out
    // after a phrase transition). If left running, cursor.update() keeps
    // advancing their X position outside the loop bounds.
    // Disable (hide) non-active cursors; pause the active one (keeps it visible).
    for (let i = 0; i < this.phrases.length; i++) {
      const cursor = this.phrases[i]?.cursor;
      if (!cursor) continue;
      if (i === this.activePhrase) {
        cursor.pause();
      } else {
        cursor.disable();
      }
    }

    // MUS-243: cursor repositioning on pause is owned by the playback
    // machine's `paused` state entry (`positionCursorAtLastCompletedMeasure`
    // → `repositionCursorOnPause`, which also sets `scene.lastPauseTimestamp`
    // for resume). That entry runs synchronously when USER_PAUSE is
    // dispatched (in `pauseExercise()`), BEFORE this method runs via the
    // `pauseQueued` path in `update()`. Calling `repositionCursorOnPause()`
    // again here would rewind a second measure (measure 3 → 2 → 1) and emit
    // a stale ExercisePaused. So we only do the looping-specific event-stream
    // seek, reusing the timestamp the machine already captured.
    console.log(
      `[PAUSE-DBG] _pauseExercise — machine-owned reposition; lastPauseTimestamp=${this.lastPauseTimestamp} isLoopingActive=${this.isLoopingActive} activePhrase=${this.activePhrase}`,
    );

    if (this.isLoopingActive && this.bracketOverlay) {
      const bracketStart = this.bracketOverlay.getStartTimestamp();
      const machinePauseTs = this.lastPauseTimestamp;
      const effectivePauseTs =
        machinePauseTs !== null && machinePauseTs >= bracketStart ? machinePauseTs : bracketStart;
      this.playbackStarted = false;
      this.eventStream.seekToTimestamp(effectivePauseTs);
    }
  }

  /**
   * Reposition the cursor to the first note of the last completed measure.
   * Mirrors OSMDSightReadingScene.setCursor() — called from _pauseExercise
   * so the cursor visually jumps back to where playback will resume.
   */
  private repositionCursorOnPause(): number | undefined {
    if (!this.playbackStarted) {
      return undefined;
    }

    const activePhrase = this.phrases[this.activePhrase];
    if (!activePhrase?.cursor) return undefined;

    const lastPlayedMeasure = activePhrase.cursor.getLastPlayedMeasure();
    // Jump back to the last COMPLETED measure (one before the current). A
    // pause before the music has started again (during the resume's count-in,
    // or a resize's pause) keeps the bar the last pause chose: the cursor has
    // not played since, so rewinding again would lose a bar each time.
    const targetMeasure = this.pausedCursorMeasure ?? Math.max(lastPlayedMeasure - 1, 0);

    Logger.info('[UNPAUSE-DBG] scene: repositionCursorOnPause', {
      activePhrase: this.activePhrase,
      lastPlayedMeasure,
      targetMeasure,
      cursorSetStartArgs: {
        localMeasureIndex: targetMeasure, // will be recalculated below
      },
    });
    this.pausedCursorMeasure = targetMeasure;
    return this.placePausedCursorAtMeasure(targetMeasure);
  }

  /**
   * The bar a pause kept stops mattering once the music plays again after the
   * resume (past its count-in, or past the first note in graded play).
   */
  private releasePausedCursorMeasureOnceMusicPlays(): void {
    if (this.pausedCursorMeasure === null || this.lastPauseTimestamp !== null) return;
    const timeKeeper = this.eventStream?.timeKeeper;
    if (
      this.getPlaybackPhase() === 'playing' &&
      timeKeeper &&
      !timeKeeper.isPaused() &&
      !timeKeeper.getIsCountingIn()
    ) {
      this.pausedCursorMeasure = null;
    }
  }

  /**
   * Put the paused cursor on the first beat of `targetMeasure` (a global
   * measure index), where playback resumes, and return that global
   * timestamp. Run on pause, and again after a re-layout while paused (a
   * resize or rotation moves every note, and the cursor must stay on the
   * note it resumes from).
   */
  private placePausedCursorAtMeasure(targetMeasure: number): number | undefined {
    const activePhrase = this.phrases[this.activePhrase];
    if (!activePhrase?.cursor) return undefined;

    // Find which phrase contains the target measure
    const phraseIdx = this.phrases.findIndex((phrase) => phrase.containsMeasure(targetMeasure));

    if (phraseIdx === -1) {
      Logger.warn('[OSMDRepertoire] Target measure not found in any phrase', {
        targetMeasure,
        phraseStartingMeasures: this.phrases.map((phrase) => phrase.phraseStartingMeasure),
      });
      // Fallback: position cursor at the first note of the active phrase
      const firstNotePos = activePhrase.getFirstNotePosition();
      activePhrase.cursor.setX(firstNotePos - activePhrase.cursor.width / 2);
      activePhrase.cursor.enable();
      this.updateMobileCameraScroll();
      return undefined;
    }

    const targetPhrase = this.phrases[phraseIdx];

    // If the target measure is in a different phrase, switch active phrase
    if (phraseIdx !== this.activePhrase) {
      const currentPhrase = this.phrases[this.activePhrase];
      const currentFirstNote = currentPhrase.getFirstNotePosition();
      currentPhrase.cursor?.setStart(currentFirstNote, currentPhrase.data.startTimestamp, 0);
      currentPhrase.cursor?.setX(currentFirstNote - (currentPhrase.cursor?.width ?? 0) * 2);
      currentPhrase.deactivate();
      this.activePhrase = phraseIdx;
    }

    targetPhrase.activate();
    const localMeasureIndex = targetMeasure - targetPhrase.phraseStartingMeasure;
    // Use getFirstBeatPositionInMeasure (includes rests) so the cursor goes
    // to beat 1 even when the measure starts with a rest. Without this, the
    // cursor would land on the first sounding note and then visually jump
    // backwards on resume.
    const firstBeatInMeasure = targetPhrase.getFirstBeatPositionInMeasure(localMeasureIndex);
    // Fallback to phrase's first note if measure beat position not found
    const cursorStartPos = firstBeatInMeasure ?? targetPhrase.getFirstNotePosition();
    const timesig = targetPhrase.data.timeSignature;
    const cursor = targetPhrase.cursor!;
    const localMeasureOffset = targetMeasure - cursor.startingMeasure;
    cursor.lastPlayedMeasure = localMeasureOffset;
    // Use the actual OSMD timestamp for the target measure, which correctly
    // handles pickup (anacrusis) measures. The naive formula
    // `(n/d) * localMeasureIndex` assumes uniform measure durations and
    // produces a timestamp that's too large when the first measure is a
    // shortened pickup. The notePositions data was already computed with
    // pickup-aware timestamps via buildMeasureStartTimestamps().
    const actualMeasureTimestamp = targetPhrase.getMeasureStartTimestamp(localMeasureIndex);
    const setStartTimestamp =
      actualMeasureTimestamp !== undefined
        ? actualMeasureTimestamp - targetPhrase.data.startTimestamp
        : (timesig.numerator / timesig.denominator) * localMeasureIndex;
    cursor.setStart(cursorStartPos, setStartTimestamp, localMeasureOffset);
    const cursorPrivate = cursor as unknown as {
      lastExpectedOnset: number;
      lastNoteXPosition: number;
      iterator: { currentTimeStamp: number };
    };
    // The global OSMD timestamp where the cursor is now sitting.
    // This is the phrase's startTimestamp plus the iterator's local
    // currentTimeStamp (which points at the first note of the target
    // measure after rollback). The container uses this to initialise the
    // TimeKeeper at the correct position on resume.
    const globalPausedTimestamp =
      targetPhrase.data.startTimestamp + cursorPrivate.lastExpectedOnset;

    // MUS-243: pause must never land the cursor before the loop start. The
    // last-completed-measure rewind above is measure-aligned, but the loop
    // bracket can start mid-measure (or in a later measure than where the
    // user paused), so the naive rewind can land before the bracket. When
    // that happens, clamp to the bracket start — both the cursor visual and
    // the resume timestamp returned to the machine (consumed by
    // `unpauseMainTKAt`) — so play resumes from the first note of the loop.
    if (this.isLoopingActive && this.bracketOverlay) {
      const bracketStart = this.bracketOverlay.getStartTimestamp();
      if (globalPausedTimestamp < bracketStart) {
        Logger.info('[UNPAUSE-DBG] scene: clamping pause position to bracket start', {
          globalPausedTimestamp,
          bracketStart,
        });
        this.positionCursorAtBracketStart(bracketStart);
        return bracketStart;
      }
    }

    Logger.info('[UNPAUSE-DBG] scene: repositionCursorOnPause setStart done', {
      cursorStartPos,
      setStartTimestamp,
      localMeasureOffset,
      localMeasureIndex,
      timesig: `${timesig.numerator}/${timesig.denominator}`,
      cursorLastExpectedOnset: cursorPrivate.lastExpectedOnset,
      cursorLastNoteXPosition: cursorPrivate.lastNoteXPosition,
      iterCurrentTimeStamp: cursorPrivate.iterator?.currentTimeStamp,
      globalPausedTimestamp,
      phraseStartTimestamp: targetPhrase.data.startTimestamp,
    });
    this.updateMobileCameraScroll();
    return globalPausedTimestamp;
  }

  // ---------------------------------------------------------------------------
  // MIDI handlers
  // ---------------------------------------------------------------------------

  async noteOn(payload: MidiOnPayload): Promise<void> {
    const { note, velocity, source, observedAtMs } = payload;
    Logger.debug(
      `[LOOP-DBG] noteOn entry note=${note} playbackStarted=${this.playbackStarted} shouldUpdate=${this.shouldUpdate} isLoopCountInActive=${this.isLoopCountInActive} countInActive=${this.countInActive} isLoopingActive=${this.isLoopingActive} mainTKPaused=${this.eventStream?.timeKeeper?.isPaused()}`,
    );

    if (this.autoPlaybackCountInTimer) {
      return;
    }

    if (this.queueMidiDuringPlaybackStart({ type: 'on', payload })) {
      return;
    }

    // Buffer every note during looping so restartLoop can replay notes
    // (including chords) that arrive just before the loop boundary.
    if (this.isLoopingActive && velocity !== undefined) {
      const now = performance.now();
      // Prune notes older than the buffer window
      this.recentNoteBuffer = this.recentNoteBuffer.filter((n) => now - n.time < 300);
      this.recentNoteBuffer.push({ payload, time: now });
    }

    if (this.playbackStarted) {
      Logger.debug(
        `[LOOP-DBG] noteOn — PLAYBACK STARTED path (forwarding to eventStream) note=${note}`,
      );
      Logger.debug('[OSMD-MIDI] Scene noteOn — forwarding to eventStream.handleNoteOnEvent', {
        eventStreamId: this.eventStream?.id,
        totalNotesInStream: this.eventStream?.totalNotesLength() ?? 'N/A',
        timeKeeperPaused: this.eventStream?.timeKeeper?.isPaused(),
        timeKeeperTimestamp: this.eventStream?.timeKeeper?.audioTimeToMeasureTimestamp(),
        errorRecognitionActive: this.eventStream?.errorRecognitionActive,
      });
      if (velocity !== undefined) {
        this.eventStream.handleNoteOnEvent(note, velocity, { source, observedAtMs });
      }

      // If the user scrolled away during playback, snap the view back to
      // the active phrase so they can see what they're playing.
      this.ensureActivePhraseVisible();
    } else if (this.isLoopCountInActive && this.shouldUpdate && this.eventStream !== undefined) {
      // MUS-243: First note during loop count-in wait. Dispatching NOTE_PLAYED
      // synchronously transitions the machine to `playing`; the machine's
      // actions handle TK unpause (at bracket start), Scheduler2 unpause,
      // cursor unpause, and error-recognition restore. After dispatch returns
      // we forward the note to the event stream — TK is now running so the
      // onset matches correctly.
      this.playbackStartPending = true;
      this.dispatchPlaybackEvent({ type: 'NOTE_PLAYED' });
      await this.waitForSchedulerUnpause();

      this.playbackStarted = true;
      this.hasEverPlayed = true;
      this.isLoopCountInActive = false;
      this.countInActive = false;

      this.phrases[this.activePhrase]?.activate();

      // Container's StartedPlaying listener starts osmdEventStream.update()
      // (the missed-note → NEVER_PLAYED → red sweeper) and the accuracy
      // update loop. Without this emission, the count-in → playing
      // transition never arms those loops and missed notes never go red.
      NotifyReactEvents.StartedPlaying(undefined);

      if (velocity !== undefined) {
        this.forwardFirstNoteWhenTimeKeeperReady(payload);
      }
      this.playbackStartPending = false;
      this.flushPendingPlaybackStartEvents();

      // Scroll to the bracket start in case the user scrolled away
      this.scrollToShowBracketRange();
    } else if (this.shouldUpdate && this.eventStream !== undefined) {
      // MUS-243: First note during normal (initial) count-in. Dispatching
      // NOTE_PLAYED synchronously transitions the machine to `playing`; the
      // machine's actions handle aux-TK pause, main-TK unpause, Scheduler2
      // unpause, and cursor unpause. After dispatch returns we forward the
      // note to the event stream — TK is now running so the onset matches.
      this.playbackStartPending = true;
      this.dispatchPlaybackEvent({ type: 'NOTE_PLAYED' });
      await this.waitForSchedulerUnpause();

      this.playbackStarted = true;
      this.hasEverPlayed = true;
      this.countInActive = false;

      const startPhraseIndex = this.isLoopingActive ? this.activePhrase : 0;
      this.phrases[startPhraseIndex]?.activate();

      // Container's StartedPlaying listener starts osmdEventStream.update()
      // (the missed-note → NEVER_PLAYED → red sweeper) and the accuracy
      // update loop. Without this emission, the count-in → playing
      // transition never arms those loops and missed notes never go red.
      NotifyReactEvents.StartedPlaying(undefined);

      if (velocity !== undefined) {
        this.forwardFirstNoteWhenTimeKeeperReady(payload);
      }
      this.playbackStartPending = false;
      this.flushPendingPlaybackStartEvents();

      this.ensureActivePhraseVisible();
    } else {
      Logger.debug(
        `[LOOP-DBG] noteOn — IGNORED shouldUpdate=${this.shouldUpdate} playbackStarted=${this.playbackStarted}`,
      );
    }
  }

  /**
   * Forward the count-in's first note into the event stream once the main
   * TimeKeeper is actually running.
   *
   * On iOS the TimeKeeper unpause is asynchronous: `unpauseScheduler2At` →
   * `Scheduler2.unpauseTicks()` must await `MuseFlowAudio.startPlayback()` to
   * read the native start time before it can unpause. The machine dispatch that
   * drives the count-in → playing transition returns *before* that promise
   * resolves, so forwarding the first note synchronously runs
   * `handleNoteOnEvent` while the TimeKeeper is still paused — and it bails,
   * dropping the very first note (iOS-only; web unpauses synchronously). We
   * chain off the promise `unpauseScheduler2At` captured in
   * `pendingScheduler2Unpause` so the first note matches its onset bucket. On
   * web that field is null and the note forwards immediately, exactly as before.
   */
  private forwardFirstNoteWhenTimeKeeperReady(payload: MidiOnPayload): void {
    const { note, velocity, source, observedAtMs } = payload;
    const pendingUnpause = this.pendingScheduler2Unpause;
    this.pendingScheduler2Unpause = null;
    if (!pendingUnpause) {
      this.eventStream.handleNoteOnEvent(note, velocity, { source, observedAtMs });
      return;
    }
    console.log(
      `[LOOP-DBG] noteOn — deferring first note=${note} until native TimeKeeper unpause resolves`,
    );
    pendingUnpause
      .then(() => {
        console.log(
          `[LOOP-DBG] noteOn — native unpause resolved, forwarding first note=${note} mainTKPaused=${this.eventStream?.timeKeeper?.isPaused()}`,
        );
        this.eventStream?.handleNoteOnEvent(note, velocity, { source, observedAtMs });
      })
      .catch((err: unknown) => {
        Logger.warn('[OSMDPlayableScene] deferred first-note forward failed', err);
      });
  }

  noteOff(payload: MidiOffPayload): void {
    const { note, source, observedAtMs } = payload;
    if (this.queueMidiDuringPlaybackStart({ type: 'off', payload })) {
      return;
    }

    if (this.playbackStarted) {
      this.eventStream.handleNoteOffEvent(note, { source, observedAtMs });
    }
  }

  /**
   * Flush queued MIDI events through the event stream.
   */
  processEventsQueue(): void {
    Logger.debug('[OSMD-MIDI] Scene processEventsQueue', {
      queueLength: this.eventsQueue.length,
      events: this.eventsQueue,
      eventStreamId: this.eventStream?.id,
      totalNotesInStream: this.eventStream?.totalNotesLength() ?? 'N/A',
      timeKeeperPaused: this.eventStream?.timeKeeper?.isPaused(),
      timeKeeperTimestamp: this.eventStream?.timeKeeper?.audioTimeToMeasureTimestamp(),
      errorRecognitionActive: this.eventStream?.errorRecognitionActive,
    });
    this.eventsQueue.forEach(({ note: midiNote, velocity: midiVelocity, source, observedAtMs }) => {
      Logger.debug('[OSMD-MIDI] Scene processEventsQueue — processing event', {
        midiNote,
        midiVelocity,
        eventStreamId: this.eventStream?.id,
        eventStreamConstructor: this.eventStream?.constructor?.name,
      });
      if (midiVelocity === 0) {
        this.eventStream.handleNoteOffEvent(midiNote, { source, observedAtMs });
      } else {
        Logger.debug('[OSMD-MIDI] Scene processEventsQueue — calling handleNoteOnEvent NOW');
        this.eventStream.handleNoteOnEvent(midiNote, midiVelocity, { source, observedAtMs });
        Logger.debug('[OSMD-MIDI] Scene processEventsQueue — handleNoteOnEvent returned');
      }
    });
    this.eventsQueue = [];
  }

  // ---------------------------------------------------------------------------
  // Note state management
  // ---------------------------------------------------------------------------

  /**
   * Get a NoteContainer by ID. Implements INoteContainerProvider.
   * Searches all phrases linearly.
   */
  getNoteContainer(id: string): NoteContainer | undefined {
    for (const phrase of this.phrases) {
      const noteContainer = phrase.getNoteContainer(id);
      if (noteContainer) {
        return noteContainer;
      }
    }
    Logger.warn('[OSMDPlayableScene] getNoteContainer — not found:', id);
    return undefined;
  }

  /**
   * Get a playable note by its VexFlow ID.
   * Searches all phrases linearly.
   */
  getNoteById(id: string, _phraseTimestamp?: number): IPlayable | null {
    for (const phrase of this.phrases) {
      const noteContainer = phrase.getNoteContainer(id);
      if (noteContainer) {
        return new NoteContainerPlayable(noteContainer);
      }
    }
    return null;
  }

  /**
   * Get the end position of the rightmost phrase.
   * Implements INoteGraphicsDatabase.
   */
  getPhraseEndPosition(): number {
    if (this.phrases.length === 0) return 0;
    return Math.max(...this.phrases.map((phrase) => phrase.getPhraseEndPosition()));
  }

  /**
   * Get the X position of a note by its VexFlow ID.
   * Implements INoteGraphicsDatabase.
   */
  getNotePosition(id: string, timestamp?: number, rest?: boolean): number | null {
    for (const phrase of this.phrases) {
      const xPos = phrase.getNoteXPosition(id, timestamp, rest);
      if (xPos !== undefined) {
        return xPos;
      }
    }
    return null;
  }

  setNoteState({ id, state }: { id: string; state: ProgressState }): void {
    const note = this.getNoteById(id);
    note?.setTimingState(state);
  }

  pulseNote({ id }: { id: string }): void {
    const note = this.getNoteById(id);
    note?.pulse();
  }

  // ---------------------------------------------------------------------------
  // Soft reset
  // ---------------------------------------------------------------------------

  /**
   * Reset the scene to its initial state without destroying and re-creating phrases.
   * Resets note colors, event stream, cursors, and camera position.
   */
  softReset = (): void => {
    // A lazy phrase batch may still be replacing note containers/cursors.
    // Reset immediately for responsiveness, then repeat once that batch
    // commits so its late mutations cannot undo this reset.
    if (this.lazyPhraseLoadPromise) {
      this.softResetPendingAfterLazyLoad = true;
    }
    this.waitingForLazyPhraseAdvance = false;

    // MUS-243: stop the machine first. After a playthrough it's in `playing`
    // (or `paused`) with the main TK still running. SOFT_REFRESH → `stopped`
    // pauses main/aux TK + Scheduler2 + cursor and clears play-progress
    // context, so the cursor reset below isn't immediately overrun and the
    // subsequent USER_PLAY enters a fresh flexible-note count-in.
    this.dispatchPlaybackEvent({ type: 'SOFT_REFRESH' });

    this.clearAutoPlaybackCountInTimer();
    this.playbackStartPending = false;
    this.pendingPlaybackStartEvents = [];
    this.pendingSchedulerUnpause = null;
    this.pendingStartExercisePayload = null;
    this.isAddingPhrases = false;

    // 1. Reset every NoteContainer in every phrase
    for (const phrase of this.phrases) {
      for (const noteContainer of phrase.noteContainers.values()) {
        noteContainer.reset();
      }
    }

    // 2. Reset the event stream
    this.eventStream.reset();

    // 2b. Reset each phrase's accumulated play data. addAllEventsToStream
    // below calls EventStream.addEvents2, which pushes every freshly-created
    // bucket into `phrase.playData` (only when EventStream.eventBuckets has
    // no matching bucket — and after the reset above, none exist). Without
    // this clear, every soft refresh appends a second copy of the same
    // buckets to each phrase, doubling RepPhraseCompleted error_data on
    // the next playthrough (and tripling on the one after that).
    //
    // Mirror the legacy `softRefreshRepertoire` (RepertoirePlayContext)
    // pattern: `Phrase.resetPlayData()` also zeroes `startTimestamp`, so
    // we save and restore it. Without the restore, addAllEventsToStream
    // below would pass `phraseModel.getStartTimestamp() === 0` as the
    // `startMeasure` arg to addEvents2 for every phrase, collapsing all
    // event buckets onto phrase 0's timestamp range. Phrase 1+'s events
    // would be silently dropped (matched against phrase 0's buckets via
    // the epsilon-find), and the cursor would stop advancing on line 2.
    //
    // The context's softRefreshRepertoire runs BEFORE this listener fires
    // and has already set `phraseModel.startTimestamp` to the correct
    // cumulative offset (including pickup-offset arithmetic), so capturing
    // it here gives us back the right value after `resetPlayData`.
    for (const phrase of this.phrases) {
      const startTimestamp = phrase.phraseModel.getStartTimestamp();
      phrase.phraseModel.resetPlayData();
      phrase.phraseModel.setStartTimestamp(startTimestamp);
    }

    // 3. Re-add events from all phrases with correct cumulative timestamps
    this.addAllEventsToStream();

    // 4. Reset active phrase index
    this.activePhrase = 0;

    // 5. Reset every cursor to its initial position
    for (const phrase of this.phrases) {
      if (phrase.cursor) {
        const cursorIterator = new OSMDIteratorExtensible(
          phrase,
          phrase.notePositions,
          phrase.phraseModel,
          phrase.data.timeSignature,
          phrase.data.startTimestamp,
        );
        phrase.cursor.reset(
          cursorIterator,
          phrase.data.startTimestamp,
          phrase.phraseStartingMeasure,
        );
        const firstNotePos = phrase.getFirstNotePosition();
        phrase.cursor.setLastNotePos(firstNotePos);
        phrase.cursor.setX(firstNotePos - phrase.cursor.width / 2);
        phrase.cursor.disable();
      }
    }

    // 6. Scroll camera to top / beginning
    this.cameras.main.scrollY = 0;

    // 7. Deactivate all phrases, then activate the first
    for (const phrase of this.phrases) {
      phrase.deactivate();
    }
    if (this.phrases.length > 0) {
      this.phrases[0].activate();
    }
    this.updateMobileCameraScroll();

    // Reset playback state
    this.playbackStarted = false;
    this.playbackStartPending = false;
    this.pendingPlaybackStartEvents = [];
    this.pendingSchedulerUnpause = null;
    this.countInActive = false;
    this.isLoopCountInActive = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
    this.lastTickTime = undefined;
    this.eventsQueue = [];
    this.pieceCompleted = false;
    this.repertoireCompletedEmitted = false;
    this.hasEverPlayed = false;
    this.isAutoScrolling = false;
    this.pendingPhraseTransition = null;
    this.cursorStartTimestamp = null;
    // Clear any user scroll offset so the camera follows the cursor from the
    // top again on this fresh playthrough. Without this, a user who scrolled
    // the score after finishing (now permitted) would leave the camera stuck
    // where they dragged it when the restart begins. (startExercise clears
    // this on the play-button path; softReset must do the same for the
    // soft-refresh restart path, which does not route through startExercise.)
    this.mobileUserScrolledX = false;

    // Auto-start the count-in so the cursor is immediately blinking and
    // ready for the user's first note — no separate "press play" step needed.
    this.shouldUpdate = true;
    this.beginCountIn();

    // Transition the machine into count-in state. Previously the auto-restart
    // came via React: softRefreshRepertoire dispatched setRepertoirePlaying(true)
    // → the play/pause bridge useEffect emitted StartExercise → scene.startExercise
    // → USER_PLAY. That round-trip was order-dependent and broke when callers
    // like playLevel-when-repertoireEnded dispatched their own setRepertoirePlaying
    // after awaiting handleSoftRefresh. Dispatching USER_PLAY here makes the
    // soft refresh self-contained: scene reset + machine transition both
    // happen synchronously inside softReset; the UI follows via the reducer's
    // SET_PLAYBACK_PHASE → repertoirePlaying mirror.
    this.dispatchPlaybackEvent({ type: 'USER_PLAY' });
  };

  // ---------------------------------------------------------------------------
  // Event stream helpers
  // ---------------------------------------------------------------------------

  /**
   * Add events for every phrase to the event stream.
   * Each phrase gets its own iterator, and events are offset by the phrase's start timestamp.
   */
  private addAllEventsToStream(): void {
    for (const phrase of this.phrases) {
      const iterator = new OSMDIteratorExtensible(
        phrase,
        phrase.notePositions,
        phrase.phraseModel,
        phrase.data.timeSignature,
        phrase.data.startTimestamp,
      );
      this.eventStream.addEvents2(iterator, phrase.phraseModel.getStartTimestamp());
    }
  }

  // ---------------------------------------------------------------------------
  // Resize
  // ---------------------------------------------------------------------------

  /**
   * Resize the scene and re-layout all phrases.
   */
  private resize(props: { width: number; height: number; playButtonOverlap?: number }): void {
    const hasSizeActuallyChanged =
      Math.abs(props.width - (this.lastResizeProps?.width ?? 0)) > 1 ||
      Math.abs(props.height - (this.lastResizeProps?.height ?? 0)) > 1;
    console.debug('last resize', this.lastResizeProps, props, hasSizeActuallyChanged);
    if (!hasSizeActuallyChanged) return;
    this.lastResizeProps = props;
    const scaledWidth = props.width * window.devicePixelRatio;
    const scaledHeight = props.height * window.devicePixelRatio;

    this.width = props.width;
    this.height = props.height;
    this.scale.resize(scaledWidth, scaledHeight);

    // Scale.NONE + crisp: the backing store is DPR-scaled while the canvas CSS
    // size must track the logical viewport. reconfigureScale() sets this at
    // mount, but scale.resize() above overwrites the CSS to the DPR-scaled
    // size, leaving the canvas oversized (and overflowing) after a rotation.
    // Re-pin the CSS size to the logical viewport so the canvas fits.
    const canvas = this.game.canvas;
    if (canvas) {
      canvas.style.width = `${props.width}px`;
      canvas.style.height = `${props.height}px`;
    }

    // Explicitly update the camera viewport to match the new canvas dimensions.
    // Phaser's scale.resize() propagates to cameras via the RESIZE event, but
    // OSMDSightReadingScene notes that "camera dimensions can be stale or out
    // of sync after resize." This explicit call guarantees that layoutPhrases()
    // reads the correct viewport width/height from the camera.
    this.cameras.main.setSize(scaledWidth, scaledHeight);

    // mobileView is fixed at init (phone vs. not) and never changes here:
    // phones are locked to landscape and iPads always use the desktop layout,
    // so an orientation change just re-runs the same layout at the new size.
    if (this.phrases.length === 0) return;

    this.layoutPhrases();
    this.updateCursorDimensions();
    this.prefetchForCurrentViewport();

    // Paused mid-piece: the re-layout moved every note, so put the cursor
    // back on the note playback resumes from (the resume and its count-in
    // ring lead into that note).
    if (
      this.playbackStarted &&
      !this.isLoopingActive &&
      this.lastPauseTimestamp !== null &&
      this.pausedCursorMeasure !== null
    ) {
      this.placePausedCursorAtMeasure(this.pausedCursorMeasure);
    }

    // After re-layout, scroll to keep the active phrase visible.
    // For the first phrase, stay at scrollY = 0 so the title remains visible.
    if (this.playbackStarted && this.activePhrase < this.phrases.length) {
      if (this.activePhrase === 0) {
        this.cameras.main.scrollY = 0;
      } else {
        const activeContainer = this.phrases[this.activePhrase].getContainer();
        const resizeDpr = window.devicePixelRatio;
        const resizeToolbarClearance = Math.round(120 * resizeDpr);
        this.cameras.main.scrollY = Math.max(0, activeContainer.y - resizeToolbarClearance);
      }
    }

    // After re-layout, reposition the cursor and bracket if looping is active.
    // The layout changes container coordinates, making the cursor position stale.
    if (this.isLoopingActive && this.bracketOverlay) {
      const bracketStart = this.bracketOverlay.getStartTimestamp();
      const bracketEnd = this.bracketOverlay.getEndTimestamp();
      if (bracketStart < bracketEnd) {
        const layoutInfo = this.getPhrasesLayoutInfo();
        this.bracketOverlay.updatePosition(bracketStart, bracketEnd, layoutInfo);
        if (!this.playbackStarted) {
          this.positionCursorAtBracketStart(bracketStart);
        }
      }
    }
  }

  /**
   * Update cursor dimensions and positions for all phrases after a layout change.
   */
  /**
   * Compute the actual vertical extent of staff lines from OSMD's GraphicSheet.
   * Returns { topY, bottomY } in scaled coordinates relative to the phrase
   * container's origin, or undefined if OSMD data is unavailable.
   *
   * Uses OSMD's StaffLine.PositionAndShape.AbsolutePosition to find the top
   * of the first (treble) staff and the bottom of the last (bass) staff.
   * This accounts for the full staff line extent including lines above/below
   * the outermost notes — unlike notePositions which only give note head centers.
   */
  private getStaffContentBounds(
    phrase: OSMDPlayablePhrase,
  ): { topY: number; bottomY: number } | undefined {
    const osmd = phrase.osmd;
    if (!osmd?.GraphicSheet) return undefined;

    const unitInPixels = 10;
    const scale = phrase.container.scaleX;
    const singleStaffPixelHeight = osmd.EngravingRules.StaffHeight * unitInPixels;

    const pages = osmd.GraphicSheet.MusicPages;
    if (pages.length === 0) return undefined;

    let firstStaffTopY = Infinity;
    let lastStaffBottomY = -Infinity;

    for (const page of pages) {
      for (const system of page.MusicSystems) {
        for (const staffLine of system.StaffLines) {
          const absPos = staffLine.PositionAndShape.AbsolutePosition;

          const staffTopYScaled = absPos.y * unitInPixels * scale;
          const staffBottomYScaled = (absPos.y * unitInPixels + singleStaffPixelHeight) * scale;

          firstStaffTopY = Math.min(firstStaffTopY, staffTopYScaled);
          lastStaffBottomY = Math.max(lastStaffBottomY, staffBottomYScaled);
        }
      }
    }

    if (firstStaffTopY === Infinity) return undefined;
    return { topY: firstStaffTopY, bottomY: lastStaffBottomY };
  }

  private updateCursorDimensions(): void {
    for (let idx = 0; idx < this.phrases.length; idx++) {
      const phrase = this.phrases[idx];
      const phraseCursor = phrase.cursor;
      if (!phraseCursor) {
        continue;
      }

      const actualPhraseHeight = phrase.getScaledHeight();
      const phraseContainerY = phrase.container.y;
      const staffBounds = this.getStaffContentBounds(phrase);

      if (staffBounds) {
        // ---- OSMD-driven approach: size and center the cursor based on
        // the actual staff line positions from OSMD's GraphicSheet. ----
        // staffBounds.topY = top of first (treble) staff's top line (scaled)
        // staffBounds.bottomY = bottom of last (bass) staff's bottom line (scaled)

        const staffSpan = staffBounds.bottomY - staffBounds.topY;
        const staffMidpoint = (staffBounds.topY + staffBounds.bottomY) / 2;

        // The OSMD staff bounds can extend past the phrase's rendered height
        // (because calculateOriginalHeight uses bbox, not StaffHeight).
        // Nudge the midpoint up by 5% of the staff span to compensate for
        // this overshoot and visually center the cursor on the staves.
        const midpointUpwardShift = staffSpan * 0.05;
        const adjustedMidpoint = staffMidpoint - midpointUpwardShift;

        // Small padding (5%) to give the cursor a bit of breathing room
        // beyond the outermost staff lines
        const staffPadding = 0.05;
        const cursorHeight = staffSpan * (1 + staffPadding);

        phraseCursor.height = cursorHeight;
        phraseCursor.width = cursorHeight / 10;
        phraseCursor.radius = phraseCursor.width / 2;

        // Center the cursor on the adjusted midpoint
        const cursorY = phraseContainerY + adjustedMidpoint - cursorHeight / 2;
        phraseCursor.setY(cursorY);
      } else {
        // ---- Fallback: no notePositions available (should be rare). ----
        // Use a simple fraction of phrase height, centered at 50%.
        const fallbackHeightFraction = 0.85;
        phraseCursor.height = actualPhraseHeight * fallbackHeightFraction;
        phraseCursor.width = phraseCursor.height / 10;
        phraseCursor.radius = phraseCursor.width / 2;

        const cursorY = phraseContainerY + actualPhraseHeight * 0.5 - phraseCursor.height / 2;
        phraseCursor.setY(cursorY);
      }

      if (!this.playbackStarted) {
        const firstNote = phrase.getFirstNotePosition();
        if (firstNote !== undefined) {
          phraseCursor.setLastNotePos(firstNote);
          phraseCursor.setX(firstNote - phraseCursor.width / 2);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  /**
   * Clear all loaded piece data and reset playback state without destroying
   * the scene. Called when the user navigates to a different repertoire piece
   * so the scene can be reused with new phrase data.
   */
  private unloadPiece(): void {
    console.log(
      `[LOOP-DBG] unloadPiece() called phrasesCount=${this.phrases?.length} isLoopingActive=${this.isLoopingActive}`,
    );
    this.phraseLoadGeneration++;
    this.totalPhraseCount = 0;
    this.pendingPhraseDatas = [];
    this.lazyPhraseLoadPromise = null;
    this.waitingForLazyPhraseAdvance = false;
    this.softResetPendingAfterLazyLoad = false;
    this.clearAutoPlaybackCountInTimer();
    this.playbackStartPending = false;
    this.pendingPlaybackStartEvents = [];
    this.pendingSchedulerUnpause = null;
    this.pendingStartExercisePayload = null;
    this.isAddingPhrases = false;
    this.phrases.forEach((phrase, idx) => {
      console.log(
        `[LOOP-DBG] unloadPiece — destroying phrase ${idx} container=${!!phrase?.container} notePositions=${phrase?.notePositions?.length}`,
      );
      try {
        phrase.destroy();
      } catch (err) {
        console.error(`[LOOP-DBG] unloadPiece — phrase ${idx} destroy FAILED:`, err);
      }
    });
    this.phrases = [];

    this.playbackStarted = false;
    this.playbackStartPending = false;
    this.pendingPlaybackStartEvents = [];
    this.pendingSchedulerUnpause = null;
    this.shouldUpdate = false;
    this.pauseQueued = false;
    this.pieceCompleted = false;
    this.repertoireCompletedEmitted = false;
    this.pendingPhraseTransition = null;
    this.measureCount = 0;
    this.maxPhraseContainerWidth = 0;
    this.activePhrase = 0;
    this.countInActive = false;
    this.isLoopCountInActive = false;

    ClearQueuePhaser('AddAllOSMDPhrases');
    ClearQueuePhaser('AddPhraseOSMD');

    if (this.bracketOverlay) {
      this.bracketOverlay.hide();
    }
    this.isLoopingActive = false;

    if (this.cameras?.main) {
      this.cameras.main.scrollY = 0;
    }
    this.logMemoryDiagnostics('After unloadPiece');
  }

  // ---------------------------------------------------------------------------
  // MUS-243: Playback state machine integration
  //
  // The XState machine is the authoritative owner of the playback lifecycle.
  // Side-effect actions defined on the machine route through this scene's
  // `IPlaybackSceneFacade` implementation below, which calls the same
  // subsystems (timekeepers, scheduler2, cursor, event stream) that the
  // legacy imperative code paths call.
  //
  // Phase 2 status: machine instantiated, facade implemented, but the
  // legacy entry points (startExercise, _pauseExercise, restartLoop,
  // restartLoopFromBeginning, noteOn LOOP_COUNT_IN_PATH, handleBracketChanged)
  // are STILL in place driving real behavior. The machine runs but is not
  // yet wired to receive intent events from the container — that's Phase 3.
  // ---------------------------------------------------------------------------

  /** Scheduler2 reference injected by `RepertoirePlayContainerOSMD` after the
   *  scene is created. Null until injection. Used by machine actions for the
   *  Scheduler2-related operations (the scene doesn't otherwise own this). */
  private scheduler2: Scheduler | null = null;
  private pendingSchedulerUnpause: Promise<void> | null = null;

  /** Ungraded play (Play Along): read from the count-in input on each play. */
  private ungradedPlay = false;
  /** Ungraded play: the cursor waits on its note through each count-in. */
  private countInCursorHoldInstance?: CountInCursorHold;
  /** Created on first use: scene test fixtures build scenes without field initialisers. */
  private get countInCursorHold(): CountInCursorHold {
    this.countInCursorHoldInstance ??= new CountInCursorHold();
    return this.countInCursorHoldInstance;
  }
  /** Ungraded play: the count-in's countdown number and closing ring. */
  private countInCountdownInstance?: CountInCountdownView;
  /** The measure the paused cursor waits on (global index), set by the pause rewind. */
  private pausedCursorMeasure: number | null = null;
  private get countInCountdown(): CountInCountdownView {
    this.countInCountdownInstance ??= new CountInCountdownView(this, 'aboveRightOfCursor');
    return this.countInCountdownInstance;
  }

  /** The running playback machine actor. Instantiated when `setScheduler2`
   *  is called (since some actions depend on scheduler2). Cleaned up in
   *  `destroy()`. */
  private playbackActor: ActorRefFrom<typeof playbackMachine> | null = null;

  /** OSMD timestamp captured on pause. Used by `unpauseMainTKAt` and
   *  `unpauseScheduler2At` to resume at the exact paused position.
   *  Set by `positionCursorAtLastCompletedMeasure` facade method (paused
   *  state entry). Cleared by `positionCursorAtBracketStart` (entering
   *  count-in wait) and `clearScheduler2` (entering stopped). */
  private lastPauseTimestamp: number | null = null;

  /** Inject the Scheduler2 instance from the container. Must be called once
   *  before the machine can fully function. Triggers machine instantiation. */
  setScheduler2(scheduler: Scheduler): void {
    this.scheduler2 = scheduler;
    if (this.playbackActor === null) {
      this.startPlaybackMachine();
    }
  }

  private trackSchedulerUnpause(result: Promise<void> | void): void {
    if (result && typeof result.then === 'function') {
      const pending = result;
      this.pendingSchedulerUnpause = pending;
      pending.then(
        () => {
          if (this.pendingSchedulerUnpause === pending) {
            this.pendingSchedulerUnpause = null;
          }
        },
        () => {
          if (this.pendingSchedulerUnpause === pending) {
            this.pendingSchedulerUnpause = null;
          }
        },
      );
      return;
    }

    this.pendingSchedulerUnpause = null;
  }

  private waitForSchedulerUnpause(): Promise<void> {
    return this.pendingSchedulerUnpause ?? Promise.resolve();
  }

  private queueMidiDuringPlaybackStart(
    event: { type: 'on'; payload: MidiOnPayload } | { type: 'off'; payload: MidiOffPayload },
  ): boolean {
    if (!this.playbackStartPending) return false;

    this.pendingPlaybackStartEvents.push(event);
    return true;
  }

  private flushPendingPlaybackStartEvents(): void {
    const events = this.pendingPlaybackStartEvents.splice(0);
    for (const event of events) {
      if (event.type === 'on') {
        void this.noteOn(event.payload);
      } else {
        this.noteOff(event.payload);
      }
    }
  }

  /** Get the current PlaybackPhase from the running machine. Returns
   *  'stopped' if the machine hasn't been started yet. */
  getPlaybackPhase(): PlaybackPhase {
    if (this.playbackActor === null) return 'stopped';
    return this.playbackActor.getSnapshot().value as PlaybackPhase;
  }

  /** True while the cursor is actively advancing (`playing`) or the score is
   *  in a count-in / loop-restart "flashing" stage. Mobile score scrolling is
   *  suppressed during these phases; `paused` and `stopped` (including after
   *  the piece has completed) allow the user to scroll freely. */
  private isPlaybackActivelyRunning(): boolean {
    const phase = this.getPlaybackPhase();
    return (
      phase === 'playing' ||
      phase === 'normalCountIn' ||
      phase === 'loopCountInWait' ||
      phase === 'loopBoundaryRestart'
    );
  }

  /** Send a user-intent or scene-internal event to the playback machine.
   *  No-op if the machine hasn't been started yet (scheduler2 not injected). */
  dispatchPlaybackEvent(event: PlaybackEvent): void {
    if (this.playbackActor === null) {
      console.warn(
        '[LOOP-DBG-243] dispatchPlaybackEvent called before machine started; event dropped',
        event,
      );
      return;
    }
    this.playbackActor.send(event);
  }

  /** Subscribe to playback phase changes. Returns an unsubscribe function. */
  onPlaybackPhaseChanged(handler: (phase: PlaybackPhase) => void): () => void {
    if (this.playbackActor === null) {
      console.warn(
        '[LOOP-DBG-243] onPlaybackPhaseChanged called before machine started; subscription will not fire',
      );
      return () => undefined;
    }
    let previousPhase: PlaybackPhase | null = null;
    const subscription = this.playbackActor.subscribe((snapshot) => {
      const phase = snapshot.value as PlaybackPhase;
      if (phase !== previousPhase) {
        previousPhase = phase;
        handler(phase);
      }
    });
    return () => subscription.unsubscribe();
  }

  private startPlaybackMachine(): void {
    const facade = this.createPlaybackFacade();
    const actions = buildSceneActions(facade);
    this.playbackActor = createActor(playbackMachine.provide({ actions }), {
      input: {
        tempo: this.tempo,
        isLoopingActive: this.isLoopingActive,
        bracketStartTimestamp: this.bracketOverlay?.getStartTimestamp() ?? null,
        bracketEndTimestamp: this.bracketOverlay?.getEndTimestamp() ?? null,
      },
    }).start();

    // MUS-243: emit PlaybackPhaseChanged on every transition so the React
    // Redux mirror (`repertoireReducer.playbackPhase`) stays in sync.
    let previousPhase: PlaybackPhase | null = null;
    this.playbackActor.subscribe((snapshot) => {
      const phase = snapshot.value as PlaybackPhase;
      if (phase === previousPhase) return;
      const prior = previousPhase;
      previousPhase = phase;

      // Perfect-phrase lifecycle hooks driven off playback phase transitions:
      //  - A fresh loop iteration begins when a loop count-in resolves into
      //    `playing` (every iteration goes count-in → playing, including the
      //    first). A resume from `paused` re-enters `playing` too — exclude it.
      //  - Track pause duration so a resume after >60s drops the streak.
      if (snapshot.context.isLoopingActive) {
        if (phase === 'playing' && prior !== 'paused') {
          // Fresh iteration: clear the perfect latch so it must be re-earned by
          // playing this iteration's notes cleanly.
          console.log(
            `[PPC] trigger=LOOP_STARTED (phase ${prior}→playing) — new iteration, clearing latch`,
          );
          this.perfectLoopLatched = false;
          this.prevPerfectTickOk = null;
          this.perfectPhrase?.onLoopStarted();
        }
        if (phase === 'paused') {
          console.log('[PPC] paused — starting streak pause-grace timer');
          this.perfectPhrasePausedAt = performance.now();
        } else if (prior === 'paused' && this.perfectPhrasePausedAt !== null) {
          const pausedFor = performance.now() - this.perfectPhrasePausedAt;
          this.perfectPhrasePausedAt = null;
          console.log(
            `[PPC] resumed from pause pausedFor=${Math.round(pausedFor)}ms graceMs=${OSMDPlayableScene.PERFECT_PHRASE_PAUSE_GRACE_MS} exceeded=${pausedFor > OSMDPlayableScene.PERFECT_PHRASE_PAUSE_GRACE_MS}`,
          );
          if (pausedFor > OSMDPlayableScene.PERFECT_PHRASE_PAUSE_GRACE_MS) {
            this.resetPerfectPhrase('pause-grace-exceeded');
          }
        }
      }

      if (phase === 'paused' || phase === 'stopped') {
        this.countInCursorHold.cancel();
        this.countInCountdown.clear();
      }

      NotifyReactEvents.PlaybackPhaseChanged({
        phase,
        isLoopingActive: snapshot.context.isLoopingActive,
        hasEverPlayedInThisLoop: snapshot.context.hasEverPlayedInThisLoop,
      });
    });

    console.log(
      `[LOOP-DBG-243] Started. initialPhase=${this.getPlaybackPhase()} tempo=${this.tempo} isLooping=${this.isLoopingActive}`,
    );
  }

  /** Build the facade — the surface the machine uses to call into scene
   *  subsystems. Each method extracts the existing imperative pattern from
   *  the legacy entry points so behavior is preserved when the machine
   *  eventually drives the scene end-to-end. */
  private createPlaybackFacade(): IPlaybackSceneFacade {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const scene = this;
    return {
      // ---- Aux TimeKeeper ----
      armAuxTK(tempo, tickNoteType) {
        if (!scene.timeKeeperAuxiliary) return;
        scene.timeKeeperAuxiliary.resetTime();
        scene.timeKeeperAuxiliary.unpause(tempo, 0, 0, tickNoteType ?? NoteType._32nd);
        scene.lastTickTime = scene.timeKeeperAuxiliary?.audioTimeToMeasureTimestamp() ?? 0;
      },
      pauseAuxTK() {
        scene.timeKeeperAuxiliary?.pause();
      },

      // ---- Main TimeKeeper ----
      pauseMainTK() {
        if (!scene.eventStream.timeKeeper.isPaused()) {
          scene.eventStream.timeKeeper.pause();
        }
      },
      unpauseMainTKAt(timestamp, tempo) {
        // For the NON-looping path, `scheduler2.unpauseTicks()` (called next
        // by `unpauseScheduler2At`) performs the main-TK unpause itself AND
        // schedules the metronome + creates the tick interval. If we unpause
        // the TK here first, `unpauseTicks()` sees "TK already running, no
        // loop bounds" and bails out (Scheduler.ts) — no metronome, no tick
        // interval (cursor still moves because the TK is running). So skip
        // the pre-unpause when not looping and let unpauseTicks own it.
        //
        // For the LOOPING path, `unpauseTicks()` takes its "TK already
        // running WITH loop bounds" branch (it only re-arms the tick
        // interval), so the TK must already be running — unpause it here at
        // the bracket-start / resume position.
        if (!scene.isLoopingActive) {
          return;
        }
        // Ungraded on iOS / Android: the native start (unpauseScheduler2At,
        // next) places the transport after the count-in; unpausing here first
        // would start the transport, and release the cursor, before it.
        if (scene.scheduler2?.defersTimedStartToNative()) {
          return;
        }
        const effective = scene.lastPauseTimestamp ?? timestamp;
        const measureDurationAtZero = scene.eventStream.timeKeeper.measureDurationAt(0);
        const startMeasure = measureDurationAtZero !== 0 ? effective / measureDurationAtZero : 0;
        const tickNoteType = scene.eventStream.timeKeeper.getTickNoteType();
        // Ungraded play: start after the count-in the scene asked for.
        const timedStart = scene.scheduler2?.takeTimedStart(tempo, startMeasure);
        scene.eventStream.timeKeeper.unpause(
          tempo,
          0,
          startMeasure,
          tickNoteType,
          ...(timedStart ? [timedStart.startAudioTime] : []),
        );
      },

      // ---- Scheduler2 ----
      pauseScheduler2() {
        scene.scheduler2?.cancelQueuedCountIn();
        scene.scheduler2?.stopNativeScheduler();
      },
      unpauseScheduler2At(measureTimestamp, tempo) {
        if (scene.scheduler2 === null) return;
        const effective = scene.lastPauseTimestamp ?? measureTimestamp;
        const measureDur = scene.eventStream.timeKeeper.measureDurationAt(0);
        const loopStartMeasure = measureDur > 0 ? effective / measureDur : 0;
        const tickNoteType = scene.eventStream.timeKeeper.getTickNoteType();
        // Seed lastPauseMeasure exactly like the legacy
        // `context.startScheduler()` did. In the non-looping path
        // `unpauseTicks()` unpauses the TK at `effectiveStartMeasure`, which
        // (with no loop bounds) is `lastPauseMeasure`. Its construction-time
        // default is a negative count-in value that would produce negative
        // timestamps and miss every note; set it to the real start measure.
        scene.scheduler2.lastPauseMeasure = loopStartMeasure;
        // Capture the unpause result. On iOS this is a Promise that resolves
        // only after the native scheduler awaits `MuseFlowAudio.startPlayback()`
        // and unpauses the main TimeKeeper; on web it is `void` (the unpause is
        // synchronous). `noteOn` chains the count-in's first-note forward off
        // this promise so it does not run while the TimeKeeper is still paused
        // (the iOS first-note drop). See `pendingScheduler2Unpause`.
        scene.trackSchedulerUnpause(
          scene.scheduler2.unpauseTicks(tempo, 0, loopStartMeasure, tickNoteType),
        );
        // Resume complete: both TK and scheduler now running at the resume
        // point. Forget the captured pause timestamp.
        scene.lastPauseTimestamp = null;
      },
      clearScheduler2() {
        scene.pausedCursorMeasure = null;
        if (scene.scheduler2 === null) return;
        scene.scheduler2.clearLoopBounds();
        scene.scheduler2.forceStopTickInterval();
        scene.scheduler2.lastPauseMeasure = 0;
        scene.lastPauseTimestamp = null;
        // A clean slate must not carry a pending non-loop piece-end timeout.
        scene.scheduler2.clearTimeoutByName('endRepTimeout');
        // On iOS the native TimeKeeper runs its own audio-thread metronome
        // independently of the JS tick interval. forceStopTickInterval only
        // kills the JS side, so without this the native metronome keeps
        // playing after the machine transitions to `stopped`. Match the
        // facade's existing `pauseScheduler2` (which also calls this
        // unconditionally) so `stopped.entry` (clearScheduler2 + pauseMainTK,
        // not pauseScheduler2) leaves no native audio running. The call is
        // safe on web — Scheduler.stopNativeScheduler wraps the underlying
        // MuseFlowAudio.pausePlayback() in a try/catch that logs and swallows
        // any error from the unavailable native plugin.
        scene.scheduler2.stopNativeScheduler();
      },
      setScheduler2LoopBounds(start, end) {
        if (scene.scheduler2 === null) return;
        // Use the scene's existing effective-end computation so the bracket-end
        // note has time to sound before the loop restart fires.
        const effectiveEnd = scene.computeEffectiveLoopEnd(end);
        scene.scheduler2.setLoopBounds(start, effectiveEnd);
        scene.scheduler2.lastScheduledBeat = -10000;
        // Engaging loop bounds means we're looping now. The context's
        // `onCalcTheEnd` registers a non-loop `endRepTimeout` during a prior
        // non-loop session; pause does NOT clear it (pauseTicks only clears
        // the tick interval). Left alive, it keeps counting when loop
        // playback resumes and fires mid-loop, spuriously pausing the
        // machine-driven loop. Invalidate it here.
        scene.scheduler2.clearTimeoutByName('endRepTimeout');
      },

      // ---- Cursor ----
      enableCursorBlink(beatDuration) {
        const cursor = scene.phrases[scene.activePhrase]?.cursor;
        if (!cursor) return;
        cursor.enable();
        cursor.blink(beatDuration);
      },
      unpauseCursor() {
        const cursor = scene.phrases[scene.activePhrase]?.cursor;
        if (!cursor) return;
        cursor.enable();
        cursor.unpause();
      },
      pauseCursor() {
        scene.phrases[scene.activePhrase]?.cursor?.pause();
      },
      positionCursorAtBracketStart(timestamp) {
        scene.pausedCursorMeasure = null;
        scene.positionCursorAtBracketStart(timestamp);
        // Cursor moving to bracket start means we're starting fresh — forget
        // any prior pause point so the next play doesn't misuse stale state.
        scene.lastPauseTimestamp = null;
      },
      positionCursorAtLastCompletedMeasure() {
        // MUS-243: when the piece has finished playing, skip the
        // last-measure rewind entirely — the cursor should fade out at the
        // end of the last note, not jump back. Without this guard, the
        // scheduler's endRepTimeout fires PauseExercise → USER_PAUSE →
        // paused.entry, which calls this facade and rewinds the cursor
        // one measure. The scene's deferred `_pauseExercise` legacy body
        // then runs and DOES handle pieceCompleted (fades the cursor out),
        // but the brief window between those two creates a visible flash
        // of the cursor one measure back.
        if (scene.pieceCompleted) {
          return;
        }
        // repositionCursorOnPause() rewinds the cursor to the start of the
        // last completed measure and RETURNS that global OSMD timestamp.
        // Capture the returned value (where the cursor now visually sits) —
        // NOT timeKeeper.audioTimeToMeasureTimestamp(), which is the frozen
        // mid-measure position where the user actually pressed pause and
        // would make resume jump ahead of the cursor.
        const rewoundTimestamp = scene.repositionCursorOnPause();
        scene.lastPauseTimestamp = rewoundTimestamp ?? null;
      },

      // ---- Event stream / loop visuals / error recognition ----
      resetLoopVisuals(start, _end) {
        // The pass's notes are still graded here; the container records them
        // before they are cleared.
        NotifyReactEvents.LoopPassEnding();
        // Seek the onset index to the bracket start (next pass begins there),
        // but clear note states across the WHOLE piece — matching the legacy
        // setLoopingActive full reset. Clearing only the bracket range would
        // leave stale colors from a prior non-loop playthrough on notes
        // outside the bracket. During looping nothing outside the bracket is
        // ever played, so the wider reset is a harmless superset for the
        // loop-boundary path too.
        scene.eventStream.seekToTimestamp(start);
        scene.eventStream.resetNoteStatesInRange(0, Infinity);
        scene.eventStream.initAccuracyQueues();
        for (const phrase of scene.phrases) {
          for (const noteContainer of phrase.noteContainers.values()) {
            noteContainer.reset();
          }
        }
      },
      disableErrorRecognition() {
        // Force recognition off for the count-in. The eventStream keeps the
        // user's intent (userErrorRecognitionActive) so a hand toggle made
        // during the count-in isn't lost — and isn't applied live either, so
        // notes can't turn red while the cursor is parked at the bracket start.
        scene.eventStream.suppressErrorRecognition();
      },
      restoreErrorRecognition() {
        // Re-apply whatever the user's current hand-accuracy intent is, which
        // captures any toggle made during the count-in window.
        scene.eventStream.unsuppressErrorRecognition();
      },
    };
  }

  destroy(): void {
    console.log(
      `[LOOP-DBG] Scene destroy() phrasesCount=${this.phrases?.length} isLoopingActive=${this.isLoopingActive}`,
    );
    this.phraseLoadGeneration++;
    this.totalPhraseCount = 0;
    this.pendingPhraseDatas = [];
    this.lazyPhraseLoadPromise = null;
    this.waitingForLazyPhraseAdvance = false;
    this.softResetPendingAfterLazyLoad = false;
    this.playbackActor?.stop();
    this.playbackActor = null;
    this.countInCountdownInstance?.destroy();
    this.countInCountdownInstance = undefined;
    this.pausedCursorMeasure = null;
    this.clearAutoPlaybackCountInTimer();
    this.pendingStartExercisePayload = null;
    this.isAddingPhrases = false;
    this.teardownInputScrolling();
    this.phrases.forEach((phrase, idx) => {
      try {
        phrase.destroy();
      } catch (err) {
        console.error(`[LOOP-DBG] Scene destroy — phrase ${idx} destroy FAILED:`, err);
      }
    });
    this.phrases = [];

    // Remove ALL event listeners registered in init() (single source of truth
    // so none are missed — UserPlay/UserPause/App*/InjectScheduler2 were
    // previously leaked here, keeping dead-scene closures subscribed).
    for (const eventName of OSMD_REPERTOIRE_REACT_LISTENER_EVENTS) {
      RemoveListener(eventName);
    }

    if (this.bracketOverlay) {
      this.bracketOverlay.destroy();
      this.bracketOverlay = null;
    }

    this.perfectPhrase?.destroy();
    this.perfectPhrase = null;
    this.perfectPhraseBadge?.destroy();
    this.perfectPhraseBadge = null;
    this.perfectPhrasePausedAt = null;
    this.eventStream?.setLoopPerfectionBrokenCallback(undefined);

    // Do NOT clear queued events here — during a scene.start() restart,
    // destroy runs AFTER AddAllOSMDPhrases has been queued but BEFORE
    // init() replays it via collectWaiting. Clearing here would lose the
    // queued phrases. Queue clearing is handled by unloadPiece() instead.

    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
    this.pendingPhraseTransition = null;
    this.isLoopingActive = false;
    this.measureCount = 0;
    this.maxPhraseContainerWidth = 0;
    this.logMemoryDiagnostics('After destroy');
  }
}
